import { useState, useEffect, useRef, useMemo } from "react";
import ReactMarkdown from "react-markdown";
import { useAuth } from "@/hooks/use-auth";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useContent, useCreateContent, useUpdateContent, useApproveContent, useRejectContent } from "@/hooks/use-content";
import { useWorkspaceStore } from "@/hooks/use-workspace";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type { MarketingPlan, StructuredStrategy, WorkspaceAsset } from "@shared/schema";
import {
  useCopyFeedback,
  PillSelector,
  SectionLabel,
  GenerateButton,
  SchedulePopover,
  ActionBar,
  approvalBadgeStyle,
  approvalLabel,
} from "./content-studio/ui";
import {
  Loader2,
  PenTool,
  Sparkles,
  Save,
  Copy,
  Check,
  Image,
  Hash,
  Type,
  LayoutGrid,
  Film,
  ShoppingBag,
  Repeat2,
  Smile,
  Bookmark,
  ChevronLeft,
  ChevronRight,
  Megaphone,
  Calendar,
  FolderOpen,
  Upload,
  Download,
  Trash2,
  X,
  CheckCircle2,
  XCircle,
  Clock,
  Edit3,
  Eye,
  RefreshCw,
  Palette,
  Lock,
  Unlock,
  BookOpen,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Zap,
  ExternalLink,
  Globe,
  Stamp,
  UserCog,
  Tag,
  ListOrdered,
  Plus,
  ArrowUpDown,
  Play,
  SkipForward,
  FileText,
  HelpCircle,
  Lightbulb,
  BarChart2,
  Newspaper,
  Brain,
  Search,
  Target,
  TrendingUp,
  ShieldAlert,
  FlaskConical,
} from "lucide-react";
import { SocialPreviewMockup } from "@/components/social-preview-mockup";
import type { ContentItem } from "@shared/schema";
import { PageBuilderEmbed } from "./page-builder";
import VideoStudio from "@/components/video-studio";
import { getAiEngine, usesClaudeTeam, type AiEngine } from "@/lib/ai-engine";
import { SimpleStudio } from "@/components/content-simple/simple-studio";

const PLATFORMS = ["Instagram", "LinkedIn", "X", "TikTok", "Meta", "Google"] as const;
const CONTENT_TYPES = ["Post", "Carousel", "Story", "Lead Magnet"] as const;
const CONTENT_TEMPLATES = [
  "Free Prompt",
  "Industry Update",
  "Case Study",
  "Firm Spotlight",
  "Client Success",
  "Tips & Advice",
  "Behind the Scenes",
  "Trending Topic",
] as const;
const TONES = ["Professional", "Casual", "Witty", "Inspirational", "Bold"] as const;
const MEME_STYLES = ["Professional", "Sarcastic", "Relatable", "Trending"] as const;
const REPURPOSE_FORMATS = [
  "LinkedIn Post",
  "Twitter Thread",
  "Instagram Caption",
  "Email Newsletter",
  "Blog Summary",
  "Facebook Post",
] as const;

const TABS = [
  { id: "create", label: "Create", icon: PenTool },
  { id: "calendar", label: "Calendar", icon: Calendar },
  { id: "blogs", label: "Blogs", icon: BookOpen },
  { id: "queue", label: "Queue", icon: ListOrdered },
  { id: "repurpose", label: "Blog → Social", icon: Repeat2 },
  { id: "playbook", label: "Posting Schedule", icon: BookOpen },
  { id: "library", label: "Library", icon: Bookmark },
  { id: "videos", label: "Video Studio", icon: Film },
  { id: "pages", label: "Pages", icon: LayoutGrid },
  { id: "seo", label: "SEO Hub", icon: Hash },
  { id: "intelligence", label: "Intelligence", icon: Brain },
  { id: "personal-brand", label: "Personal Brand", icon: UserCog },
] as const;

type TabId = (typeof TABS)[number]["id"];

// Shared content-studio UI primitives (useCopyFeedback, PillSelector, SectionLabel,
// GenerateButton, SchedulePopover, ActionBar, approvalBadgeStyle, approvalLabel) are
// now imported from ./content-studio/ui — see T297 split.

function ManualPostForm({ initialMediaUrl, autoGenerateCaption, onCaptionConsumed }: { initialMediaUrl?: string; autoGenerateCaption?: boolean; onCaptionConsumed?: () => void } = {}) {
  const { activeWorkspaceId } = useWorkspaceStore();
  const createContent = useCreateContent();
  const { toast } = useToast();

  const [platform, setPlatform] = useState("Instagram");
  const [contentType, setContentType] = useState("Post");
  const [caption, setCaption] = useState("");
  const [mediaUrl, setMediaUrl] = useState(initialMediaUrl || "");
  const [showAssetPicker, setShowAssetPicker] = useState(false);
  const [schedDate, setSchedDate] = useState("");
  const [schedTime, setSchedTime] = useState("09:00");
  const [generatingCaption, setGeneratingCaption] = useState(false);
  const today = new Date().toISOString().split("T")[0];

  useEffect(() => {
    if (initialMediaUrl) {
      setMediaUrl(initialMediaUrl);
    }
  }, [initialMediaUrl]);

  async function handleGenerateCaptionFromImage() {
    if (!mediaUrl || !activeWorkspaceId) return;
    setGeneratingCaption(true);
    try {
      const res = await fetch(`/api/workspaces/${activeWorkspaceId}/ai/caption-from-image`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ imageUrl: mediaUrl, platform }),
      });
      const data = await res.json();
      if (res.ok && data.caption) {
        setCaption(data.caption);
        toast({ title: "Caption generated", description: "AI wrote a caption based on your photo. Feel free to edit it!" });
      } else {
        toast({ title: "Caption failed", description: data.message || "Could not generate caption", variant: "destructive" });
      }
    } catch {
      toast({ title: "Caption failed", description: "Network error generating caption", variant: "destructive" });
    } finally {
      setGeneratingCaption(false);
    }
  }

  const hasAutoGeneratedRef = useRef<string | false>(false);
  useEffect(() => {
    if (autoGenerateCaption && initialMediaUrl && activeWorkspaceId && hasAutoGeneratedRef.current !== initialMediaUrl) {
      hasAutoGeneratedRef.current = initialMediaUrl;
      handleGenerateCaptionFromImage().finally(() => {
        onCaptionConsumed?.();
      });
    }
  }, [autoGenerateCaption, initialMediaUrl, activeWorkspaceId]);

  const { data: assets } = useQuery<WorkspaceAsset[]>({
    queryKey: ["/api/workspaces", activeWorkspaceId, "assets"],
    enabled: !!activeWorkspaceId && showAssetPicker,
  });

  function handleSave(status: "DRAFT" | "SCHEDULED") {
    if (!caption.trim() || !activeWorkspaceId) {
      toast({ title: "Missing content", description: "Please write something before saving.", variant: "destructive" });
      return;
    }
    const data: any = {
      workspaceId: activeWorkspaceId,
      platform,
      type: contentType,
      caption: caption.trim(),
      mediaUrl: mediaUrl || undefined,
      status,
    };
    if (status === "SCHEDULED" && schedDate) {
      data.scheduledAt = new Date(`${schedDate}T${schedTime}:00`).toISOString();
    }
    createContent.mutate(data, {
      onSuccess: () => {
        toast({ title: status === "DRAFT" ? "Saved" : "Scheduled", description: status === "DRAFT" ? "Content saved as draft." : `Scheduled for ${schedDate} at ${schedTime}.` });
        setCaption("");
        setMediaUrl("");
        setSchedDate("");
      },
      onError: () => toast({ title: "Error", description: "Failed to save content.", variant: "destructive" }),
    });
  }

  return (
    <div className="max-w-4xl space-y-4">
      <div className="bg-card/50 border border-border rounded-xl p-4 space-y-3">
        <h2 className="text-sm font-display font-bold text-foreground">Write Your Own Post</h2>
        <div className="space-y-1">
          <SectionLabel>Platform</SectionLabel>
          <PillSelector options={PLATFORMS} value={platform} onChange={setPlatform} testIdPrefix="button-manual-platform" />
        </div>
        <div className="space-y-1">
          <SectionLabel>Content Type</SectionLabel>
          <PillSelector options={CONTENT_TYPES} value={contentType} onChange={setContentType} testIdPrefix="button-manual-type" />
        </div>
        <div className="space-y-1">
          <SectionLabel>Image URL (optional)</SectionLabel>
          <input
            data-testid="input-manual-media-url"
            value={mediaUrl}
            onChange={(e) => setMediaUrl(e.target.value)}
            placeholder="Paste an image URL or pick from your assets below..."
            className="w-full bg-muted/50 border border-border focus:border-blue-500 outline-none rounded-lg px-3 py-2 text-xs text-foreground placeholder:text-muted-foreground/70"
          />
        </div>
        {mediaUrl && (
          <div className="space-y-2">
            <div className="rounded-xl overflow-hidden border border-border max-w-xs">
              <img src={mediaUrl} alt="Preview" className="w-full" data-testid="img-manual-preview" onError={(e) => (e.currentTarget.style.display = "none")} />
            </div>
            <button
              data-testid="button-generate-caption-from-image"
              onClick={handleGenerateCaptionFromImage}
              disabled={generatingCaption}
              className="flex items-center gap-2 border border-purple-500/30 hover:border-purple-500/50 bg-purple-500/10 hover:bg-purple-500/20 text-purple-300 font-semibold rounded-lg px-3 py-1.5 text-xs transition-colors disabled:opacity-40"
            >
              {generatingCaption ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
              {generatingCaption ? "Generating caption..." : "Generate Caption with AI"}
            </button>
          </div>
        )}
        <div className="space-y-1">
          <SectionLabel>Your Content</SectionLabel>
          <textarea
            data-testid="input-manual-caption"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            disabled={generatingCaption}
            placeholder={generatingCaption ? "AI is writing your caption..." : (mediaUrl ? "AI caption will appear here, or write your own..." : "Write your post caption, article text, or content here...")}
            rows={6}
            className="w-full bg-muted/50 border border-border focus:border-blue-500 outline-none rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/70 resize-none disabled:opacity-50 disabled:cursor-not-allowed"
          />
          <p className="text-xs text-muted-foreground/50 text-right">{caption.length} characters</p>
        </div>
        <div className="space-y-2">
          <button
            data-testid="button-manual-toggle-assets"
            onClick={() => setShowAssetPicker(!showAssetPicker)}
            className="flex items-center gap-2 text-xs font-bold text-muted-foreground uppercase tracking-wider hover:text-muted-foreground transition-colors"
          >
            <FolderOpen size={12} />
            My Assets {showAssetPicker ? "\u25BE" : "\u25B8"}
          </button>
          {showAssetPicker && (
            <div className="grid grid-cols-4 gap-2 max-h-40 overflow-y-auto custom-scrollbar">
              {assets && assets.length > 0 ? assets.filter(a => a.type === "image").map(a => (
                <button
                  key={a.id}
                  data-testid={`manual-asset-pick-${a.id}`}
                  onClick={() => setMediaUrl(a.url)}
                  className={`rounded-lg overflow-hidden border-2 transition-colors aspect-square ${mediaUrl === a.url ? "border-blue-500" : "border-border hover:border-border"}`}
                >
                  <img src={a.url} alt={a.name} className="w-full h-full object-cover" />
                </button>
              )) : (
                <p className="col-span-4 text-xs text-muted-foreground/50 py-2">No assets yet. Upload some in the Library tab.</p>
              )}
            </div>
          )}
        </div>
        <div className="space-y-1">
          <SectionLabel>Schedule (optional)</SectionLabel>
          <div className="flex gap-2">
            <input
              data-testid="input-manual-date"
              type="date"
              value={schedDate}
              min={today}
              onChange={(e) => setSchedDate(e.target.value)}
              className="bg-muted/50 border border-border rounded-lg px-3 py-2 text-xs text-foreground"
            />
            <input
              data-testid="input-manual-time"
              type="time"
              value={schedTime}
              onChange={(e) => setSchedTime(e.target.value)}
              className="bg-muted/50 border border-border rounded-lg px-3 py-2 text-xs text-foreground"
            />
          </div>
        </div>
        <div className="flex gap-2 pt-2">
          <button
            data-testid="button-manual-save-draft"
            onClick={() => handleSave("DRAFT")}
            disabled={createContent.isPending || !caption.trim()}
            className="flex items-center gap-2 bg-card border border-border hover:border-blue-500/50 text-foreground font-medium px-4 py-2 rounded-lg text-sm transition-colors disabled:opacity-40"
          >
            <Save size={14} /> Save as Draft
          </button>
          <button
            data-testid="button-manual-schedule"
            onClick={() => handleSave("SCHEDULED")}
            disabled={createContent.isPending || !caption.trim() || !schedDate}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium px-4 py-2 rounded-lg text-sm transition-colors disabled:opacity-40"
          >
            <Calendar size={14} /> {schedDate ? "Schedule" : "Pick date to schedule"}
          </button>
        </div>
      </div>
    </div>
  );
}

function CreatePostTab({ initialMediaUrl, autoGenerateCaption, onCaptionConsumed }: { initialMediaUrl?: string; autoGenerateCaption?: boolean; onCaptionConsumed?: () => void } = {}) {
  const { activeWorkspaceId } = useWorkspaceStore();
  const createContent = useCreateContent();
  const { toast } = useToast();
  const { copiedKey, copy } = useCopyFeedback();

  const [mode, setMode] = useState<"ai" | "manual">(initialMediaUrl ? "manual" : "ai");
  const [platform, setPlatform] = useState("Instagram");
  const [contentType, setContentType] = useState("Post");
  const [contentTemplate, setContentTemplate] = useState("Free Prompt");
  const [carouselSlideCount, setCarouselSlideCount] = useState(5);
  const [context, setContext] = useState("");
  const [postImageInstructions, setPostImageInstructions] = useState("");
  const [includeTextOnPostImage, setIncludeTextOnPostImage] = useState(false);
  const [generatedCaption, setGeneratedCaption] = useState<string | null>(null);
  const [generatedIsCarousel, setGeneratedIsCarousel] = useState(false);
  const [generatedImagePrompt, setGeneratedImagePrompt] = useState<string | null>(null);
  const [generatedImage, setGeneratedImage] = useState<string | null>(null);
  const [generatedHashtags, setGeneratedHashtags] = useState<string[]>([]);
  const [showAssetPicker, setShowAssetPicker] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<string | null>(null);

  const { data: plans } = useQuery<MarketingPlan[]>({
    queryKey: ["/api/workspaces", activeWorkspaceId, "plans"],
    queryFn: async () => {
      if (!activeWorkspaceId) return [];
      const res = await fetch(`/api/workspaces/${activeWorkspaceId}/plans`, { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!activeWorkspaceId,
  });

  const { data: assets } = useQuery<WorkspaceAsset[]>({
    queryKey: ["/api/workspaces", activeWorkspaceId, "assets"],
    enabled: !!activeWorkspaceId && showAssetPicker,
  });

  const { data: workspaceList } = useQuery<any[]>({
    queryKey: ["/api/workspaces"],
  });
  const activeWorkspace = workspaceList?.find((w: any) => w.id === activeWorkspaceId);
  const wsIntegrations = (activeWorkspace?.integrations as any) || {};

  function isPlatformConnected(p: string): boolean {
    const key = p.toLowerCase();
    if (key === "instagram" || key === "meta" || key === "facebook") {
      return !!(wsIntegrations?.meta?.connected || wsIntegrations?.instagram?.connected || wsIntegrations?.facebook?.connected || wsIntegrations?.instagram_business?.connected);
    }
    if (key === "x" || key === "twitter") return !!(wsIntegrations?.x?.connected);
    if (key === "linkedin") return !!(wsIntegrations?.linkedin?.connected);
    if (key === "google") return !!(wsIntegrations?.google?.connected);
    return false;
  }

  const latestStrategy = plans?.[0]?.structuredStrategy as StructuredStrategy | undefined;
  const calendarTopics = latestStrategy?.platforms
    ?.filter(p => (p.platform?.toLowerCase() ?? "") === platform.toLowerCase() || (platform === "Instagram" && p.platform === "Meta") || (platform === "Instagram" && p.platform?.toLowerCase() === "instagram_business"))
    ?.flatMap(p => p.weeklyCalendar || []) || [];

  function buildTemplatePrompt(): string {
    const businessContext = latestStrategy ? ` for ${latestStrategy.overview?.split('.')[0] || 'the business'}` : "";
    const templatePrefixes: Record<string, string> = {
      "Industry Update": `Create a ${platform} post${businessContext} about the latest industry news, trends, or regulatory updates. Use Google Search grounding to find current, timely information. Make it educational and valuable. Include relevant statistics or data points.`,
      "Case Study": `Create a ${platform} post${businessContext} formatted as a case study or success story. Highlight a realistic client challenge, the approach taken, and measurable results. Use before/after format or numbered results. Make it relatable and compelling.`,
      "Firm Spotlight": `Create a ${platform} post${businessContext} that highlights the team, company culture, or behind-the-scenes look at the business. Humanize the brand. Show expertise and personality.`,
      "Client Success": `Create a ${platform} post${businessContext} celebrating a client success or testimonial (create a realistic but fictional example). Include specific results, quotes, and a call-to-action for similar prospects.`,
      "Tips & Advice": `Create a ${platform} post${businessContext} with practical, actionable tips or advice for the target audience. Use numbered lists or step-by-step format. Make it immediately useful.`,
      "Behind the Scenes": `Create a ${platform} post${businessContext} showing behind-the-scenes of daily operations, team meetings, or work process. Make it authentic and relatable.`,
      "Trending Topic": `Search Google for the latest trending topics in the industry and create a ${platform} post${businessContext} that connects a current trend to the business. Be timely and relevant.`,
    };
    const prefix = templatePrefixes[contentTemplate] || "";
    const userInstructions = context ? `\n\nAdditional instructions: ${context}` : "";
    return prefix + userInstructions;
  }

  const generateMutation = useMutation({
    mutationFn: async (data: { platform: string; type: string; context: string }) => {
      const effectiveContext = contentTemplate !== "Free Prompt" ? buildTemplatePrompt() : data.context;
      if (data.type === "Carousel") {
        const clampedSlides = Math.max(3, Math.min(8, carouselSlideCount));
        const res = await apiRequest("POST", "/api/ai/carousel", {
          platform: data.platform,
          strategy: effectiveContext || "Create engaging carousel content",
          slideCount: clampedSlides,
          context: effectiveContext,
          workspaceId: activeWorkspaceId,
        });
        return { type: "carousel", data: await res.json() };
      } else if (data.type === "Lead Magnet") {
        const res = await apiRequest("POST", "/api/ai/lead-magnet", {
          type: "guide",
          strategy: effectiveContext || "Generate a high-value lead magnet",
          workspaceId: activeWorkspaceId,
        });
        return { type: "leadmagnet", data: await res.json() };
      } else if (data.type === "Story") {
        const res = await apiRequest("POST", "/api/ai/story", {
          topic: effectiveContext || "Engaging story content",
          platform: data.platform,
          workspaceId: activeWorkspaceId,
        });
        return { type: "story", data: await res.json() };
      } else {
        const res = await apiRequest("POST", "/api/ai/content", {
          platform: data.platform,
          strategy: effectiveContext || "Create engaging social media content",
          context: effectiveContext,
          workspaceId: activeWorkspaceId,
        });
        return { type: "post", data: await res.json() };
      }
    },
    onSuccess: (result) => {
      setGeneratedImage(null);
      setGeneratedHashtags([]);
      setGeneratedIsCarousel(false);
      if (result.type === "post") {
        setGeneratedCaption(result.data.caption || "");
        const imgPrompt = result.data.imagePrompt || null;
        setGeneratedImagePrompt(imgPrompt);
        if (result.data.hashtags) setGeneratedHashtags(result.data.hashtags);
        if (imgPrompt) {
          imageMutation.mutate(buildPostImageRequest(imgPrompt));
        }
      } else if (result.type === "carousel") {
        const slides = Array.isArray(result.data) ? result.data : [];
        const formatted = slides.map((s: any, i: number) => {
          const headline = s.headline || `Slide ${i + 1}`;
          const body = s.body || s.text || s.caption || "";
          return `**${headline}**\n${body}`;
        }).join("\n---\n");
        setGeneratedCaption(formatted || "No carousel content generated.");
        setGeneratedIsCarousel(true);
        setGeneratedImagePrompt(null);
      } else if (result.type === "leadmagnet") {
        setGeneratedCaption(result.data.content || "");
        setGeneratedImagePrompt(null);
      } else if (result.type === "story") {
        const frames = result.data.frames || [];
        const formatted = frames.map((f: any, i: number) => `Frame ${i + 1}: ${f.text}\nCTA: ${f.ctaText || ""}`).join("\n\n");
        setGeneratedCaption(formatted || "No story content generated.");
        const imgPrompt = frames[0]?.imagePrompt || null;
        setGeneratedImagePrompt(imgPrompt);
        if (imgPrompt) {
          imageMutation.mutate(buildPostImageRequest(imgPrompt));
        }
      }
    },
    onError: (err: any) => {
      toast({ title: "Generation Failed", description: err.message || "Failed to generate content.", variant: "destructive" });
    },
  });

  const imageMutation = useMutation({
    mutationFn: async (params: string | { prompt: string; textOverlay?: any }) => {
      const body = typeof params === "string" ? { prompt: params, workspaceId: activeWorkspaceId } : { ...params, workspaceId: activeWorkspaceId };
      if (selectedAsset) {
        (body as any).assetUrl = selectedAsset;
      }
      const res = await apiRequest("POST", "/api/ai/image", body);
      return res.json();
    },
    onSuccess: (data: any) => {
      if (data.imageUrl) setGeneratedImage(data.imageUrl);
      else toast({ title: "No Image", description: "Image generation did not return a result.", variant: "destructive" });
    },
    onError: () => {
      toast({ title: "Image Failed", description: "Failed to generate image.", variant: "destructive" });
    },
  });

  function buildPostImageRequest(prompt: string) {
    const fullPrompt = postImageInstructions ? `${prompt}. Additional instructions: ${postImageInstructions}` : prompt;
    if (includeTextOnPostImage && generatedCaption) {
      const firstLine = generatedCaption.split("\n")[0] || "";
      return {
        prompt: fullPrompt,
        textOverlay: {
          headline: firstLine.substring(0, 80),
        },
      };
    }
    return fullPrompt;
  }

  function handleSave() {
    if (!generatedCaption || !activeWorkspaceId) return;
    createContent.mutate({
      workspaceId: activeWorkspaceId,
      platform,
      type: contentType,
      caption: generatedCaption,
      mediaUrl: generatedImage || undefined,
      status: "DRAFT",
    });
    setGeneratedCaption(null);
    setGeneratedImage(null);
    setGeneratedImagePrompt(null);
    setGeneratedHashtags([]);
    setContext("");
    toast({ title: "Saved", description: "Content saved as draft." });
  }

  function handleSchedule(date: string, time: string) {
    if (!generatedCaption || !activeWorkspaceId) return;
    const scheduledAt = new Date(`${date}T${time}:00`).toISOString();
    const connected = isPlatformConnected(platform);
    createContent.mutate({
      workspaceId: activeWorkspaceId,
      platform,
      type: contentType,
      caption: generatedCaption,
      mediaUrl: generatedImage || undefined,
      status: connected ? "SCHEDULED" : "DRAFT",
      scheduledAt: connected ? scheduledAt : undefined,
    });
    setGeneratedCaption(null);
    setGeneratedImage(null);
    setGeneratedImagePrompt(null);
    setGeneratedHashtags([]);
    setContext("");
    if (!connected) {
      toast({ title: "Saved as Draft", description: `${platform} is not connected. Content saved as draft — connect in Settings to schedule.`, variant: "default" });
    } else {
      toast({ title: "Scheduled", description: `Content scheduled for ${date} at ${time}.` });
    }
  }

  const modeToggle = (
    <div className="flex gap-1 bg-muted/30 border border-border rounded-lg p-1 w-fit mb-2">
      <button
        data-testid="button-mode-ai"
        onClick={() => setMode("ai")}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${mode === "ai" ? "bg-blue-600 text-white" : "text-muted-foreground hover:text-foreground"}`}
      >
        <Sparkles size={12} /> AI Generate
      </button>
      <button
        data-testid="button-mode-manual"
        onClick={() => setMode("manual")}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${mode === "manual" ? "bg-blue-600 text-white" : "text-muted-foreground hover:text-foreground"}`}
      >
        <PenTool size={12} /> Write Your Own
      </button>
    </div>
  );

  if (mode === "manual") return <div className="max-w-4xl">{modeToggle}<ManualPostForm initialMediaUrl={initialMediaUrl} autoGenerateCaption={autoGenerateCaption} onCaptionConsumed={onCaptionConsumed} /></div>;

  return (
    <div className="max-w-4xl space-y-4">
      {modeToggle}
      <div className="bg-card/50 border border-border rounded-xl p-4 space-y-3" data-testid="content-generator">
        <h2 className="text-sm font-display font-bold text-foreground">Generate Content</h2>
        <div className="space-y-1">
          <SectionLabel>Platform</SectionLabel>
          <PillSelector options={PLATFORMS} value={platform} onChange={setPlatform} testIdPrefix="button-platform" />
          {!isPlatformConnected(platform) && (
            <div className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-amber-500/8 border border-amber-500/20 mt-1.5" data-testid={`oauth-warning-${platform.toLowerCase()}`}>
              <div className="flex items-center gap-1.5">
                <AlertTriangle size={11} className="text-amber-400 shrink-0" />
                <span className="text-[11px] text-amber-400/80">{platform} not connected — content will save as draft.</span>
              </div>
              <a
                href="/settings"
                className="text-[11px] font-bold text-blue-400 hover:text-blue-300 transition-colors shrink-0"
                data-testid={`link-connect-${platform.toLowerCase()}`}
              >
                Connect →
              </a>
            </div>
          )}
        </div>
        <div className="space-y-1">
          <SectionLabel>Content Type</SectionLabel>
          <PillSelector options={CONTENT_TYPES} value={contentType} onChange={setContentType} testIdPrefix="button-type" />
        </div>
        <div className="space-y-1">
          <SectionLabel>Content Template</SectionLabel>
          <PillSelector options={CONTENT_TEMPLATES} value={contentTemplate} onChange={setContentTemplate} testIdPrefix="button-template" />
        </div>
        {calendarTopics.length > 0 && (
          <div className="space-y-2">
            <SectionLabel>From Your Strategy Calendar</SectionLabel>
            <div className="max-h-32 overflow-y-auto custom-scrollbar space-y-1">
              {calendarTopics.map((entry, i) => (
                <button
                  key={i}
                  onClick={() => setContext(`${entry.contentType} about: ${entry.topic} (Best time: ${entry.day} ${entry.time})`)}
                  className="w-full text-left px-3 py-2 rounded-lg bg-card/50 border border-border text-xs text-muted-foreground hover:text-foreground/80 hover:border-blue-500/30 transition-colors"
                  data-testid={`calendar-topic-${i}`}
                >
                  <span className="text-blue-400 font-medium">{entry.day}</span> - {entry.contentType}: {entry.topic}
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="space-y-1">
          <SectionLabel>{contentTemplate === "Free Prompt" ? "Context / Instructions" : "Additional Instructions (optional)"}</SectionLabel>
          {contentTemplate === "Free Prompt" && (
            <p className="text-[11px] text-muted-foreground/60 -mt-0.5 italic">Be specific — mention format, tone, slide count, and topic for best results.</p>
          )}
          <textarea
            data-testid="input-context"
            value={context}
            onChange={(e) => setContext(e.target.value)}
            placeholder={contentTemplate === "Free Prompt" ? "e.g. Write a LinkedIn post about how we helped a homeowner save $3,000 on their renovation — conversational, ends with a question..." : `Additional details for your ${contentTemplate} post...`}
            rows={2}
            className="w-full bg-muted/50 border border-border focus:border-blue-500 outline-none rounded-lg px-3 py-2 text-xs text-foreground placeholder:text-muted-foreground/70 resize-none"
          />
        </div>
        <div className="space-y-1">
          <SectionLabel>Image Instructions (optional)</SectionLabel>
          <textarea
            data-testid="input-post-image-instructions"
            value={postImageInstructions}
            onChange={(e) => setPostImageInstructions(e.target.value)}
            placeholder="e.g. Modern office setting, use blue tones..."
            rows={2}
            className="w-full bg-muted/50 border border-border focus:border-blue-500 outline-none rounded-lg px-3 py-2 text-xs text-foreground placeholder:text-muted-foreground/70 resize-none"
          />
        </div>
        <div className="space-y-2">
          <button
            data-testid="button-toggle-assets"
            onClick={() => setShowAssetPicker(!showAssetPicker)}
            className="flex items-center gap-2 text-xs font-bold text-muted-foreground uppercase tracking-wider hover:text-muted-foreground transition-colors"
          >
            <FolderOpen size={12} />
            My Assets {showAssetPicker ? "\u25BE" : "\u25B8"}
          </button>
          {showAssetPicker && (
            <div className="grid grid-cols-4 gap-2 max-h-40 overflow-y-auto custom-scrollbar">
              {assets && assets.length > 0 ? assets.filter(a => a.type === "image").map(a => (
                <button
                  key={a.id}
                  data-testid={`asset-pick-${a.id}`}
                  onClick={() => {
                    setSelectedAsset(a.url);
                    setGeneratedImage(a.url);
                  }}
                  className={`rounded-lg overflow-hidden border-2 transition-colors aspect-square ${
                    selectedAsset === a.url ? "border-blue-500" : "border-border hover:border-border"
                  }`}
                >
                  <img src={a.url} alt={a.name} className="w-full h-full object-cover" />
                </button>
              )) : (
                <p className="col-span-4 text-xs text-muted-foreground/50 py-2">No assets yet. Upload some in the Library tab.</p>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setIncludeTextOnPostImage(!includeTextOnPostImage)}
            className={`w-10 h-5 rounded-full transition-colors relative ${includeTextOnPostImage ? "bg-blue-600" : "bg-muted"}`}
            data-testid="toggle-text-on-post-image"
          >
            <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${includeTextOnPostImage ? "translate-x-5" : "translate-x-0.5"}`} />
          </button>
          <span className="text-sm text-muted-foreground">Include text overlay on image</span>
        </div>
        {/* T004: Carousel slide count stepper */}
        {contentType === "Carousel" && (
          <div className="flex items-center gap-4 p-3 rounded-xl bg-muted/30 border border-border" data-testid="carousel-slide-count-section">
            <div className="flex-1">
              <SectionLabel>Slides: <span className="text-blue-400 font-bold">{carouselSlideCount}</span></SectionLabel>
              <input
                data-testid="input-carousel-slide-count"
                type="range" min={3} max={8} step={1}
                value={carouselSlideCount}
                onChange={(e) => setCarouselSlideCount(Number(e.target.value))}
                className="w-full accent-blue-500 mt-1"
              />
              <div className="flex justify-between text-[10px] text-muted-foreground/50 mt-0.5"><span>3 min</span><span>8 max</span></div>
            </div>
          </div>
        )}
        <GenerateButton
          isPending={generateMutation.isPending}
          onClick={() => generateMutation.mutate({ platform, type: contentType, context })}
          label={contentTemplate !== "Free Prompt" ? `Generate ${contentTemplate}` : "Generate"}
        />
      </div>

      {generatedCaption && (
        <div className="bg-card/50 border border-blue-500/20 rounded-2xl p-6 space-y-4 animate-in fade-in" data-testid="generated-content">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-sm font-bold text-muted-foreground uppercase tracking-wider">Generated Content</h3>
            {contentType === "Carousel" && (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-purple-500/10 text-purple-400 uppercase">Carousel</span>
            )}
            {contentTemplate !== "Free Prompt" && contentType !== "Carousel" && (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-blue-500/10 text-blue-400 uppercase">{contentTemplate}</span>
            )}
          </div>
          {generatedIsCarousel ? (
            <CarouselSlidesPanel caption={generatedCaption} onCaptionChange={setGeneratedCaption} />
          ) : (
            <div className="text-sm text-foreground/80 leading-relaxed whitespace-pre-wrap max-h-60 overflow-y-auto custom-scrollbar">
              {generatedCaption}
            </div>
          )}
          {generatedHashtags.length > 0 && (
            <div className="space-y-1.5">
              <span className="text-[10px] font-bold text-muted-foreground/70 uppercase">Hashtags</span>
              <div className="flex flex-wrap gap-1.5">
                {generatedHashtags.map((h, i) => (
                  <button
                    key={i}
                    onClick={() => copy(h, `hashtag-${i}`)}
                    className="text-xs text-blue-400 hover:text-blue-300 transition-colors cursor-pointer"
                  >
                    {h}
                  </button>
                ))}
              </div>
            </div>
          )}
          {imageMutation.isPending && !generatedImage && (
            <div className="rounded-xl border border-border bg-muted/30 h-48 flex items-center justify-center gap-2">
              <Loader2 size={16} className="animate-spin text-blue-400" />
              <span className="text-sm text-muted-foreground">Generating image...</span>
            </div>
          )}
          {generatedImage && (
            <div className="rounded-xl overflow-hidden border border-border">
              <img src={generatedImage} alt="Generated" className="w-full" data-testid="img-generated" />
            </div>
          )}
          <ActionBar
            text={generatedCaption + (generatedHashtags.length > 0 ? "\n\n" + generatedHashtags.join(" ") : "")}
            imagePrompt={generatedImagePrompt}
            onSave={handleSave}
            savePending={createContent.isPending}
            copyKey="create-post"
            copiedKey={copiedKey}
            onCopy={() => copy(generatedCaption + (generatedHashtags.length > 0 ? "\n\n" + generatedHashtags.join(" ") : ""), "create-post")}
            onGenerateImage={() => {
              const prompt = generatedImagePrompt || `Professional marketing image for ${platform}: ${generatedCaption.slice(0, 200)}`;
              imageMutation.mutate(buildPostImageRequest(prompt));
            }}
            imagePending={imageMutation.isPending}
            onSchedule={handleSchedule}
            schedulePending={createContent.isPending}
          />
        </div>
      )}
    </div>
  );
}

function CaptionsTab() {
  const { activeWorkspaceId } = useWorkspaceStore();
  const createContent = useCreateContent();
  const { toast } = useToast();
  const { copiedKey, copy } = useCopyFeedback();

  const [platform, setPlatform] = useState("Instagram");
  const [topic, setTopic] = useState("");
  const [tone, setTone] = useState("Professional");
  const [count, setCount] = useState(5);
  const [captions, setCaptions] = useState<string[]>([]);

  const mutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/ai/captions", { platform, topic, tone, count });
      return res.json();
    },
    onSuccess: (data: any) => {
      setCaptions(data.captions || []);
    },
    onError: (err: any) => {
      toast({ title: "Generation Failed", description: err.message, variant: "destructive" });
    },
  });

  return (
    <div className="max-w-4xl space-y-4">
      <div className="bg-card/50 border border-border rounded-xl p-4 space-y-3">
        <h2 className="text-sm font-display font-bold text-foreground">AI Caption Generator</h2>
        <div className="space-y-2">
          <SectionLabel>Platform</SectionLabel>
          <PillSelector options={PLATFORMS} value={platform} onChange={setPlatform} testIdPrefix="button-caption-platform" />
        </div>
        <div className="space-y-2">
          <SectionLabel>Topic</SectionLabel>
          <input
            data-testid="input-caption-topic"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder="What are the captions about?"
            className="w-full bg-muted/50 border border-border focus:border-blue-500 outline-none rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/70"
          />
        </div>
        <div className="space-y-2">
          <SectionLabel>Tone</SectionLabel>
          <PillSelector options={TONES} value={tone} onChange={setTone} testIdPrefix="button-caption-tone" />
        </div>
        <div className="space-y-2">
          <SectionLabel>Number of Captions: {count}</SectionLabel>
          <input
            data-testid="input-caption-count"
            type="range"
            min={3}
            max={10}
            value={count}
            onChange={(e) => setCount(Number(e.target.value))}
            className="w-full accent-blue-600"
          />
          <div className="flex justify-between text-xs text-muted-foreground/70">
            <span>3</span>
            <span>10</span>
          </div>
        </div>
        <GenerateButton isPending={mutation.isPending} onClick={() => mutation.mutate()} testId="button-generate-captions" />
      </div>

      {captions.length > 0 && (
        <div className="space-y-3 animate-in fade-in" data-testid="generated-captions">
          <h3 className="text-sm font-bold text-muted-foreground uppercase tracking-wider">Generated Captions</h3>
          {captions.map((cap, i) => (
            <div key={i} className="bg-card/50 border border-border rounded-xl p-4 flex items-start justify-between gap-4">
              <p className="text-sm text-foreground/80 flex-1">{cap}</p>
              <div className="flex gap-2 shrink-0">
                <button
                  data-testid={`button-copy-caption-${i}`}
                  onClick={() => copy(cap, `caption-${i}`)}
                  className="text-muted-foreground/50 hover:text-foreground transition-colors"
                >
                  {copiedKey === `caption-${i}` ? <Check size={16} className="text-green-400" /> : <Copy size={16} />}
                </button>
                <button
                  data-testid={`button-save-caption-${i}`}
                  onClick={() => {
                    if (!activeWorkspaceId) return;
                    createContent.mutate({
                      workspaceId: activeWorkspaceId,
                      platform,
                      type: "Post",
                      caption: cap,
                      status: "DRAFT",
                    });
                    toast({ title: "Saved", description: "Caption saved as draft." });
                  }}
                  className="text-muted-foreground/50 hover:text-blue-400 transition-colors"
                >
                  <Save size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function HashtagsTab() {
  const { toast } = useToast();
  const { copiedKey, copy } = useCopyFeedback();

  const [platform, setPlatform] = useState("Instagram");
  const [topic, setTopic] = useState("");
  const [count, setCount] = useState(20);
  const [hashtags, setHashtags] = useState<string[]>([]);

  const mutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/ai/hashtags", { platform, topic, count });
      return res.json();
    },
    onSuccess: (data: any) => {
      setHashtags(data.hashtags || []);
    },
    onError: (err: any) => {
      toast({ title: "Generation Failed", description: err.message, variant: "destructive" });
    },
  });

  const allHashtags = hashtags.join(" ");

  return (
    <div className="max-w-4xl space-y-4">
      <div className="bg-card/50 border border-border rounded-xl p-4 space-y-3">
        <h2 className="text-sm font-display font-bold text-foreground">AI Hashtag Generator</h2>
        <div className="space-y-2">
          <SectionLabel>Platform</SectionLabel>
          <PillSelector options={PLATFORMS} value={platform} onChange={setPlatform} testIdPrefix="button-hashtag-platform" />
        </div>
        <div className="space-y-2">
          <SectionLabel>Topic</SectionLabel>
          <input
            data-testid="input-hashtag-topic"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder="Enter a topic for hashtag generation..."
            className="w-full bg-muted/50 border border-border focus:border-blue-500 outline-none rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/70"
          />
        </div>
        <div className="space-y-2">
          <SectionLabel>Count: {count}</SectionLabel>
          <input
            data-testid="input-hashtag-count"
            type="range"
            min={10}
            max={30}
            value={count}
            onChange={(e) => setCount(Number(e.target.value))}
            className="w-full accent-blue-600"
          />
          <div className="flex justify-between text-xs text-muted-foreground/70">
            <span>10</span>
            <span>30</span>
          </div>
        </div>
        <GenerateButton isPending={mutation.isPending} onClick={() => mutation.mutate()} testId="button-generate-hashtags" />
      </div>

      {hashtags.length > 0 && (
        <div className="animate-in fade-in space-y-4" data-testid="generated-hashtags">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold text-muted-foreground uppercase tracking-wider">Generated Hashtags</h3>
            <button
              data-testid="button-copy-all-hashtags"
              onClick={() => copy(allHashtags, "all-hashtags")}
              className="border border-border hover:border-border text-muted-foreground hover:text-foreground font-bold rounded-xl px-4 py-2.5 text-sm flex items-center gap-2 transition-colors"
            >
              {copiedKey === "all-hashtags" ? <Check size={14} className="text-green-400" /> : <Copy size={14} />}
              Copy All
            </button>
          </div>
          <div className="bg-card/50 border border-border rounded-2xl p-6">
            <div className="flex flex-wrap gap-2">
              {hashtags.map((tag, i) => (
                <button
                  key={i}
                  data-testid={`button-hashtag-${i}`}
                  onClick={() => copy(tag, `hashtag-${i}`)}
                  className={`px-3 py-1.5 rounded-xl text-sm font-medium transition-colors ${
                    copiedKey === `hashtag-${i}`
                      ? "bg-green-500/20 text-green-400 border border-green-500/30"
                      : "bg-blue-600/10 text-blue-400 border border-blue-500/20 hover:bg-blue-600/20"
                  }`}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CarouselTab() {
  const { activeWorkspaceId } = useWorkspaceStore();
  const createContent = useCreateContent();
  const { toast } = useToast();
  const { copiedKey, copy } = useCopyFeedback();

  const [platform, setPlatform] = useState("LinkedIn");
  const [mode, setMode] = useState<"topic" | "blog">("topic");
  const [content, setContent] = useState("");
  const [slideCount, setSlideCount] = useState(7);
  const [slides, setSlides] = useState<{ headline?: string; body?: string; text?: string; imagePrompt?: string; imageUrl?: string }[]>([]);
  const [scrollPos, setScrollPos] = useState(0);
  const [generatingImages, setGeneratingImages] = useState(false);

  async function generateSlideImages(slideList: typeof slides) {
    setGeneratingImages(true);
    const updated = [...slideList];
    for (let i = 0; i < Math.min(updated.length, 3); i++) {
      const prompt = updated[i].imagePrompt;
      if (!prompt) continue;
      try {
        const res = await apiRequest("POST", "/api/ai/image", { prompt, workspaceId: activeWorkspaceId });
        const data = await res.json();
        if (data.imageUrl) updated[i] = { ...updated[i], imageUrl: data.imageUrl };
      } catch {
        toast({ title: "Image generation failed", description: `Could not generate image for slide ${i + 1}.`, variant: "destructive" });
      }
    }
    setSlides(updated);
    setGeneratingImages(false);
  }

  const mutation = useMutation({
    mutationFn: async () => {
      if (mode === "blog") {
        const res = await apiRequest("POST", "/api/ai/blog-to-carousel", { blogContent: content, slideCount, workspaceId: activeWorkspaceId });
        const data = await res.json();
        return { slides: data.slides || [] };
      } else {
        const res = await apiRequest("POST", "/api/ai/carousel", {
          platform,
          strategy: content || "Create engaging carousel",
          slideCount,
          context: content,
          workspaceId: activeWorkspaceId,
        });
        const data = await res.json();
        const arr = Array.isArray(data) ? data : [];
        return { slides: arr.map((s: any) => ({ headline: `Slide`, body: s.text || s.caption || "", imagePrompt: s.imagePrompt })) };
      }
    },
    onSuccess: (data) => {
      const newSlides = data.slides || [];
      setSlides(newSlides);
      setScrollPos(0);
      generateSlideImages(newSlides);
    },
    onError: (err: any) => {
      toast({ title: "Generation Failed", description: err.message, variant: "destructive" });
    },
  });

  const allText = slides.map((s, i) => `Slide ${i + 1}: ${s.headline || ""}\n${s.body || s.text || ""}`).join("\n\n");

  return (
    <div className="max-w-5xl space-y-4">
      <div className="bg-card/50 border border-border rounded-xl p-4 space-y-3">
        <h2 className="text-sm font-display font-bold text-foreground">Carousel Maker</h2>
        <div className="space-y-2">
          <SectionLabel>Platform</SectionLabel>
          <PillSelector options={["LinkedIn", "Instagram"] as const} value={platform} onChange={setPlatform} testIdPrefix="button-carousel-platform" />
        </div>
        <div className="space-y-2">
          <SectionLabel>Mode</SectionLabel>
          <div className="flex gap-2">
            {(["topic", "blog"] as const).map((m) => (
              <button
                key={m}
                data-testid={`button-carousel-mode-${m}`}
                onClick={() => setMode(m)}
                className={`px-2 py-1 rounded-md text-[11px] font-medium transition-colors ${
                  mode === m
                    ? "bg-blue-600/20 text-blue-400 border border-blue-500/30"
                    : "bg-muted/50 text-muted-foreground border border-border hover:text-foreground hover:border-border"
                }`}
              >
                {m === "topic" ? "From Topic" : "From Blog"}
              </button>
            ))}
          </div>
        </div>
        <div className="space-y-2">
          <SectionLabel>{mode === "topic" ? "Topic / Instructions" : "Blog Content"}</SectionLabel>
          <textarea
            data-testid="input-carousel-content"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder={mode === "topic" ? "Describe carousel topic..." : "Paste your blog content here..."}
            rows={mode === "blog" ? 8 : 4}
            className="w-full bg-muted/50 border border-border focus:border-blue-500 outline-none rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/70 resize-none"
          />
        </div>
        <div className="space-y-2">
          <SectionLabel>Slides: {slideCount}</SectionLabel>
          <input
            data-testid="input-carousel-slides"
            type="range"
            min={5}
            max={12}
            value={slideCount}
            onChange={(e) => setSlideCount(Number(e.target.value))}
            className="w-full accent-blue-600"
          />
          <div className="flex justify-between text-xs text-muted-foreground/70">
            <span>5</span>
            <span>12</span>
          </div>
        </div>
        <GenerateButton isPending={mutation.isPending} onClick={() => mutation.mutate()} testId="button-generate-carousel" />
      </div>

      {slides.length > 0 && (
        <div className="animate-in fade-in space-y-4" data-testid="generated-carousel">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold text-muted-foreground uppercase tracking-wider">Carousel Preview</h3>
            <div className="flex gap-2">
              <button
                data-testid="button-copy-carousel"
                onClick={() => copy(allText, "carousel-all")}
                className="border border-border hover:border-border text-muted-foreground hover:text-foreground font-bold rounded-xl px-4 py-2.5 text-sm flex items-center gap-2 transition-colors"
              >
                {copiedKey === "carousel-all" ? <Check size={14} className="text-green-400" /> : <Copy size={14} />}
                Copy All
              </button>
              <button
                data-testid="button-save-carousel"
                onClick={() => {
                  if (!activeWorkspaceId) return;
                  createContent.mutate({
                    workspaceId: activeWorkspaceId,
                    platform,
                    type: "Carousel",
                    contentType: "carousel",
                    caption: allText,
                    slides: slides.map(s => ({ heading: s.headline, body: s.body || s.text || "", imageUrl: s.imageUrl })),
                    mediaUrls: slides.map(s => s.imageUrl).filter(Boolean),
                    status: "DRAFT",
                  });
                  toast({ title: "Saved", description: "Carousel saved as draft." });
                }}
                className="bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-xl px-4 py-2.5 text-sm flex items-center gap-2 transition-colors"
              >
                <Save size={14} /> Save
              </button>
            </div>
          </div>
          {generatingImages && (
            <div className="flex items-center gap-2 text-xs text-blue-400">
              <Loader2 size={12} className="animate-spin" />
              <span>Generating slide images...</span>
            </div>
          )}
          <div className="relative">
            <div className="flex gap-4 overflow-x-auto pb-4 custom-scrollbar">
              {slides.map((slide, i) => (
                <div
                  key={i}
                  className="min-w-[260px] max-w-[280px] bg-card/50 border border-border rounded-2xl shrink-0 space-y-3 overflow-hidden"
                >
                  {slide.imageUrl ? (
                    <img src={slide.imageUrl} alt={`Slide ${i + 1}`} className="w-full h-36 object-cover" data-testid={`img-slide-${i}`} />
                  ) : (
                    <div className="w-full h-36 bg-gradient-to-br from-blue-600/10 to-purple-600/10 flex items-center justify-center">
                      {generatingImages && i < 3 ? (
                        <Loader2 size={16} className="animate-spin text-blue-400/40" />
                      ) : (
                        <Image size={20} className="text-muted-foreground/30" />
                      )}
                    </div>
                  )}
                  <div className="px-5 pb-5 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-blue-400 bg-blue-600/10 px-2 py-0.5 rounded-md">
                        Slide {i + 1}
                      </span>
                      <button
                        data-testid={`button-copy-slide-${i}`}
                        onClick={() => copy(`${slide.headline || ""}\n${slide.body || slide.text || ""}`, `slide-${i}`)}
                        className="text-muted-foreground/50 hover:text-foreground transition-colors"
                      >
                        {copiedKey === `slide-${i}` ? <Check size={14} className="text-green-400" /> : <Copy size={14} />}
                      </button>
                    </div>
                    {slide.headline && <h4 className="text-sm font-bold text-foreground">{slide.headline}</h4>}
                    <p className="text-sm text-foreground/70 leading-relaxed">{slide.body || slide.text || ""}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StoriesTab() {
  const { activeWorkspaceId } = useWorkspaceStore();
  const createContent = useCreateContent();
  const { toast } = useToast();
  const { copiedKey, copy } = useCopyFeedback();

  const [topic, setTopic] = useState("");
  const [brandTone, setBrandTone] = useState("");
  const [frames, setFrames] = useState<{ text: string; imagePrompt?: string; ctaText?: string; imageUrl?: string }[]>([]);
  const [generatingImages, setGeneratingImages] = useState(false);

  async function generateFrameImages(frameList: typeof frames) {
    setGeneratingImages(true);
    const updated = [...frameList];
    for (let i = 0; i < Math.min(updated.length, 3); i++) {
      const prompt = updated[i].imagePrompt;
      if (!prompt) continue;
      try {
        const res = await apiRequest("POST", "/api/ai/image", { prompt, workspaceId: activeWorkspaceId });
        const data = await res.json();
        if (data.imageUrl) updated[i] = { ...updated[i], imageUrl: data.imageUrl };
      } catch {
        toast({ title: "Image generation failed", description: `Could not generate image for frame ${i + 1}.`, variant: "destructive" });
      }
    }
    setFrames(updated);
    setGeneratingImages(false);
  }

  const mutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/ai/story", { topic, brandTone: brandTone || "professional", platform: "Instagram", workspaceId: activeWorkspaceId });
      return res.json();
    },
    onSuccess: (data: any) => {
      const newFrames = data.frames || [];
      setFrames(newFrames);
      generateFrameImages(newFrames);
    },
    onError: (err: any) => {
      toast({ title: "Generation Failed", description: err.message, variant: "destructive" });
    },
  });

  const allText = frames.map((f, i) => `Frame ${i + 1}: ${f.text}\nCTA: ${f.ctaText || "N/A"}`).join("\n\n");

  return (
    <div className="max-w-4xl space-y-4">
      <div className="bg-card/50 border border-border rounded-xl p-4 space-y-3">
        <h2 className="text-sm font-display font-bold text-foreground">Story Maker</h2>
        <div className="space-y-2">
          <SectionLabel>Topic</SectionLabel>
          <input
            data-testid="input-story-topic"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder="What is your story about?"
            className="w-full bg-muted/50 border border-border focus:border-blue-500 outline-none rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/70"
          />
        </div>
        <div className="space-y-2">
          <SectionLabel>Brand Tone</SectionLabel>
          <input
            data-testid="input-story-tone"
            value={brandTone}
            onChange={(e) => setBrandTone(e.target.value)}
            placeholder="e.g., Professional, Fun, Edgy..."
            className="w-full bg-muted/50 border border-border focus:border-blue-500 outline-none rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/70"
          />
        </div>
        <GenerateButton isPending={mutation.isPending} onClick={() => mutation.mutate()} testId="button-generate-stories" />
      </div>

      {frames.length > 0 && (
        <div className="animate-in fade-in space-y-4" data-testid="generated-stories">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold text-muted-foreground uppercase tracking-wider">Story Frames</h3>
            <div className="flex gap-2">
              <button
                data-testid="button-copy-stories"
                onClick={() => copy(allText, "stories-all")}
                className="border border-border hover:border-border text-muted-foreground hover:text-foreground font-bold rounded-xl px-4 py-2.5 text-sm flex items-center gap-2 transition-colors"
              >
                {copiedKey === "stories-all" ? <Check size={14} className="text-green-400" /> : <Copy size={14} />}
                Copy All
              </button>
              <button
                data-testid="button-save-stories"
                onClick={() => {
                  if (!activeWorkspaceId) return;
                  createContent.mutate({
                    workspaceId: activeWorkspaceId,
                    platform: "Instagram",
                    type: "Story",
                    contentType: "story",
                    caption: allText,
                    slides: frames.map(f => ({ heading: f.ctaText || "", body: f.text || "", imageUrl: f.imageUrl })),
                    mediaUrls: frames.map(f => f.imageUrl).filter(Boolean),
                    mediaUrl: frames[0]?.imageUrl || undefined,
                    status: "DRAFT",
                  });
                  toast({ title: "Saved", description: "Story saved as draft." });
                }}
                className="bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-xl px-4 py-2.5 text-sm flex items-center gap-2 transition-colors"
              >
                <Save size={14} /> Save
              </button>
            </div>
          </div>
          {generatingImages && (
            <div className="flex items-center gap-2 text-xs text-blue-400">
              <Loader2 size={12} className="animate-spin" />
              <span>Generating frame images...</span>
            </div>
          )}
          <div className="flex gap-4 overflow-x-auto pb-4 custom-scrollbar">
            {frames.map((frame, i) => (
              <div
                key={i}
                className="min-w-[220px] max-w-[240px] bg-gradient-to-b from-blue-600/10 to-foreground/[0.03] border border-border rounded-2xl shrink-0 space-y-3 flex flex-col overflow-hidden"
              >
                {frame.imageUrl ? (
                  <img src={frame.imageUrl} alt={`Frame ${i + 1}`} className="w-full h-32 object-cover" data-testid={`img-frame-${i}`} />
                ) : (
                  <div className="w-full h-32 bg-gradient-to-br from-purple-600/10 to-blue-600/10 flex items-center justify-center">
                    {generatingImages && i < 3 ? (
                      <Loader2 size={16} className="animate-spin text-blue-400/40" />
                    ) : (
                      <Image size={20} className="text-muted-foreground/30" />
                    )}
                  </div>
                )}
                <div className="px-5 pb-5 space-y-3 flex flex-col flex-1">
                  <span className="text-xs font-bold text-blue-400 bg-blue-600/10 px-2 py-0.5 rounded-md self-start">
                    Frame {i + 1}
                  </span>
                  <p className="text-sm text-foreground/80 leading-relaxed flex-1">{frame.text}</p>
                  {frame.ctaText && (
                    <div className="bg-blue-600/20 border border-blue-500/30 rounded-xl px-3 py-2 text-center text-xs font-bold text-blue-400">
                      {frame.ctaText}
                    </div>
                  )}
                  <button
                    data-testid={`button-copy-frame-${i}`}
                    onClick={() => copy(frame.text, `frame-${i}`)}
                    className="text-muted-foreground/50 hover:text-foreground transition-colors self-end"
                  >
                    {copiedKey === `frame-${i}` ? <Check size={14} className="text-green-400" /> : <Copy size={14} />}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const AD_PLATFORMS = ["Meta", "Google", "LinkedIn", "Instagram", "X", "TikTok"] as const;
const AD_OBJECTIVES = ["Brand Awareness", "Lead Generation", "Website Traffic", "Conversions", "App Install", "Engagement"] as const;

function AdCreativeTab() {
  const { activeWorkspaceId } = useWorkspaceStore();
  const createContent = useCreateContent();
  const { toast } = useToast();
  const { copiedKey, copy } = useCopyFeedback();

  const [platform, setPlatform] = useState("Meta");
  const [objective, setObjective] = useState("Lead Generation");
  const [product, setProduct] = useState("");
  const [targetAudience, setTargetAudience] = useState("");
  const [tone, setTone] = useState("Professional");
  const [result, setResult] = useState<any>(null);
  const [generatedImage, setGeneratedImage] = useState<string | null>(null);
  const [imageInstructions, setImageInstructions] = useState("");
  const [includeTextOnImage, setIncludeTextOnImage] = useState(true);

  const imageInstructionsRef = useRef(imageInstructions);
  imageInstructionsRef.current = imageInstructions;
  const includeTextRef = useRef(includeTextOnImage);
  includeTextRef.current = includeTextOnImage;

  const imageMutation = useMutation({
    mutationFn: async (params: { prompt: string; textOverlay?: { headline?: string; subtext?: string; cta?: string; style?: string } }) => {
      const res = await apiRequest("POST", "/api/ai/image", { prompt: params.prompt, textOverlay: params.textOverlay, workspaceId: activeWorkspaceId });
      return res.json();
    },
    onSuccess: (data: any) => {
      if (data.imageUrl) setGeneratedImage(data.imageUrl);
    },
    onError: () => {
      toast({ title: "Image Failed", description: "Failed to generate ad image.", variant: "destructive" });
    },
  });

  const generateMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/ai/ad-creative", {
        platform,
        objective,
        targetAudience: targetAudience || "general audience",
        product: product || "our product/service",
        tone,
        workspaceId: activeWorkspaceId,
      });
      return res.json();
    },
    onSuccess: (data: any) => {
      setResult(data);
      setGeneratedImage(null);
      if (data.imagePrompt) {
        const curInstructions = imageInstructionsRef.current;
        const curIncludeText = includeTextRef.current;
        const prompt = curInstructions ? `${data.imagePrompt}. Additional instructions: ${curInstructions}` : data.imagePrompt;
        const params: { prompt: string; textOverlay?: any } = { prompt };
        if (curIncludeText) {
          params.textOverlay = {
            headline: data.headline || "",
            subtext: data.description || "",
            cta: data.ctaOptions?.[0] || "",
          };
        }
        imageMutation.mutate(params);
      }
    },
    onError: (err: any) => {
      toast({ title: "Generation Failed", description: err.message, variant: "destructive" });
    },
  });

  function buildImageRequest() {
    const prompt = result?.imagePrompt || `Professional ad image for ${platform}: ${product}`;
    const fullPrompt = imageInstructions ? `${prompt}. Additional instructions: ${imageInstructions}` : prompt;
    if (includeTextOnImage && result) {
      return {
        prompt: fullPrompt,
        textOverlay: {
          headline: result.headline || "",
          subtext: result.description || "",
          cta: result.ctaOptions?.[0] || "",
        },
      };
    }
    return { prompt: fullPrompt };
  }

  function handleSave() {
    if (!result || !activeWorkspaceId) return;
    const caption = `${result.headline}\n\n${result.primaryText}\n\n${result.description || ""}`;
    createContent.mutate({
      workspaceId: activeWorkspaceId,
      platform,
      type: "Ad",
      caption,
      mediaUrl: generatedImage || undefined,
      status: "DRAFT",
    });
    toast({ title: "Saved", description: "Ad creative saved as draft." });
  }

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="space-y-6">
        <div className="bg-card/50 border border-border rounded-xl p-4 space-y-3">
          <h2 className="text-sm font-display font-bold text-foreground">Ad Creative Generator</h2>
          <p className="text-xs text-muted-foreground">Pete creates ad packages with headlines, copy, CTAs, and visuals.</p>
          <div className="space-y-2">
            <SectionLabel>Ad Platform</SectionLabel>
            <PillSelector options={AD_PLATFORMS} value={platform} onChange={setPlatform} testIdPrefix="button-ad-platform" />
          </div>
          <div className="space-y-2">
            <SectionLabel>Campaign Objective</SectionLabel>
            <PillSelector options={AD_OBJECTIVES} value={objective} onChange={setObjective} testIdPrefix="button-ad-objective" />
          </div>
          <div className="space-y-2">
            <SectionLabel>Tone</SectionLabel>
            <PillSelector options={TONES} value={tone} onChange={(v) => setTone(v)} testIdPrefix="button-ad-tone" />
          </div>
          <div className="space-y-2">
            <SectionLabel>Product / Service</SectionLabel>
            <textarea
              data-testid="input-ad-product"
              value={product}
              onChange={(e) => setProduct(e.target.value)}
              placeholder="Describe the product or service you're advertising..."
              rows={2}
              className="w-full bg-muted/50 border border-border focus:border-blue-500 outline-none rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/70 resize-none"
            />
          </div>
          <div className="space-y-2">
            <SectionLabel>Target Audience</SectionLabel>
            <textarea
              data-testid="input-ad-audience"
              value={targetAudience}
              onChange={(e) => setTargetAudience(e.target.value)}
              placeholder="e.g. Small business owners aged 30-55 interested in accounting services"
              rows={2}
              className="w-full bg-muted/50 border border-border focus:border-blue-500 outline-none rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/70 resize-none"
            />
          </div>
          <div className="space-y-2">
            <SectionLabel>Image Instructions (optional)</SectionLabel>
            <textarea
              data-testid="input-ad-image-instructions"
              value={imageInstructions}
              onChange={(e) => setImageInstructions(e.target.value)}
              placeholder="e.g. Show a calculator on a desk with a tax form, use blue and white colors, include the company logo..."
              rows={2}
              className="w-full bg-muted/50 border border-border focus:border-blue-500 outline-none rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/70 resize-none"
            />
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setIncludeTextOnImage(!includeTextOnImage)}
              className={`w-10 h-5 rounded-full transition-colors relative ${includeTextOnImage ? "bg-blue-600" : "bg-muted"}`}
              data-testid="toggle-text-on-image"
            >
              <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${includeTextOnImage ? "translate-x-5" : "translate-x-0.5"}`} />
            </button>
            <span className="text-sm text-muted-foreground">Include headline & CTA text on image</span>
          </div>
          <GenerateButton
            isPending={generateMutation.isPending}
            onClick={() => generateMutation.mutate()}
            label="Generate Ad Creative"
            testId="button-generate-ad"
          />
        </div>
      </div>

      <div className="space-y-6">
        {result && (
          <div className="bg-card/50 border border-blue-500/20 rounded-2xl p-6 space-y-5 animate-in fade-in" data-testid="ad-creative-result">
            <div className="space-y-3">
              <SectionLabel>Headline</SectionLabel>
              <p className="text-lg font-display font-bold text-foreground">{result.headline}</p>
            </div>

            {result.headlineVariations?.length > 0 && (
              <div className="space-y-2">
                <SectionLabel>A/B Test Variations</SectionLabel>
                <div className="space-y-1.5">
                  {result.headlineVariations.map((v: string, i: number) => (
                    <div key={i} className="flex items-center justify-between gap-2 bg-card/50 border border-border rounded-lg px-3 py-2">
                      <span className="text-sm text-foreground/70">{v}</span>
                      <button
                        onClick={() => copy(v, `headline-${i}`)}
                        className="text-muted-foreground/50 hover:text-foreground transition-colors shrink-0"
                      >
                        {copiedKey === `headline-${i}` ? <Check size={14} className="text-green-400" /> : <Copy size={14} />}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <SectionLabel>Primary Text</SectionLabel>
              <p className="text-sm text-foreground/80 leading-relaxed whitespace-pre-wrap">{result.primaryText}</p>
            </div>

            {result.description && (
              <div className="space-y-2">
                <SectionLabel>Description</SectionLabel>
                <p className="text-sm text-muted-foreground">{result.description}</p>
              </div>
            )}

            {result.ctaOptions?.length > 0 && (
              <div className="space-y-2">
                <SectionLabel>CTA Options</SectionLabel>
                <div className="flex flex-wrap gap-2">
                  {result.ctaOptions.map((cta: string, i: number) => (
                    <span key={i} className="px-3 py-1.5 rounded-lg bg-blue-600/20 text-blue-400 text-xs font-bold">
                      {cta}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {result.targetingNotes && (
              <div className="space-y-2">
                <SectionLabel>Targeting Notes</SectionLabel>
                <p className="text-xs text-muted-foreground leading-relaxed">{result.targetingNotes}</p>
              </div>
            )}

            {imageMutation.isPending && !generatedImage && (
              <div className="rounded-xl border border-border bg-muted/30 h-48 flex items-center justify-center gap-2">
                <Loader2 size={16} className="animate-spin text-blue-400" />
                <span className="text-sm text-muted-foreground">Generating ad image...</span>
              </div>
            )}
            {generatedImage && (
              <div className="rounded-xl overflow-hidden border border-border">
                <img src={generatedImage} alt="Ad Creative" className="w-full" data-testid="img-ad-creative" />
              </div>
            )}

            <ActionBar
              text={`${result.headline}\n\n${result.primaryText}\n\n${result.description || ""}`}
              imagePrompt={result.imagePrompt}
              onSave={handleSave}
              savePending={createContent.isPending}
              copyKey="ad-creative"
              copiedKey={copiedKey}
              onCopy={() => copy(`${result.headline}\n\n${result.primaryText}\n\n${result.description || ""}`, "ad-creative")}
              onGenerateImage={() => imageMutation.mutate(buildImageRequest())}
              imagePending={imageMutation.isPending}
            />
          </div>
        )}

        {!result && (
          <div className="bg-card/50 border border-border rounded-2xl p-8 flex flex-col items-center justify-center text-center gap-3">
            <Megaphone size={32} className="text-muted-foreground/30" />
            <p className="text-sm text-muted-foreground/70">Configure your ad parameters and click generate to create a complete ad creative package.</p>
          </div>
        )}
      </div>
    </div>
  );
}

function EcommerceTab() {
  const { activeWorkspaceId } = useWorkspaceStore();
  const createContent = useCreateContent();
  const { toast } = useToast();
  const { copiedKey, copy } = useCopyFeedback();

  const [productName, setProductName] = useState("");
  const [description, setDescription] = useState("");
  const [platform, setPlatform] = useState("Instagram");
  const [tone, setTone] = useState("Professional");
  const [result, setResult] = useState<{ caption: string; hashtags: string; imagePrompt: string } | null>(null);
  const [generatedImage, setGeneratedImage] = useState<string | null>(null);

  const imageMutation = useMutation({
    mutationFn: async (prompt: string) => {
      const res = await apiRequest("POST", "/api/ai/image", { prompt, workspaceId: activeWorkspaceId });
      return res.json();
    },
    onSuccess: (data: any) => {
      if (data.imageUrl) setGeneratedImage(data.imageUrl);
    },
    onError: () => {
      toast({ title: "Image Failed", description: "Failed to generate image.", variant: "destructive" });
    },
  });

  const mutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/ai/ecom-post", { productName, description, platform, tone, workspaceId: activeWorkspaceId });
      return res.json();
    },
    onSuccess: (data: any) => {
      const imgPrompt = data.imagePrompt || "";
      setResult({
        caption: data.caption || "",
        hashtags: Array.isArray(data.hashtags) ? data.hashtags.join(" ") : data.hashtags || "",
        imagePrompt: imgPrompt,
      });
      setGeneratedImage(null);
      if (imgPrompt) {
        imageMutation.mutate(imgPrompt);
      }
    },
    onError: (err: any) => {
      toast({ title: "Generation Failed", description: err.message, variant: "destructive" });
    },
  });

  const fullText = result ? `${result.caption}\n\n${result.hashtags}` : "";

  return (
    <div className="max-w-4xl space-y-4">
      <div className="bg-card/50 border border-border rounded-xl p-4 space-y-3">
        <h2 className="text-sm font-display font-bold text-foreground">E-Commerce Post Generator</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <SectionLabel>Product Name</SectionLabel>
            <input
              data-testid="input-ecom-product"
              value={productName}
              onChange={(e) => setProductName(e.target.value)}
              placeholder="Product name..."
              className="w-full bg-muted/50 border border-border focus:border-blue-500 outline-none rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/70"
            />
          </div>
          <div className="space-y-2">
            <SectionLabel>Platform</SectionLabel>
            <PillSelector options={PLATFORMS} value={platform} onChange={setPlatform} testIdPrefix="button-ecom-platform" />
          </div>
        </div>
        <div className="space-y-2">
          <SectionLabel>Description</SectionLabel>
          <textarea
            data-testid="input-ecom-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe the product..."
            rows={3}
            className="w-full bg-muted/50 border border-border focus:border-blue-500 outline-none rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/70 resize-none"
          />
        </div>
        <div className="space-y-2">
          <SectionLabel>Tone</SectionLabel>
          <PillSelector options={TONES} value={tone} onChange={setTone} testIdPrefix="button-ecom-tone" />
        </div>
        <GenerateButton isPending={mutation.isPending} onClick={() => mutation.mutate()} testId="button-generate-ecom" />
      </div>

      {result && (
        <div className="bg-card/50 border border-blue-500/20 rounded-2xl p-6 space-y-4 animate-in fade-in" data-testid="generated-ecom">
          <h3 className="text-sm font-bold text-muted-foreground uppercase tracking-wider">Generated E-Commerce Post</h3>
          <div className="space-y-3">
            <div className="space-y-1">
              <span className="text-xs font-bold text-blue-400">Caption</span>
              <p className="text-sm text-foreground/80 leading-relaxed">{result.caption}</p>
            </div>
            <div className="space-y-1">
              <span className="text-xs font-bold text-blue-400">Hashtags</span>
              <p className="text-sm text-muted-foreground">{result.hashtags}</p>
            </div>
            {result.imagePrompt && (
              <div className="space-y-1">
                <span className="text-xs font-bold text-blue-400">Image Prompt</span>
                <p className="text-sm text-muted-foreground italic">{result.imagePrompt}</p>
              </div>
            )}
          </div>
          {imageMutation.isPending && !generatedImage && (
            <div className="rounded-xl border border-border bg-muted/30 h-48 flex items-center justify-center gap-2">
              <Loader2 size={16} className="animate-spin text-blue-400" />
              <span className="text-sm text-muted-foreground">Generating product image...</span>
            </div>
          )}
          {generatedImage && (
            <div className="rounded-xl overflow-hidden border border-border">
              <img src={generatedImage} alt="Generated product" className="w-full" data-testid="img-ecom-generated" />
            </div>
          )}
          <ActionBar
            text={fullText}
            imagePrompt={result.imagePrompt}
            onSave={() => {
              if (!activeWorkspaceId) return;
              createContent.mutate({
                workspaceId: activeWorkspaceId,
                platform,
                type: "Post",
                caption: fullText,
                mediaUrl: generatedImage || undefined,
                status: "DRAFT",
              });
              toast({ title: "Saved", description: "E-commerce post saved as draft." });
            }}
            savePending={createContent.isPending}
            copyKey="ecom"
            copiedKey={copiedKey}
            onCopy={() => copy(fullText, "ecom")}
            onGenerateImage={() => imageMutation.mutate(result.imagePrompt)}
            imagePending={imageMutation.isPending}
          />
        </div>
      )}
    </div>
  );
}

function RepurposeTab() {
  const { activeWorkspaceId } = useWorkspaceStore();
  const createContent = useCreateContent();
  const { toast } = useToast();
  const { copiedKey, copy } = useCopyFeedback();

  const [sourceContent, setSourceContent] = useState("");
  const [selectedFormats, setSelectedFormats] = useState<string[]>(["LinkedIn Post", "Twitter Thread"]);
  const [results, setResults] = useState<{ format: string; content: string }[]>([]);
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  const [articleUrl, setArticleUrl] = useState("");
  const [urlResults, setUrlResults] = useState<{ platform: string; caption: string }[]>([]);
  const [urlArticle, setUrlArticle] = useState<{ title: string; summary: string } | null>(null);
  const [urlExpandedIndex, setUrlExpandedIndex] = useState<number | null>(null);

  const mutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/ai/repurpose", { sourceContent, targetFormats: selectedFormats });
      return res.json();
    },
    onSuccess: (data: any) => {
      setResults(data.results || []);
      setExpandedIndex(0);
    },
    onError: (err: any) => {
      toast({ title: "Generation Failed", description: err.message, variant: "destructive" });
    },
  });

  const urlMutation = useMutation({
    mutationFn: async () => {
      if (!activeWorkspaceId) throw new Error("No workspace selected");
      const res = await apiRequest("POST", `/api/workspaces/${activeWorkspaceId}/repurpose-url`, { url: articleUrl });
      return res.json();
    },
    onSuccess: (data: any) => {
      setUrlArticle(data.article || null);
      setUrlResults(data.posts || []);
      setUrlExpandedIndex(0);
      toast({ title: "Repurposed!", description: `${data.savedCount} social posts created and saved to your content queue.` });
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "content"] });
    },
    onError: (err: any) => {
      toast({ title: "Repurpose Failed", description: err.message, variant: "destructive" });
    },
  });

  function toggleFormat(f: string) {
    setSelectedFormats((prev) => (prev.includes(f) ? prev.filter((x) => x !== f) : [...prev, f]));
  }

  return (
    <div className="max-w-4xl space-y-4">
      <div className="bg-card/50 border border-border rounded-xl p-4 space-y-3">
        <h2 className="text-sm font-display font-bold text-foreground flex items-center gap-2">
          <RefreshCw size={14} className="text-blue-400" />
          Repurpose Article URL
        </h2>
        <p className="text-xs text-muted-foreground">Paste any article URL and we'll fetch it, summarize it, and generate 4 branded social posts in your voice.</p>
        <div className="space-y-2">
          <SectionLabel>Article URL</SectionLabel>
          <input
            data-testid="input-repurpose-url"
            value={articleUrl}
            onChange={(e) => setArticleUrl(e.target.value)}
            placeholder="https://example.com/article-to-repurpose"
            className="w-full bg-muted/50 border border-border focus:border-blue-500 outline-none rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/70"
          />
        </div>
        <GenerateButton
          isPending={urlMutation.isPending}
          onClick={() => urlMutation.mutate()}
          testId="button-repurpose-url"
          label="Fetch & Repurpose"
        />
      </div>

      {urlArticle && (
        <div className="bg-card/50 border border-border rounded-xl p-4 space-y-2">
          <h3 className="text-sm font-bold text-foreground">{urlArticle.title}</h3>
          <p className="text-xs text-muted-foreground">{urlArticle.summary}</p>
        </div>
      )}

      {urlResults.length > 0 && (
        <div className="space-y-3 animate-in fade-in" data-testid="generated-url-repurpose">
          <h3 className="text-sm font-bold text-muted-foreground uppercase tracking-wider">Generated Social Posts</h3>
          {urlResults.map((r, i) => (
            <div key={i} className="bg-card/50 border border-border rounded-2xl overflow-hidden">
              <button
                data-testid={`button-url-expand-${i}`}
                onClick={() => setUrlExpandedIndex(urlExpandedIndex === i ? null : i)}
                className="w-full px-5 py-4 flex items-center justify-between text-left"
              >
                <span className="text-sm font-bold text-foreground">{r.platform}</span>
                <ChevronRight
                  size={16}
                  className={`text-muted-foreground transition-transform ${urlExpandedIndex === i ? "rotate-90" : ""}`}
                />
              </button>
              {urlExpandedIndex === i && (
                <div className="px-5 pb-5 space-y-3 animate-in fade-in">
                  <p className="text-sm text-foreground/80 leading-relaxed whitespace-pre-wrap">{r.caption}</p>
                  <div className="flex gap-2 flex-wrap">
                    <button
                      data-testid={`button-copy-url-repurpose-${i}`}
                      onClick={() => copy(r.caption, `url-repurpose-${i}`)}
                      className="border border-border hover:border-border text-muted-foreground hover:text-foreground font-bold rounded-xl px-4 py-2.5 text-sm flex items-center gap-2 transition-colors"
                    >
                      {copiedKey === `url-repurpose-${i}` ? <Check size={14} className="text-green-400" /> : <Copy size={14} />}
                      {copiedKey === `url-repurpose-${i}` ? "Copied" : "Copy"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="bg-card/50 border border-border rounded-xl p-4 space-y-3">
        <h2 className="text-sm font-display font-bold text-foreground">Content Repurposer</h2>
        <div className="space-y-2">
          <SectionLabel>Source Content</SectionLabel>
          <textarea
            data-testid="input-repurpose-source"
            value={sourceContent}
            onChange={(e) => setSourceContent(e.target.value)}
            placeholder="Paste your blog post, article, transcript, or any long-form content..."
            rows={8}
            className="w-full bg-muted/50 border border-border focus:border-blue-500 outline-none rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/70 resize-none"
          />
        </div>
        <div className="space-y-2">
          <SectionLabel>Target Formats</SectionLabel>
          <div className="flex flex-wrap gap-2">
            {REPURPOSE_FORMATS.map((f) => (
              <button
                key={f}
                data-testid={`button-format-${f.toLowerCase().replace(/\s/g, "-")}`}
                onClick={() => toggleFormat(f)}
                className={`px-2 py-1 rounded-md text-[11px] font-medium transition-colors ${
                  selectedFormats.includes(f)
                    ? "bg-blue-600/20 text-blue-400 border border-blue-500/30"
                    : "bg-muted/50 text-muted-foreground border border-border hover:text-foreground hover:border-border"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>
        <GenerateButton
          isPending={mutation.isPending}
          onClick={() => mutation.mutate()}
          testId="button-generate-repurpose"
          label="Repurpose"
        />
      </div>

      {results.length > 0 && (
        <div className="space-y-3 animate-in fade-in" data-testid="generated-repurpose">
          <h3 className="text-sm font-bold text-muted-foreground uppercase tracking-wider">Repurposed Content</h3>
          {results.map((r, i) => (
            <div key={i} className="bg-card/50 border border-border rounded-2xl overflow-hidden">
              <button
                data-testid={`button-expand-${i}`}
                onClick={() => setExpandedIndex(expandedIndex === i ? null : i)}
                className="w-full px-5 py-4 flex items-center justify-between text-left"
              >
                <span className="text-sm font-bold text-foreground">{r.format}</span>
                <ChevronRight
                  size={16}
                  className={`text-muted-foreground transition-transform ${expandedIndex === i ? "rotate-90" : ""}`}
                />
              </button>
              {expandedIndex === i && (
                <div className="px-5 pb-5 space-y-3 animate-in fade-in">
                  <p className="text-sm text-foreground/80 leading-relaxed whitespace-pre-wrap">{r.content}</p>
                  <div className="flex gap-2 flex-wrap">
                    <button
                      data-testid={`button-copy-repurpose-${i}`}
                      onClick={() => copy(r.content, `repurpose-${i}`)}
                      className="border border-border hover:border-border text-muted-foreground hover:text-foreground font-bold rounded-xl px-4 py-2.5 text-sm flex items-center gap-2 transition-colors"
                    >
                      {copiedKey === `repurpose-${i}` ? <Check size={14} className="text-green-400" /> : <Copy size={14} />}
                      {copiedKey === `repurpose-${i}` ? "Copied" : "Copy"}
                    </button>
                    <button
                      data-testid={`button-save-repurpose-${i}`}
                      onClick={() => {
                        if (!activeWorkspaceId) return;
                        createContent.mutate({
                          workspaceId: activeWorkspaceId,
                          platform: r.format.includes("LinkedIn")
                            ? "LinkedIn"
                            : r.format.includes("Twitter")
                              ? "X"
                              : r.format.includes("Instagram")
                                ? "Instagram"
                                : r.format.includes("Facebook")
                                  ? "Meta"
                                  : "LinkedIn",
                          type: "Post",
                          caption: r.content,
                          status: "DRAFT",
                        });
                        toast({ title: "Saved", description: `${r.format} saved as draft.` });
                      }}
                      className="bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-xl px-4 py-2.5 text-sm flex items-center gap-2 transition-colors"
                    >
                      <Save size={14} /> Save
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MemeTab() {
  const { activeWorkspaceId } = useWorkspaceStore();
  const createContent = useCreateContent();
  const { toast } = useToast();
  const { copiedKey, copy } = useCopyFeedback();

  const [topic, setTopic] = useState("");
  const [style, setStyle] = useState("Relatable");
  const [result, setResult] = useState<{ topText: string; bottomText: string; imagePrompt: string } | null>(null);
  const [generatedImage, setGeneratedImage] = useState<string | null>(null);

  const imageMutation = useMutation({
    mutationFn: async (prompt: string) => {
      const res = await apiRequest("POST", "/api/ai/image", { prompt, workspaceId: activeWorkspaceId });
      return res.json();
    },
    onSuccess: (data: any) => {
      if (data.imageUrl) setGeneratedImage(data.imageUrl);
    },
    onError: () => {
      toast({ title: "Image Failed", description: "Failed to generate image.", variant: "destructive" });
    },
  });

  const mutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/ai/meme", { topic, style, workspaceId: activeWorkspaceId });
      return res.json();
    },
    onSuccess: (data: any) => {
      const imgPrompt = data.imagePrompt || "";
      setResult({ topText: data.topText || "", bottomText: data.bottomText || "", imagePrompt: imgPrompt });
      setGeneratedImage(null);
      if (imgPrompt) {
        imageMutation.mutate(imgPrompt);
      }
    },
    onError: (err: any) => {
      toast({ title: "Generation Failed", description: err.message, variant: "destructive" });
    },
  });

  const memeText = result ? `${result.topText}\n\n${result.bottomText}` : "";

  return (
    <div className="max-w-4xl space-y-4">
      <div className="bg-card/50 border border-border rounded-xl p-4 space-y-3">
        <h2 className="text-sm font-display font-bold text-foreground">AI Meme Maker</h2>
        <div className="space-y-2">
          <SectionLabel>Topic</SectionLabel>
          <input
            data-testid="input-meme-topic"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder="What should the meme be about?"
            className="w-full bg-muted/50 border border-border focus:border-blue-500 outline-none rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/70"
          />
        </div>
        <div className="space-y-2">
          <SectionLabel>Style</SectionLabel>
          <PillSelector options={MEME_STYLES} value={style} onChange={setStyle} testIdPrefix="button-meme-style" />
        </div>
        <GenerateButton isPending={mutation.isPending} onClick={() => mutation.mutate()} testId="button-generate-meme" />
      </div>

      {result && (
        <div className="bg-card/50 border border-blue-500/20 rounded-2xl p-6 space-y-4 animate-in fade-in" data-testid="generated-meme">
          <h3 className="text-sm font-bold text-muted-foreground uppercase tracking-wider">Meme Concept</h3>
          <div className="bg-muted/30 border border-border rounded-xl p-6 space-y-4 text-center">
            <p className="text-lg font-bold text-foreground uppercase">{result.topText}</p>
            {generatedImage ? (
              <div className="rounded-xl overflow-hidden border border-border mx-auto max-w-sm">
                <img src={generatedImage} alt="Meme" className="w-full" data-testid="img-meme-generated" />
              </div>
            ) : imageMutation.isPending ? (
              <div className="bg-muted/50 border border-border rounded-xl h-48 flex items-center justify-center gap-2">
                <Loader2 size={16} className="animate-spin text-blue-400" />
                <span className="text-muted-foreground text-sm">Generating meme image...</span>
              </div>
            ) : (
              <div className="bg-muted/50 border border-border rounded-xl h-48 flex items-center justify-center">
                <Image size={24} className="text-muted-foreground/30" />
              </div>
            )}
            <p className="text-lg font-bold text-foreground uppercase">{result.bottomText}</p>
          </div>
          {result.imagePrompt && (
            <div className="space-y-1">
              <span className="text-xs font-bold text-blue-400">Image Prompt</span>
              <p className="text-sm text-muted-foreground italic">{result.imagePrompt}</p>
            </div>
          )}
          <ActionBar
            text={memeText}
            imagePrompt={result.imagePrompt}
            onSave={() => {
              if (!activeWorkspaceId) return;
              createContent.mutate({
                workspaceId: activeWorkspaceId,
                platform: "Instagram",
                type: "Post",
                caption: memeText,
                mediaUrl: generatedImage || undefined,
                status: "DRAFT",
              });
              toast({ title: "Saved", description: "Meme saved as draft." });
            }}
            savePending={createContent.isPending}
            copyKey="meme"
            copiedKey={copiedKey}
            onCopy={() => copy(memeText, "meme")}
            onGenerateImage={() => imageMutation.mutate(result.imagePrompt)}
            imagePending={imageMutation.isPending}
          />
        </div>
      )}
    </div>
  );
}

function UnscheduledDraftRow({ item, workspaceId }: { item: ContentItem; workspaceId: number }) {
  const { toast } = useToast();
  const [picking, setPicking] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [scheduled, setScheduled] = useState(false);

  // Default to tomorrow at 9am local time
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(9, 0, 0, 0);
  const tomorrowLocal = tomorrow.toISOString().slice(0, 16);

  const [pickedDate, setPickedDate] = useState(tomorrowLocal);

  const handleConfirm = async () => {
    setScheduling(true);
    try {
      await apiRequest("PATCH", `/api/content/${item.id}`, {
        status: "SCHEDULED",
        scheduledAt: new Date(pickedDate).toISOString(),
      });
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", workspaceId, "content"] });
      setScheduled(true);
      setPicking(false);
      toast({ title: "Scheduled", description: `${item.platform} post queued.` });
    } catch {
      toast({ title: "Failed to schedule", variant: "destructive" });
    } finally { setScheduling(false); }
  };

  const _pf = (item.platform || "").toLowerCase();
  const platformColor = _pf === "facebook" ? "bg-blue-500" : _pf === "instagram" ? "bg-pink-500" : _pf === "linkedin" ? "bg-blue-400" : _pf === "x" ? "bg-white/60" : "bg-purple-400";

  return (
    <div
      className={`rounded-lg border transition-all ${scheduled ? "opacity-40 border-border" : "bg-muted/20 border-border"}`}
      data-testid={`unscheduled-draft-${item.id}`}
    >
      <div className="flex items-center gap-3 px-3 py-2">
        <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${platformColor}`} />
        <div className="flex-1 min-w-0">
          <span className="text-[10px] font-bold text-muted-foreground/60 uppercase">{item.platform}</span>
          <p className="text-xs text-muted-foreground truncate">{item.caption}</p>
        </div>
        {scheduled ? (
          <CheckCircle2 size={12} className="text-emerald-400 shrink-0" />
        ) : picking ? (
          <button
            data-testid={`button-cancel-schedule-${item.id}`}
            onClick={() => setPicking(false)}
            className="shrink-0 text-muted-foreground/40 hover:text-muted-foreground text-[10px] transition-colors"
          >
            cancel
          </button>
        ) : (
          <button
            data-testid={`button-schedule-draft-${item.id}`}
            onClick={() => setPicking(true)}
            className="shrink-0 flex items-center gap-1 px-2.5 py-1 bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/25 text-blue-400 text-[10px] font-bold rounded-md transition-all"
          >
            <Calendar size={9} /> Schedule
          </button>
        )}
      </div>
      {picking && !scheduled && (
        <div className="flex items-center gap-2 px-3 pb-2.5">
          <input
            type="datetime-local"
            value={pickedDate}
            onChange={e => setPickedDate(e.target.value)}
            data-testid={`input-schedule-date-${item.id}`}
            className="flex-1 text-[11px] bg-background border border-border rounded-md px-2 py-1 text-foreground focus:outline-none focus:border-blue-500/50"
          />
          <button
            data-testid={`button-confirm-schedule-${item.id}`}
            onClick={handleConfirm}
            disabled={scheduling || !pickedDate}
            className="flex items-center gap-1 px-2.5 py-1 bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-500/30 text-emerald-400 text-[10px] font-bold rounded-md transition-all disabled:opacity-50"
          >
            {scheduling ? <Loader2 size={9} className="animate-spin" /> : <CheckCircle2 size={9} />}
            Confirm
          </button>
        </div>
      )}
    </div>
  );
}

function ContentCalendarTab() {
  const { activeWorkspaceId } = useWorkspaceStore();
  const { data: savedContent, isLoading: loadingContent } = useContent();
  const { toast } = useToast();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [editItem, setEditItem] = useState<ContentItem | null>(null);
  const [approvingAll, setApprovingAll] = useState(false);
  const [stampingItemId, setStampingItemId] = useState<number | null>(null);

  const { data: brandKit } = useQuery<any>({
    queryKey: ["/api/workspaces", activeWorkspaceId, "brand-kit"],
    enabled: !!activeWorkspaceId,
  });

  const handleCalendarStamp = async (e: React.MouseEvent, item: ContentItem) => {
    e.stopPropagation();
    if (!activeWorkspaceId || stampingItemId || !item.mediaUrl) return;
    setStampingItemId(item.id);
    try {
      const res = await fetch(`/api/workspaces/${activeWorkspaceId}/apply-brand-stamp`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ postId: item.id }),
      });
      const data = await res.json();
      if (res.ok) {
        queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "content"] });
        toast({ title: "Stamp applied", description: "Brand stamp baked into image." });
      } else {
        toast({ title: "Stamp failed", description: data.message, variant: "destructive" });
      }
    } catch { toast({ title: "Stamp error", variant: "destructive" }); }
    finally { setStampingItemId(null); }
  };

  const handleCalendarApproveAll = async () => {
    setApprovingAll(true);
    try {
      const res = await apiRequest("POST", `/api/workspaces/${activeWorkspaceId}/content/approve-all-pending`, {});
      const result = await res.json();
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "content"] });
      toast({ title: "Done", description: `${result.approved} post${result.approved !== 1 ? "s" : ""} approved and scheduled.` });
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally {
      setApprovingAll(false);
    }
  };
  const [viewMode, setViewMode] = useState<"month" | "week">("month");
  const [blogsOnly, setBlogsOnly] = useState(false);
  const [personalBrandOnly, setPersonalBrandOnly] = useState(false);
  const [selectedWeekStart, setSelectedWeekStart] = useState<Date>(() => {
    const now = new Date();
    const day = now.getDay();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate() - day);
  });
  const [dayDetailDate, setDayDetailDate] = useState<Date | null>(null);

  const { data: playbook } = useQuery({
    queryKey: ["/api/workspaces", activeWorkspaceId, "playbook"],
    enabled: !!activeWorkspaceId,
  });

  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const monthName = currentMonth.toLocaleString("default", { month: "long", year: "numeric" });

  const contentByDate: Record<string, ContentItem[]> = {};
  if (savedContent) {
    const filteredContent = blogsOnly
      ? savedContent.filter((item: ContentItem) => item.contentType === "blog" || item.platform === "blog" || item.platform === "WordPress")
      : personalBrandOnly
      ? savedContent.filter((item: ContentItem) => item.contentMode === "personal_brand" || (item as any).contentCategory === "personal_brand")
      : savedContent;
    filteredContent.forEach((item: ContentItem) => {
      if (item.scheduledAt) {
        const d = new Date(item.scheduledAt);
        const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
        if (!contentByDate[key]) contentByDate[key] = [];
        contentByDate[key].push(item);
      }
    });
  }

  const prevMonth = () => setCurrentMonth(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentMonth(new Date(year, month + 1, 1));

  const prevWeek = () => {
    const prev = new Date(selectedWeekStart);
    prev.setDate(prev.getDate() - 7);
    setSelectedWeekStart(prev);
  };
  const nextWeek = () => {
    const next = new Date(selectedWeekStart);
    next.setDate(next.getDate() + 7);
    setSelectedWeekStart(next);
  };

  const weekDates = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(selectedWeekStart);
    d.setDate(d.getDate() + i);
    return d;
  });

  const weekLabel = `${weekDates[0].toLocaleDateString("default", { month: "short", day: "numeric" })} – ${weekDates[6].toLocaleDateString("default", { month: "short", day: "numeric", year: "numeric" })}`;

  const days: (number | null)[] = [];
  for (let i = 0; i < firstDay; i++) days.push(null);
  for (let d = 1; d <= daysInMonth; d++) days.push(d);

  const platformColor = (p: string) => {
    const colors: Record<string, string> = { LinkedIn: "bg-blue-500", Instagram: "bg-pink-500", X: "bg-foreground/60", Meta: "bg-blue-400", Google: "bg-green-500", TikTok: "bg-purple-500" };
    return colors[p] || "bg-foreground/30";
  };

  const categoryColor = (category: string | null | undefined) => {
    if (!category) return "bg-muted/50";
    const c = category.toLowerCase();
    if (c.includes("education") || c.includes("tips")) return "bg-blue-500/20 border-blue-500/30";
    if (c.includes("promotion")) return "bg-orange-500/20 border-orange-500/30";
    if (c.includes("engagement") || c.includes("question")) return "bg-green-500/20 border-green-500/30";
    if (c.includes("behind") || c.includes("scenes")) return "bg-purple-500/20 border-purple-500/30";
    if (c.includes("news") || c.includes("announcement")) return "bg-amber-500/20 border-amber-500/30";
    return "bg-muted/50";
  };

  const platformIcon = (p: string) => {
    const icons: Record<string, string> = {
      LinkedIn: "in",
      Instagram: "ig",
      X: "𝕏",
      Meta: "fb",
      Google: "G",
      TikTok: "tt",
    };
    return icons[p] || "?";
  };

  const handleDayClick = (dateObj: Date) => {
    setDayDetailDate(dateObj);
  };

  const dayDetailKey = dayDetailDate ? `${dayDetailDate.getFullYear()}-${dayDetailDate.getMonth()}-${dayDetailDate.getDate()}` : "";
  const dayDetailItems = dayDetailKey ? contentByDate[dayDetailKey] || [] : [];

  return (
    <div className="space-y-4" data-testid="content-calendar">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-display font-bold text-foreground" data-testid="calendar-title">
          {viewMode === "month" ? monthName : weekLabel}
        </h2>
        <div className="flex items-center gap-3">
          <button
            data-testid="button-export-ical"
            onClick={() => {
              const a = document.createElement("a");
              a.href = `/api/workspaces/${activeWorkspaceId}/export/content-calendar.ics`;
              a.download = "content-calendar.ics";
              a.click();
            }}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-border text-muted-foreground hover:text-foreground transition-colors"
          >
            <Download size={12} />
            iCal
          </button>
          <button
            data-testid="button-blogs-only-filter"
            onClick={() => { setBlogsOnly(v => !v); setPersonalBrandOnly(false); }}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${blogsOnly ? "border-[#21759B]/50 bg-[#21759B]/10 text-[#21759B]" : "border-border text-muted-foreground hover:text-foreground"}`}
          >
            <BookOpen size={12} />
            Blogs only
          </button>
          <button
            data-testid="button-personal-brand-filter"
            onClick={() => { setPersonalBrandOnly(v => !v); setBlogsOnly(false); }}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${personalBrandOnly ? "border-purple-500/50 bg-purple-500/10 text-purple-400" : "border-border text-muted-foreground hover:text-foreground"}`}
          >
            <UserCog size={12} />
            Personal only
          </button>
          <div className="flex bg-muted/30 border border-border rounded-lg p-0.5" data-testid="calendar-view-toggle">
            <button
              onClick={() => setViewMode("month")}
              className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${viewMode === "month" ? "bg-muted text-foreground" : "text-muted-foreground/70 hover:text-muted-foreground"}`}
              data-testid="button-month-view"
            >
              Month
            </button>
            <button
              onClick={() => setViewMode("week")}
              className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${viewMode === "week" ? "bg-muted text-foreground" : "text-muted-foreground/70 hover:text-muted-foreground"}`}
              data-testid="button-week-view"
            >
              Week
            </button>
          </div>
          <div className="flex gap-1">
            <button
              onClick={viewMode === "month" ? prevMonth : prevWeek}
              className="p-2 rounded-lg bg-muted/50 hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
              data-testid="button-prev-month"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              onClick={viewMode === "month" ? nextMonth : nextWeek}
              className="p-2 rounded-lg bg-muted/50 hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
              data-testid="button-next-month"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>

      {viewMode === "month" ? (
        <div className="grid grid-cols-7 gap-px bg-muted/50 rounded-xl overflow-hidden border border-border">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
            <div key={d} className="py-2 text-center text-[10px] font-bold text-muted-foreground/70 uppercase tracking-wider bg-muted/30">{d}</div>
          ))}
          {days.map((day, i) => {
            const key = day ? `${year}-${month}-${day}` : "";
            const items = key ? contentByDate[key] || [] : [];
            const isToday = day && new Date().getDate() === day && new Date().getMonth() === month && new Date().getFullYear() === year;
            
            const dayOfWeek = day ? new Date(year, month, day).getDay() : null;
            const playbookEntries = (playbook as any)?.weeklyCalendar?.filter((entry: any) => {
              const entryDayIndex = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"].indexOf(entry.day);
              return entryDayIndex === dayOfWeek;
            }) || [];

            return (
              <div
                key={i}
                className={`min-h-[80px] p-1.5 bg-background/40 ${day ? "hover:bg-card/50 cursor-pointer" : ""} transition-colors`}
                data-testid={day ? `calendar-day-${day}` : undefined}
                onClick={() => day && handleDayClick(new Date(year, month, day))}
              >
                {day && (
                  <>
                    <span className={`text-xs font-medium block mb-1 ${isToday ? "text-blue-400 font-bold" : "text-muted-foreground/70"}`}>{day}</span>
                    {playbookEntries.length > 0 && (
                      <div className="space-y-0.5 mb-1">
                        {playbookEntries.slice(0, 2).map((entry: any, idx: number) => (
                          <div key={idx} className="flex items-center gap-1 px-1 py-0.5 rounded bg-blue-500/10 border border-blue-500/20" title={`Playbook: ${entry.topic || entry.caption_theme}`}>
                            <span className="text-[8px] font-bold text-blue-400/70 shrink-0" title={entry.platform}>{platformIcon(entry.platform)}</span>
                            <span className="text-[8px] text-blue-400/70 truncate">{entry.content_type || "Post"}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="space-y-0.5">
                      {items.slice(0, 3).map((item: ContentItem) => (
                        <div
                          key={item.id}
                          className={`flex items-center gap-1 px-1 py-0.5 rounded border cursor-pointer hover:bg-muted transition-colors group/cal ${categoryColor((item as any).contentCategory)}`}
                          data-testid={`calendar-item-${item.id}`}
                          onClick={(e) => { e.stopPropagation(); setEditItem(item); }}
                          title={item.caption?.slice(0, 100)}
                        >
                          <span className="text-[8px] font-bold shrink-0" title={item.platform}>{platformIcon(item.platform)}</span>
                          {(item as any).contentType === "blog" && (
                            <span className="text-[7px] font-bold uppercase bg-[#21759B]/20 text-[#21759B] border border-[#21759B]/30 rounded px-0.5 shrink-0">Blog</span>
                          )}
                          {(item as any).contentType === "blog" && (item as any).seoScore != null && (
                            <span className={`text-[7px] font-bold rounded px-0.5 shrink-0 border ${(item as any).seoScore >= 90 ? "bg-green-500/15 text-green-400 border-green-500/30" : (item as any).seoScore >= 75 ? "bg-yellow-500/15 text-yellow-400 border-yellow-500/30" : "bg-red-500/15 text-red-400 border-red-500/30"}`} data-testid={`badge-seo-score-${item.id}`}>SEO {(item as any).seoScore}</span>
                          )}
                          <span className="text-[9px] text-muted-foreground truncate flex-1">{item.caption?.slice(0, 30) || item.type}</span>
                          {(item as any).blogUrl && (
                            <a href={(item as any).blogUrl} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="text-[8px] text-[#21759B] shrink-0 hover:underline" title="View live post">↗</a>
                          )}
                          {((item as any).qualityStatus === "flagged" || (item as any).qualityStatus === "needs_review") && (
                            <span className="text-[9px] shrink-0" title="Needs review">⚠</span>
                          )}
                          {item.approvalStatus === "pending_approval" && (
                            <div className="w-1.5 h-1.5 rounded-full bg-yellow-400 shrink-0" title="Pending approval" />
                          )}
                          {item.approvalStatus === "approved" && (
                            <div className="w-1.5 h-1.5 rounded-full bg-green-400 shrink-0" title="Approved" />
                          )}
                        </div>
                      ))}
                      {items.length > 3 && <span className="text-[9px] text-muted-foreground/70 px-1">+{items.length - 3} more</span>}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="rounded-xl overflow-hidden border border-border" data-testid="calendar-week-view">
          {/* Day headers */}
          <div className="grid grid-cols-[80px_repeat(7,1fr)] gap-px bg-muted/50">
            <div className="bg-muted/30" />
            {weekDates.map((wd) => {
              const dayName = wd.toLocaleDateString("default", { weekday: "short" });
              const dayNum = wd.getDate();
              const isToday = wd.toDateString() === new Date().toDateString();
              return (
                <div key={wd.toISOString()} className="text-center py-2 bg-muted/30">
                  <div className="text-[10px] font-bold text-muted-foreground/70 uppercase tracking-wider">{dayName}</div>
                  <div className={`text-sm font-bold ${isToday ? "text-blue-400" : "text-muted-foreground"}`}>{dayNum}</div>
                </div>
              );
            })}
          </div>

          {/* Business lane */}
          <div className="grid grid-cols-[80px_repeat(7,1fr)] gap-px bg-muted/30 border-t border-border" data-testid="week-lane-business">
            <div className="flex items-center justify-center bg-blue-500/5 border-r border-blue-500/20 p-1">
              <span className="text-[9px] font-bold text-blue-400 uppercase tracking-wider rotate-0 writing-mode-vertical">Business</span>
            </div>
            {weekDates.map((wd) => {
              const key = `${wd.getFullYear()}-${wd.getMonth()}-${wd.getDate()}`;
              const allItems: ContentItem[] = contentByDate[key] || [];
              const items = allItems.filter((item: ContentItem) => item.contentMode !== "personal_brand" && (item as any).contentCategory !== "personal_brand");
              const isToday = wd.toDateString() === new Date().toDateString();
              const dayOfWeek = wd.getDay();
              const playbookEntries = (playbook as any)?.weeklyCalendar?.filter((entry: any) => {
                const entryDayIndex = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"].indexOf(entry.day);
                return entryDayIndex === dayOfWeek;
              }) || [];
              return (
                <div
                  key={`biz-${wd.toISOString()}`}
                  className={`min-h-[160px] p-1.5 bg-background/40 hover:bg-card/50 cursor-pointer transition-colors ${isToday ? "ring-1 ring-inset ring-blue-500/30" : ""}`}
                  data-testid={`week-day-${wd.getDate()}`}
                  onClick={() => handleDayClick(wd)}
                >
                  <div className="space-y-1">
                    {playbookEntries.length > 0 && (
                      <div className="space-y-1 mb-1.5 pb-1.5 border-b border-blue-500/20">
                        {playbookEntries.map((entry: any, idx: number) => (
                          <div key={idx} className="flex items-center gap-1 px-1 py-0.5 rounded bg-blue-500/10 border border-blue-500/20" title={`Playbook: ${entry.topic || entry.caption_theme}`}>
                            <span className="text-[9px] font-bold text-blue-400/70 shrink-0">{platformIcon(entry.platform)}</span>
                            <div className="flex-1 min-w-0">
                              <div className="text-[8px] text-blue-400/60">{entry.content_type || "Post"}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {items.length === 0 && playbookEntries.length === 0 && (
                      <span className="text-[9px] text-muted-foreground/30 italic">—</span>
                    )}
                    {items.map((item: ContentItem) => (
                      <div
                        key={item.id}
                        className={`flex items-start gap-1 p-1 rounded border cursor-pointer hover:bg-muted transition-colors ${categoryColor((item as any).contentCategory)}`}
                        data-testid={`week-item-${item.id}`}
                        onClick={(e) => { e.stopPropagation(); setEditItem(item); }}
                      >
                        <span className="text-[9px] shrink-0">{platformIcon(item.platform)}</span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1">
                            <span className="text-[8px] font-bold text-blue-400 uppercase">{item.platform?.slice(0, 2)}</span>
                            {(item as any).contentType === "blog" && <span className="text-[6px] font-bold bg-[#21759B]/20 text-[#21759B] rounded px-0.5">Blog</span>}
                            {item.approvalStatus === "approved" && <div className="w-1 h-1 rounded-full bg-green-400 shrink-0" />}
                          </div>
                          <p className="text-[8px] text-muted-foreground line-clamp-2">{item.caption?.slice(0, 50)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Personal Brand lane */}
          <div className="grid grid-cols-[80px_repeat(7,1fr)] gap-px bg-muted/30 border-t border-purple-500/20" data-testid="week-lane-personal-brand">
            <div className="flex items-center justify-center bg-purple-500/5 border-r border-purple-500/20 p-1">
              <span className="text-[9px] font-bold text-purple-400 uppercase tracking-wider">Personal</span>
            </div>
            {weekDates.map((wd) => {
              const key = `${wd.getFullYear()}-${wd.getMonth()}-${wd.getDate()}`;
              const allItems: ContentItem[] = contentByDate[key] || [];
              const items = allItems.filter((item: ContentItem) => item.contentMode === "personal_brand" || (item as any).contentCategory === "personal_brand");
              const isToday = wd.toDateString() === new Date().toDateString();
              return (
                <div
                  key={`pb-${wd.toISOString()}`}
                  className={`min-h-[120px] p-1.5 bg-background/40 hover:bg-purple-500/5 cursor-pointer transition-colors ${isToday ? "ring-1 ring-inset ring-purple-500/30" : ""}`}
                  data-testid={`week-day-pb-${wd.getDate()}`}
                  onClick={() => handleDayClick(wd)}
                >
                  <div className="space-y-1">
                    {items.length === 0 && (
                      <span className="text-[9px] text-muted-foreground/30 italic">—</span>
                    )}
                    {items.map((item: ContentItem) => (
                      <div
                        key={item.id}
                        className="flex items-start gap-1 p-1 rounded border border-purple-500/20 bg-purple-500/10 cursor-pointer hover:bg-purple-500/20 transition-colors"
                        data-testid={`week-item-pb-${item.id}`}
                        onClick={(e) => { e.stopPropagation(); setEditItem(item); }}
                      >
                        <span className="text-[9px] shrink-0">👤</span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1">
                            <span className="text-[8px] font-bold text-purple-400 uppercase">LI</span>
                            {item.approvalStatus === "approved" && <div className="w-1 h-1 rounded-full bg-green-400 shrink-0" />}
                          </div>
                          <p className="text-[8px] text-purple-300/80 line-clamp-2">{item.caption?.slice(0, 50)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {(() => {
        if (!savedContent || viewMode !== "month") return null;
        const today = new Date();
        const futureItems = savedContent.filter((item: ContentItem) => {
          if (!item.scheduledAt) return false;
          const d = new Date(item.scheduledAt);
          return d >= today;
        });
        const currentMonthFutureItems = futureItems.filter((item: ContentItem) => {
          const d = new Date(item.scheduledAt!);
          return d.getFullYear() === year && d.getMonth() === month;
        });
        if (currentMonthFutureItems.length >= 3) return null;
        const nextMonthItems: { month: Date; count: number } | null = (() => {
          const monthCounts = new Map<string, { month: Date; count: number }>();
          futureItems.forEach((item: ContentItem) => {
            const d = new Date(item.scheduledAt!);
            const fy = d.getFullYear();
            const fm = d.getMonth();
            if (fy > year || (fy === year && fm > month)) {
              const key = `${fy}-${fm}`;
              if (!monthCounts.has(key)) monthCounts.set(key, { month: new Date(fy, fm, 1), count: 0 });
              monthCounts.get(key)!.count += 1;
            }
          });
          if (monthCounts.size === 0) return null;
          return Array.from(monthCounts.values()).sort((a, b) => a.month.getTime() - b.month.getTime())[0];
        })();
        if (!nextMonthItems) return null;
        const nextMonthName = nextMonthItems.month.toLocaleString("default", { month: "long" });
        return (
          <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-blue-500/10 border border-blue-500/20 rounded-xl" data-testid="calendar-content-ahead-banner">
            <div className="flex items-center gap-2 text-sm text-blue-400/90">
              <Calendar size={14} className="shrink-0" />
              <span>Your next <span className="font-bold">{nextMonthItems.count}</span> post{nextMonthItems.count !== 1 ? "s are" : " is"} scheduled in <span className="font-bold">{nextMonthName}</span></span>
            </div>
            <button
              data-testid="button-jump-to-next-month"
              onClick={() => setCurrentMonth(nextMonthItems.month)}
              className="flex items-center gap-1 text-xs font-bold text-blue-400 hover:text-blue-300 transition-colors shrink-0"
            >
              Jump to {nextMonthName} <ChevronRight size={14} />
            </button>
          </div>
        );
      })()}

      {dayDetailDate && (
        <div className="bg-card/80 border border-border rounded-xl p-4 space-y-3" data-testid="day-detail-panel">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold text-foreground">
              {dayDetailDate.toLocaleDateString("default", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
            </h3>
            <button
              onClick={() => setDayDetailDate(null)}
              className="p-1 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
              data-testid="button-close-day-detail"
            >
              <X size={14} />
            </button>
          </div>
          {dayDetailItems.length === 0 ? (
            <p className="text-xs text-muted-foreground/50 py-4 text-center">No content scheduled for this day.</p>
          ) : (
            <div className="space-y-2">
              {dayDetailItems.sort((a, b) => new Date(a.scheduledAt!).getTime() - new Date(b.scheduledAt!).getTime()).map((item: ContentItem) => (
                <div
                  key={item.id}
                  className="flex items-center gap-3 p-3 bg-muted/30 border border-border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors"
                  data-testid={`day-detail-item-${item.id}`}
                  onClick={() => setEditItem(item)}
                >
                  <div className={`w-2.5 h-2.5 rounded-full ${platformColor(item.platform)} shrink-0`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] font-bold text-blue-400 uppercase">{item.platform}</span>
                      <span className="text-[10px] text-muted-foreground/70">{item.type}</span>
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${approvalBadgeStyle(item.approvalStatus)}`}>
                        {approvalLabel(item.approvalStatus)}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">{item.caption?.slice(0, 120) || "No caption"}</p>
                  </div>
                  <span className="text-[10px] text-muted-foreground/70 shrink-0">
                    {new Date(item.scheduledAt!).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </span>
                  {item.mediaUrl && activeWorkspaceId && (
                    <button
                      data-testid={`button-cal-stamp-${item.id}`}
                      onClick={(e) => handleCalendarStamp(e, item)}
                      disabled={stampingItemId === item.id}
                      title="Apply Brand Stamp"
                      className="p-1.5 rounded-md bg-muted/60 border border-border hover:border-amber-500/50 hover:text-amber-400 text-muted-foreground transition-colors shrink-0"
                    >
                      {stampingItemId === item.id ? <Loader2 size={10} className="animate-spin" /> : <Stamp size={10} />}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {loadingContent && <div className="text-center text-muted-foreground/50 text-sm py-8">Loading content...</div>}

      {/* Unscheduled Drafts — posts Peggy wrote but haven't been given a date yet */}
      {savedContent && (() => {
        const unscheduled = savedContent.filter((i: ContentItem) =>
          !i.scheduledAt &&
          (i.status === "DRAFT" || i.status === "draft") &&
          i.platform !== "blog" && i.platform !== "WordPress"
        );
        if (unscheduled.length === 0) return null;
        return (
          <div className="space-y-2 mb-4" data-testid="calendar-unscheduled-section">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold text-muted-foreground flex items-center gap-1.5">
                <Clock size={12} className="text-yellow-400" />
                Unscheduled Drafts
                <span className="ml-1 px-1.5 py-0.5 rounded-full bg-yellow-500/15 text-yellow-400 text-[10px] font-bold">{unscheduled.length}</span>
              </h3>
              <button
                data-testid="button-calendar-schedule-all"
                onClick={async () => {
                  setApprovingAll(true);
                  try {
                    await Promise.all(unscheduled.map((item: ContentItem, idx: number) =>
                      apiRequest("PATCH", `/api/content/${item.id}`, {
                        status: "SCHEDULED",
                        scheduledAt: new Date(Date.now() + (idx + 1) * 3 * 60 * 60 * 1000).toISOString(),
                      })
                    ));
                    queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "content"] });
                    toast({ title: `${unscheduled.length} post${unscheduled.length !== 1 ? "s" : ""} scheduled` });
                  } catch { toast({ title: "Failed to schedule", variant: "destructive" }); }
                  finally { setApprovingAll(false); }
                }}
                disabled={approvingAll}
                className="flex items-center gap-1 px-2.5 py-1 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/25 text-emerald-400 text-[10px] font-bold rounded-lg transition-all disabled:opacity-50"
              >
                {approvingAll ? <Loader2 size={10} className="animate-spin" /> : <CheckCircle2 size={10} />}
                Schedule all
              </button>
            </div>
            <div className="space-y-1 max-h-64 overflow-y-auto custom-scrollbar">
              {unscheduled.slice(0, 30).map((item: ContentItem, idx: number) => (
                <UnscheduledDraftRow
                  key={item.id}
                  item={item}
                  workspaceId={activeWorkspaceId!}
                />
              ))}
              {unscheduled.length > 30 && (
                <p className="text-[10px] text-muted-foreground/40 text-center py-1">
                  +{unscheduled.length - 30} more — open the Posts tab to see all
                </p>
              )}
            </div>
          </div>
        );
      })()}

      {savedContent && (
        <div className="space-y-2">
          <h3 className="text-sm font-bold text-muted-foreground">Scheduled Items</h3>
          {savedContent.filter((i: ContentItem) => i.scheduledAt).length === 0 ? (
            (() => {
              const pendingItems = savedContent.filter((i: ContentItem) => i.approvalStatus === "pending_approval" || i.approvalStatus === "pending");
              return pendingItems.length > 0 ? (
                <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl space-y-2" data-testid="calendar-pending-notice">
                  <p className="text-sm font-bold text-yellow-400">Your posts are waiting for review</p>
                  <p className="text-xs text-muted-foreground/70">
                    {pendingItems.length} post{pendingItems.length !== 1 ? "s are" : " is"} in your approval queue — approve them all now or review one by one.
                  </p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      data-testid="button-calendar-approve-all"
                      onClick={handleCalendarApproveAll}
                      disabled={approvingAll}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600/20 hover:bg-emerald-600/30 border border-emerald-500/30 text-emerald-400 text-xs font-bold rounded-lg transition-all disabled:opacity-50"
                    >
                      {approvingAll ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
                      Approve all ({pendingItems.length})
                    </button>
                    <button
                      data-testid="button-calendar-go-to-playbook"
                      onClick={() => {
                        const playbookTabBtn = document.querySelector('[data-testid="tab-playbook"]') as HTMLButtonElement;
                        if (playbookTabBtn) playbookTabBtn.click();
                      }}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-yellow-500/20 hover:bg-yellow-500/30 border border-yellow-500/30 text-yellow-400 text-xs font-bold rounded-lg transition-all"
                    >
                      Review one by one
                    </button>
                  </div>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground/50">No scheduled content yet.</p>
              );
            })()
          ) : (
            <div className="space-y-2 max-h-[30vh] overflow-y-auto custom-scrollbar">
              {savedContent.filter((i: ContentItem) => i.scheduledAt).sort((a: ContentItem, b: ContentItem) => new Date(a.scheduledAt!).getTime() - new Date(b.scheduledAt!).getTime()).map((item: ContentItem) => (
                <div
                  key={item.id}
                  className="flex items-center gap-3 p-3 bg-muted/30 border border-border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors"
                  data-testid={`scheduled-item-${item.id}`}
                  onClick={() => setEditItem(item)}
                >
                  <div className={`w-2 h-2 rounded-full ${platformColor(item.platform)} shrink-0`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] font-bold text-blue-400 uppercase">{item.platform}</span>
                      <span className="text-[10px] text-muted-foreground/70">{item.type}</span>
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${approvalBadgeStyle(item.approvalStatus)}`}>
                        {approvalLabel(item.approvalStatus)}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground truncate">{item.caption}</p>
                  </div>
                  <span className="text-[10px] text-muted-foreground/70 shrink-0">
                    {new Date(item.scheduledAt!).toLocaleDateString()} {new Date(item.scheduledAt!).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </span>
                  {item.mediaUrl && activeWorkspaceId && (
                    <button
                      data-testid={`button-sched-stamp-${item.id}`}
                      onClick={(e) => handleCalendarStamp(e, item)}
                      disabled={stampingItemId === item.id}
                      title="Apply Brand Stamp"
                      className="p-1.5 rounded-md bg-muted/60 border border-border hover:border-amber-500/50 hover:text-amber-400 text-muted-foreground transition-colors shrink-0"
                    >
                      {stampingItemId === item.id ? <Loader2 size={10} className="animate-spin" /> : <Stamp size={10} />}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {editItem && <ContentEditModal item={editItem} onClose={() => setEditItem(null)} />}
    </div>
  );
}

const CREATE_SUB_TABS = [
  { id: "post", label: "Post", icon: PenTool },
  { id: "carousel", label: "Carousel", icon: LayoutGrid },
  { id: "stories", label: "Stories", icon: Film },
  { id: "ads", label: "Ad Creative", icon: Megaphone },
  { id: "captions", label: "Captions", icon: Type },
  { id: "hashtags", label: "Hashtags", icon: Hash },
  { id: "ecommerce", label: "E-Commerce", icon: ShoppingBag },
  { id: "repurpose", label: "Repurpose", icon: Repeat2 },
  { id: "meme", label: "Meme", icon: Smile },
] as const;

type CreateSubTabId = (typeof CREATE_SUB_TABS)[number]["id"];

function CreateTab({ initialMediaUrl, autoGenerateCaption, onCaptionConsumed }: { initialMediaUrl?: string; autoGenerateCaption?: boolean; onCaptionConsumed?: () => void } = {}) {
  const [subTab, setSubTab] = useState<CreateSubTabId>("post");

  return (
    <div className="space-y-4">
      <div className="flex gap-1 bg-muted/30 border border-border rounded-lg p-1 overflow-x-auto custom-scrollbar" data-testid="create-subtabs">
        {CREATE_SUB_TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              data-testid={`subtab-${t.id}`}
              onClick={() => setSubTab(t.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap transition-all ${
                subTab === t.id
                  ? "bg-muted text-foreground"
                  : "text-muted-foreground/70 hover:text-muted-foreground"
              }`}
            >
              <Icon size={12} />
              {t.label}
            </button>
          );
        })}
      </div>
      {subTab === "post" && <CreatePostTab initialMediaUrl={initialMediaUrl} autoGenerateCaption={autoGenerateCaption} onCaptionConsumed={onCaptionConsumed} />}
      {subTab === "carousel" && <CarouselTab />}
      {subTab === "stories" && <StoriesTab />}
      {subTab === "ads" && <AdCreativeTab />}
      {subTab === "captions" && <CaptionsTab />}
      {subTab === "hashtags" && <HashtagsTab />}
      {subTab === "ecommerce" && <EcommerceTab />}
      {subTab === "repurpose" && <RepurposeTab />}
      {subTab === "meme" && <MemeTab />}
    </div>
  );
}

const CATEGORY_COLORS: Record<string, string> = {
  educational: "border-blue-500/60 bg-blue-500/5",
  promotional: "border-orange-500/60 bg-orange-500/5",
  testimonial: "border-green-500/60 bg-green-500/5",
  behind_scenes: "border-purple-500/60 bg-purple-500/5",
  engagement: "border-teal-500/60 bg-teal-500/5",
};
const CATEGORY_TEXT: Record<string, string> = {
  educational: "text-blue-400",
  promotional: "text-orange-400",
  testimonial: "text-green-400",
  behind_scenes: "text-purple-400",
  engagement: "text-teal-400",
};
const CATEGORY_BAR: Record<string, string> = {
  educational: "bg-blue-500",
  promotional: "bg-orange-500",
  testimonial: "bg-green-500",
  behind_scenes: "bg-purple-500",
  engagement: "bg-teal-500",
};
const PLAYBOOK_INDUSTRIES_CS = [
  "Accounting / CPA", "Dental", "Legal", "Real Estate", "Restaurant",
  "Retail / Boutique", "Bakery", "Fitness / Gym", "Salon / Spa",
  "Construction / Trades", "Tech / SaaS", "Consulting", "Healthcare / Clinic",
  "Auto / Dealership", "E-commerce",
];

function PlaybookTab() {
  const { activeWorkspaceId } = useWorkspaceStore();
  const { toast } = useToast();
  const [selectedDay, setSelectedDay] = useState<number | null>(null);
  const [editEntry, setEditEntry] = useState<any>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [showGenerateForm, setShowGenerateForm] = useState(false);
  const [genIndustry, setGenIndustry] = useState("");
  const [genFreq, setGenFreq] = useState("3-5x/week");
  const [genGoals, setGenGoals] = useState<string[]>(["Grow brand awareness", "Generate leads"]);
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [templateIndustry, setTemplateIndustry] = useState("");
  const [approvingAll, setApprovingAll] = useState(false);
  const [togglingAutoApprove, setTogglingAutoApprove] = useState(false);
  const [showAutoApproveDialog, setShowAutoApproveDialog] = useState(false);

  const { data: playbook, isLoading, refetch } = useQuery<any>({
    queryKey: ["/api/workspaces", activeWorkspaceId, "playbook"],
    enabled: !!activeWorkspaceId,
    refetchOnMount: "always",
    staleTime: 0,
    queryFn: async () => {
      const res = await fetch(`/api/workspaces/${activeWorkspaceId}/playbook`, { credentials: "include" });
      if (!res.ok) return null;
      return res.json();
    },
  });

  const { data: scheduleConfig, refetch: refetchScheduleConfig } = useQuery<any>({
    queryKey: ["/api/workspaces", activeWorkspaceId, "schedule-config"],
    enabled: !!activeWorkspaceId,
    queryFn: async () => {
      const res = await fetch(`/api/workspaces/${activeWorkspaceId}/schedule-config`, { credentials: "include" });
      if (!res.ok) return null;
      return res.json();
    },
  });

  const { data: savedContentForPlaybook } = useContent();
  const pendingCount = (savedContentForPlaybook || []).filter((i: any) => i.approvalStatus === "pending_approval" || i.approvalStatus === "pending").length;

  const handleApproveAll = async () => {
    setApprovingAll(true);
    try {
      const res = await apiRequest("POST", `/api/workspaces/${activeWorkspaceId}/content/approve-all-pending`, {});
      const result = await res.json();
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "content"] });
      toast({ title: "Done", description: `${result.approved} post${result.approved !== 1 ? "s" : ""} approved and scheduled.` });
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally {
      setApprovingAll(false);
    }
  };

  const performToggleAutoApprove = async (newValue: boolean) => {
    setTogglingAutoApprove(true);
    try {
      await apiRequest("PATCH", `/api/workspaces/${activeWorkspaceId}/schedule-config`, {
        autoApprovePeggyContent: newValue,
        schedule: scheduleConfig?.agentSchedule || "manual",
        autoPublish: scheduleConfig?.autoPublish ?? false,
      });
      let approvedCount = 0;
      if (newValue) {
        try {
          const approveRes = await apiRequest("POST", `/api/workspaces/${activeWorkspaceId}/content/approve-all-pending`, {});
          const approveResult = await approveRes.json();
          approvedCount = approveResult.approved ?? 0;
        } catch {}
      }
      refetchScheduleConfig();
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "content"] });
      toast({
        title: newValue ? "Auto-approve enabled" : "Auto-approve disabled",
        description: newValue
          ? approvedCount > 0
            ? `Peggy's posts will now publish automatically. ${approvedCount} pending post${approvedCount !== 1 ? "s" : ""} approved and scheduled.`
            : "Peggy's posts will be automatically approved and scheduled."
          : "Posts will need manual approval before publishing.",
      });
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally {
      setTogglingAutoApprove(false);
    }
  };

  const handleToggleAutoApprove = () => {
    const newValue = !(scheduleConfig?.autoApprovePeggyContent ?? false);
    if (newValue) {
      const seenKey = `auto-approve-seen-${activeWorkspaceId}`;
      const alreadySeen = localStorage.getItem(seenKey);
      if (!alreadySeen) {
        setShowAutoApproveDialog(true);
        return;
      }
    }
    performToggleAutoApprove(newValue);
  };

  const saveMutation = useMutation({
    mutationFn: async (updatedCalendar: any[]) => {
      const updated = { ...playbook, contentCalendar: updatedCalendar };
      const res = await apiRequest("PUT", `/api/workspaces/${activeWorkspaceId}/playbook`, updated);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "playbook"] });
      setSelectedDay(null);
      toast({ title: "Saved", description: "Playbook updated." });
    },
  });

  const handleRefresh = async () => {
    if (!playbook) return;
    setGenerating(true);
    try {
      const res = await apiRequest("POST", `/api/workspaces/${activeWorkspaceId}/playbook/generate`, {
        industry: playbook.industry,
        location: playbook.location,
        goals: playbook.goals,
        postingFrequency: playbook.postingFrequency,
      });
      await res.json();
      refetch();
      toast({ title: "Playbook refreshed", description: "New 30-day plan generated." });
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerate = async () => {
    if (!genIndustry) {
      toast({ title: "Pick an industry", variant: "destructive" });
      return;
    }
    setGenerating(true);
    try {
      const res = await apiRequest("POST", `/api/workspaces/${activeWorkspaceId}/playbook/generate`, {
        industry: genIndustry,
        postingFrequency: genFreq,
        goals: genGoals,
      });
      await res.json();
      refetch();
      setShowGenerateForm(false);
      toast({ title: "Playbook generated!", description: "Your 30-day plan is ready." });
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally {
      setGenerating(false);
    }
  };

  const handleUseTemplate = async () => {
    if (!templateIndustry) {
      toast({ title: "Pick an industry", variant: "destructive" });
      return;
    }
    setGenerating(true);
    try {
      const res = await apiRequest("POST", `/api/workspaces/${activeWorkspaceId}/playbook/use-template`, { industry: templateIndustry });
      await res.json();
      refetch();
      setShowTemplateForm(false);
      toast({ title: "Template loaded!", description: `30-day ${templateIndustry} playbook applied.` });
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally {
      setGenerating(false);
    }
  };

  const handleSaveEntry = () => {
    if (!editEntry || !playbook) return;
    const updated = (playbook.contentCalendar as any[]).map((e: any) =>
      e.day === editEntry.day ? editEntry : e
    );
    saveMutation.mutate(updated);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="animate-spin text-muted-foreground/40" size={24} />
      </div>
    );
  }

  if (!playbook) {
    return (
      <div className="space-y-6" data-testid="playbook-empty-state">
        <div className="text-center py-10 space-y-4">
          <div className="w-16 h-16 rounded-2xl bg-blue-600/10 border border-blue-500/20 flex items-center justify-center mx-auto">
            <BookOpen size={24} className="text-blue-400" />
          </div>
          <div>
            <h3 className="text-lg font-display font-bold text-foreground mb-2">No Playbook Yet</h3>
            <p className="text-muted-foreground/70 text-sm max-w-sm mx-auto">Set up a 30-day content playbook to automate daily posting without Draper. Choose an industry template (free) or generate a custom one with AI.</p>
          </div>
          <div className="flex justify-center gap-3 flex-wrap">
            <button data-testid="button-cs-use-template" onClick={() => { setShowTemplateForm(true); setShowGenerateForm(false); }} className="flex items-center gap-2 px-5 py-2.5 bg-emerald-600/20 hover:bg-emerald-600/30 border border-emerald-500/30 text-emerald-400 text-sm font-bold rounded-xl transition-all">
              <BookOpen size={16} />
              Use Industry Template
            </button>
            <button data-testid="button-cs-generate-playbook" onClick={() => { setShowGenerateForm(true); setShowTemplateForm(false); }} className="flex items-center gap-2 px-5 py-2.5 bg-blue-600/20 hover:bg-blue-600/30 border border-blue-500/30 text-blue-400 text-sm font-bold rounded-xl transition-all">
              <Sparkles size={16} />
              Generate Custom Playbook
            </button>
          </div>
        </div>

        {showTemplateForm && (
          <div className="border border-border rounded-xl p-5 space-y-4" data-testid="template-form">
            <h4 className="text-sm font-bold text-foreground">Select Industry Template</h4>
            <div className="flex flex-wrap gap-2">
              {PLAYBOOK_INDUSTRIES_CS.map(ind => (
                <button key={ind} onClick={() => setTemplateIndustry(ind)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${templateIndustry === ind ? "bg-emerald-600/20 text-emerald-400 border border-emerald-500/30" : "bg-muted/50 text-muted-foreground border border-border"}`}>{ind}</button>
              ))}
            </div>
            <div className="flex gap-2">
              <button onClick={handleUseTemplate} disabled={generating || !templateIndustry} data-testid="button-apply-template" className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-bold rounded-xl disabled:opacity-50 transition-all">
                {generating ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                Apply Template
              </button>
              <button onClick={() => setShowTemplateForm(false)} className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors">Cancel</button>
            </div>
          </div>
        )}

        {showGenerateForm && (
          <div className="border border-border rounded-xl p-5 space-y-4" data-testid="generate-form">
            <h4 className="text-sm font-bold text-foreground">Generate Custom Playbook</h4>
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-widest font-bold block mb-2">Industry</label>
              <div className="flex flex-wrap gap-2">
                {PLAYBOOK_INDUSTRIES_CS.map(ind => (
                  <button key={ind} onClick={() => setGenIndustry(ind)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${genIndustry === ind ? "bg-blue-600/20 text-blue-400 border border-blue-500/30" : "bg-muted/50 text-muted-foreground border border-border"}`}>{ind}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-widest font-bold block mb-2">Posting Frequency</label>
              <div className="flex gap-2">
                {["Daily", "3-5x/week", "1-2x/week"].map(f => (
                  <button key={f} onClick={() => setGenFreq(f)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${genFreq === f ? "bg-blue-600/20 text-blue-400 border border-blue-500/30" : "bg-muted/50 text-muted-foreground border border-border"}`}>{f}</button>
                ))}
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={handleGenerate} disabled={generating || !genIndustry} data-testid="button-run-generate" className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-bold rounded-xl disabled:opacity-50 transition-all">
                {generating ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                {generating ? "Generating..." : "Generate (~$0.01)"}
              </button>
              <button onClick={() => setShowGenerateForm(false)} className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors">Cancel</button>
            </div>
          </div>
        )}
      </div>
    );
  }

  const calendar = (playbook.contentCalendar as any[]) || [];
  const adTemplates = (playbook.adTemplates as any[]) || [];
  const postTypeMix = (playbook.postTypeMix as Record<string, number>) || {};
  const channelStrategies = (playbook.channelStrategies as Record<string, any>) || {};
  const needsRefresh = playbook.needsRefresh;

  return (
    <div className="space-y-5" data-testid="playbook-tab">
      {/* First-time Auto-approve confirmation dialog */}
      {showAutoApproveDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={(e) => { if (e.target === e.currentTarget) setShowAutoApproveDialog(false); }}>
          <div className="bg-card border border-border rounded-2xl shadow-2xl p-6 max-w-md w-full mx-4 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-600/20 border border-emerald-500/30 flex items-center justify-center">
                <Zap size={18} className="text-emerald-400" />
              </div>
              <div>
                <h3 className="text-base font-display font-bold text-foreground">Enable Auto-Approve?</h3>
                <p className="text-xs text-muted-foreground/70">This change affects how Peggy publishes content</p>
              </div>
            </div>
            <div className="space-y-3 text-sm text-muted-foreground/80">
              <p>When <span className="font-semibold text-foreground">Auto-Approve is ON</span>, every post Peggy creates is automatically approved and queued for publishing — no review needed.</p>
              <p>When <span className="font-semibold text-foreground">Auto-Approve is OFF</span>, posts sit in the approval queue until you review and approve each one manually. This gives you full editorial control.</p>
              {pendingCount > 0 && (
                <p className="text-yellow-400/90 font-medium">You currently have {pendingCount} post{pendingCount !== 1 ? "s" : ""} waiting for approval — enabling this will approve and schedule them all right now.</p>
              )}
            </div>
            <div className="flex justify-end gap-3 pt-1">
              <button
                data-testid="button-auto-approve-cancel"
                onClick={() => setShowAutoApproveDialog(false)}
                className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground border border-border rounded-xl transition-colors"
              >
                Keep Manual Review
              </button>
              <button
                data-testid="button-auto-approve-confirm"
                onClick={() => {
                  setShowAutoApproveDialog(false);
                  localStorage.setItem(`auto-approve-seen-${activeWorkspaceId}`, "1");
                  performToggleAutoApprove(true);
                }}
                className="px-4 py-2 text-sm font-bold bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl transition-colors"
              >
                Enable Auto-Approve
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Stale banner */}
      {needsRefresh && (
        <div className="flex items-center justify-between gap-3 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl" data-testid="playbook-stale-banner">
          <div className="flex items-center gap-3">
            <AlertTriangle size={16} className="text-yellow-400 shrink-0" />
            <div>
              <p className="text-sm font-bold text-yellow-400">Your marketing strategy was updated by Draper</p>
              <p className="text-xs text-muted-foreground/70">Refresh your playbook to align content with the new direction.</p>
            </div>
          </div>
          <button onClick={handleRefresh} disabled={generating} data-testid="button-refresh-playbook" className="flex items-center gap-2 px-4 py-2 bg-yellow-500/20 hover:bg-yellow-500/30 border border-yellow-500/30 text-yellow-400 text-xs font-bold rounded-lg transition-all whitespace-nowrap">
            {generating ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
            Refresh — ~$0.01
          </button>
        </div>
      )}

      {/* Approval controls */}
      <div className="flex items-center justify-between gap-3 p-4 bg-card/50 border border-border rounded-xl" data-testid="playbook-approval-controls">
        <div className="space-y-1 flex-1">
          <p className="text-sm font-bold text-foreground">Playbook posts require approval before they publish.</p>
          <p className="text-xs text-muted-foreground/70">Enable auto-approve to let Peggy schedule posts automatically, or approve them one by one from the Calendar tab.</p>
        </div>
        <div className="flex items-center gap-3 shrink-0 flex-wrap justify-end">
          {pendingCount > 0 && (
            <button
              data-testid="button-approve-all-pending"
              onClick={handleApproveAll}
              disabled={approvingAll}
              className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600/20 hover:bg-emerald-600/30 border border-emerald-500/30 text-emerald-400 text-xs font-bold rounded-lg transition-all disabled:opacity-50"
            >
              {approvingAll ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
              Approve all ({pendingCount})
            </button>
          )}
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground/70 whitespace-nowrap">Auto-approve</span>
            <button
              data-testid="toggle-auto-approve-peggy"
              onClick={handleToggleAutoApprove}
              disabled={togglingAutoApprove}
              className={`relative w-11 h-6 rounded-full transition-colors shrink-0 disabled:opacity-50 ${scheduleConfig?.autoApprovePeggyContent ? "bg-emerald-600" : "bg-muted"}`}
            >
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${scheduleConfig?.autoApprovePeggyContent ? "translate-x-5" : "translate-x-0"}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Subtitle */}
      <p className="text-xs text-muted-foreground/70" data-testid="playbook-subtitle">
        Peggy follows this schedule automatically — content appears in the Library as she creates it.
        {playbook.createdAt && (() => {
          const daysSince = Math.floor((Date.now() - new Date(playbook.createdAt).getTime()) / 86400000);
          const currentDay = (daysSince % 30) + 1;
          const daysUntilRefresh = 30 - (daysSince % 30);
          return (
            <span className="ml-2 text-blue-400/70">
              Day {currentDay} of 30{daysUntilRefresh <= 5 ? ` · Refreshes in ${daysUntilRefresh} day${daysUntilRefresh !== 1 ? "s" : ""}` : ""}
              {playbook.lastRefreshedAt && ` · Last refreshed ${Math.floor((Date.now() - new Date(playbook.lastRefreshedAt).getTime()) / 86400000)}d ago`}
            </span>
          );
        })()}
      </p>

      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <span className="px-3 py-1 bg-blue-600/20 text-blue-400 border border-blue-500/20 rounded-full text-xs font-bold capitalize">{playbook.industry}</span>
          {playbook.location && <span className="text-xs text-muted-foreground/60">{playbook.location}</span>}
          <span className="text-xs text-muted-foreground/60">{playbook.postingFrequency}</span>
          {playbook.generatedBy === "template" && <span className="px-2 py-0.5 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 rounded text-xs">Template</span>}
        </div>
        <button onClick={handleRefresh} disabled={generating} data-testid="button-regen-playbook" className="flex items-center gap-2 px-4 py-2 bg-muted/30 hover:bg-muted/50 border border-border text-muted-foreground text-xs font-bold rounded-xl transition-all">
          {generating ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
          Regenerate
        </button>
      </div>

      {/* 30-day calendar grid */}
      <div className="grid grid-cols-5 gap-2" data-testid="playbook-calendar-grid">
        {calendar.slice(0, 30).map((entry: any) => {
          const catKey = entry.content_category || "educational";
          const borderCls = CATEGORY_COLORS[catKey] || "border-border bg-card/30";
          const textCls = CATEGORY_TEXT[catKey] || "text-muted-foreground";
          return (
            <button
              key={entry.day}
              data-testid={`playbook-day-${entry.day}`}
              onClick={() => { setSelectedDay(entry.day); setEditEntry({ ...entry }); }}
              className={`relative p-2.5 rounded-xl border text-left transition-all hover:scale-105 hover:shadow-lg group ${borderCls}`}
            >
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs font-bold text-muted-foreground/50">Day {entry.day}</span>
                <span className={`text-xs font-bold capitalize ${textCls}`} style={{ fontSize: "9px" }}>{catKey.replace("_", " ")}</span>
              </div>
              <div className="text-xs font-medium text-muted-foreground/40 capitalize mb-1">{entry.platform} · {entry.content_type}</div>
              <div className="text-xs text-foreground/70 leading-tight line-clamp-2">{entry.topic}</div>
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="bg-black/60 backdrop-blur-sm rounded-lg px-2 py-1">
                  <Edit3 size={12} className="text-white" />
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Day edit side panel */}
      {selectedDay !== null && editEntry && (
        <div className="border border-border rounded-xl p-5 space-y-4 bg-card/30" data-testid="playbook-day-edit-panel">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-bold text-foreground">Edit Day {editEntry.day}</h4>
            <button onClick={() => setSelectedDay(null)} className="text-muted-foreground/50 hover:text-foreground transition-colors"><X size={16} /></button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-widest font-bold block mb-1">Platform</label>
              <select value={editEntry.platform} onChange={e => setEditEntry((prev: any) => ({ ...prev, platform: e.target.value }))} className="w-full bg-muted/50 border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none">
                {["instagram", "facebook", "linkedin", "x", "tiktok"].map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-widest font-bold block mb-1">Content Type</label>
              <select value={editEntry.content_type} onChange={e => setEditEntry((prev: any) => ({ ...prev, content_type: e.target.value }))} className="w-full bg-muted/50 border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none">
                {["post", "reel", "story", "carousel", "text"].map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-widest font-bold block mb-1">Topic</label>
            <textarea rows={2} value={editEntry.topic} onChange={e => setEditEntry((prev: any) => ({ ...prev, topic: e.target.value }))} className="w-full bg-muted/50 border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none resize-none" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-widest font-bold block mb-1">Caption Theme</label>
            <input type="text" value={editEntry.caption_theme} onChange={e => setEditEntry((prev: any) => ({ ...prev, caption_theme: e.target.value }))} className="w-full bg-muted/50 border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-widest font-bold block mb-1">CTA</label>
            <input type="text" value={editEntry.cta} onChange={e => setEditEntry((prev: any) => ({ ...prev, cta: e.target.value }))} className="w-full bg-muted/50 border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-widest font-bold block mb-1">Category</label>
            <select value={editEntry.content_category} onChange={e => setEditEntry((prev: any) => ({ ...prev, content_category: e.target.value }))} className="w-full bg-muted/50 border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none">
              {["educational", "promotional", "testimonial", "behind_scenes", "engagement"].map(c => <option key={c} value={c}>{c.replace("_", " ")}</option>)}
            </select>
          </div>
          <div className="flex gap-2">
            <button onClick={handleSaveEntry} disabled={saveMutation.isPending} data-testid="button-save-day-entry" className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-bold rounded-xl disabled:opacity-50 transition-all">
              {saveMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Save Changes
            </button>
            <button onClick={() => setSelectedDay(null)} className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors">Close</button>
          </div>
        </div>
      )}

      {/* View Details toggle */}
      <div className="border border-border rounded-xl overflow-hidden">
        <button
          data-testid="button-toggle-playbook-details"
          onClick={() => setShowDetails(v => !v)}
          className="flex items-center justify-between w-full px-5 py-3 bg-muted/30 hover:bg-muted/50 transition-colors"
        >
          <span className="text-sm font-bold text-foreground/70">Post Mix · Ad Templates · Channel Tips</span>
          {showDetails ? <ChevronUp size={14} className="text-muted-foreground/50" /> : <ChevronDown size={14} className="text-muted-foreground/50" />}
        </button>

        {showDetails && (
          <div className="p-5 space-y-6" data-testid="playbook-details-panel">
            {/* Post Type Mix */}
            {Object.keys(postTypeMix).length > 0 && (
              <div>
                <h5 className="text-xs font-bold text-foreground/70 uppercase tracking-widest mb-3">Post Type Mix</h5>
                <div className="h-3 rounded-full overflow-hidden flex w-full mb-3">
                  {Object.entries(postTypeMix).map(([cat, pct]) => (
                    <div key={cat} className={CATEGORY_BAR[cat] || "bg-muted"} style={{ width: `${pct}%` }} title={`${cat}: ${pct}%`} />
                  ))}
                </div>
                <div className="flex flex-wrap gap-3">
                  {Object.entries(postTypeMix).map(([cat, pct]) => (
                    <div key={cat} className="flex items-center gap-1.5">
                      <div className={`w-2.5 h-2.5 rounded-sm ${CATEGORY_BAR[cat] || "bg-muted"}`} />
                      <span className="text-xs text-muted-foreground/70 capitalize">{cat.replace("_", " ")}</span>
                      <span className="text-xs font-bold text-foreground/70">{pct}%</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Ad Templates */}
            {adTemplates.length > 0 && (
              <div>
                <h5 className="text-xs font-bold text-foreground/70 uppercase tracking-widest mb-3">Ad Templates</h5>
                <div className="space-y-3">
                  {adTemplates.map((tmpl: any, i: number) => {
                    const hlLen = (tmpl.headline || "").length;
                    const descLen = (tmpl.description || "").length;
                    return (
                      <div key={i} className="border border-border rounded-xl p-4 space-y-2" data-testid={`ad-template-${i}`}>
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-bold text-foreground">{tmpl.offer_name}</span>
                          <span className="text-xs text-muted-foreground/50 capitalize">{tmpl.discount_type?.replace("_", " ")}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-foreground/70">{tmpl.headline}</span>
                          <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${hlLen > 40 ? "bg-red-500/20 text-red-400" : hlLen >= 35 ? "bg-yellow-500/20 text-yellow-400" : "bg-green-500/20 text-green-400"}`}>{hlLen}/40</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <span className="text-xs text-muted-foreground/70 flex-1">{tmpl.description}</span>
                          <span className={`text-xs font-bold px-1.5 py-0.5 rounded shrink-0 ${descLen > 125 ? "bg-red-500/20 text-red-400" : descLen >= 110 ? "bg-yellow-500/20 text-yellow-400" : "bg-green-500/20 text-green-400"}`}>{descLen}/125</span>
                        </div>
                        <div className="inline-block px-3 py-1 bg-blue-600/20 border border-blue-500/20 text-blue-400 text-xs font-bold rounded-lg">{tmpl.cta}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Channel Strategies */}
            {Object.keys(channelStrategies).length > 0 && (
              <div>
                <h5 className="text-xs font-bold text-foreground/70 uppercase tracking-widest mb-3">Channel Strategies</h5>
                <div className="space-y-3">
                  {Object.entries(channelStrategies).map(([platform, strat]: [string, any]) => (
                    <div key={platform} className="border border-border rounded-xl p-4">
                      <h6 className="text-sm font-bold text-foreground capitalize mb-2">{platform}</h6>
                      <p className="text-xs text-muted-foreground/70 mb-2">{strat.strategy}</p>
                      <p className="text-xs text-blue-400/70 mb-2">Tone: {strat.tone}</p>
                      {strat.tips && (
                        <ul className="space-y-1">
                          {(strat.tips as string[]).map((tip, i) => (
                            <li key={i} className="text-xs text-muted-foreground/60 flex items-start gap-2">
                              <span className="text-blue-400/50 mt-0.5">•</span>
                              {tip}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

type PreviewFormat = "ig_reel" | "ig_feed" | "ig_story" | "fb_feed" | "x_post" | "li_post" | "google_ad";

function mapPlatformToFormat(platform: string): PreviewFormat {
  const p = platform.toLowerCase();
  if (p === "instagram") return "ig_feed";
  if (p === "facebook" || p === "meta") return "fb_feed";
  if (p === "x" || p === "twitter") return "x_post";
  if (p === "linkedin") return "li_post";
  if (p === "google") return "google_ad";
  return "ig_feed";
}

function getFormatTabsForPlatform(platform: string): { key: PreviewFormat; label: string }[] {
  const p = platform.toLowerCase();
  if (p === "instagram") return [
    { key: "ig_feed", label: "Feed" },
    { key: "ig_reel", label: "Reel" },
    { key: "ig_story", label: "Story" },
  ];
  if (p === "facebook" || p === "meta") return [{ key: "fb_feed", label: "Feed" }];
  if (p === "x" || p === "twitter") return [{ key: "x_post", label: "Post" }];
  if (p === "linkedin") return [{ key: "li_post", label: "Post" }];
  if (p === "google") return [{ key: "google_ad", label: "Google Ad" }];
  return [
    { key: "ig_feed", label: "IG Feed" },
    { key: "fb_feed", label: "FB Feed" },
    { key: "x_post", label: "X Post" },
    { key: "li_post", label: "LinkedIn" },
  ];
}

function GoogleAdPreview({ caption, url }: { caption: string; url?: string }) {
  const headline = caption.split("\n")[0]?.slice(0, 60) || "Your Ad Headline";
  const description = caption.split("\n").slice(1).join(" ").slice(0, 90) || caption.slice(0, 90) || "Your ad description appears here.";
  const displayUrl = url || "example.com";
  return (
    <div className="bg-white rounded-xl p-4 text-left shadow-sm border border-gray-200" data-testid="google-ad-preview">
      <div className="flex items-center gap-1.5 mb-1">
        <span className="text-[10px] font-bold text-yellow-700 bg-yellow-100 px-1.5 py-0.5 rounded">Ad</span>
        <span className="text-xs text-green-700 truncate">{displayUrl}</span>
      </div>
      <h4 className="text-sm font-medium text-blue-700 leading-snug mb-1 line-clamp-2">{headline}</h4>
      <p className="text-xs text-gray-600 leading-relaxed line-clamp-2">{description}</p>
    </div>
  );
}

function substituteCityToken(text: string, city?: string): string {
  if (!city) return text;
  return text.replace(/\[city\]/gi, city);
}

function CarouselSlidesPanel({ caption, onCaptionChange }: { caption: string; onCaptionChange?: (newCaption: string) => void }) {
  const slides = caption.split(/\n---\n/g).filter(s => s.trim());

  const handleSlideChange = (index: number, newText: string) => {
    if (!onCaptionChange) return;
    const allSlides = caption.split(/\n---\n/g);
    allSlides[index] = newText;
    onCaptionChange(allSlides.join("\n---\n"));
  };

  if (slides.length <= 1) {
    return (
      <div className="space-y-2" data-testid="carousel-slides-panel">
        <div className="flex items-center gap-2">
          <LayoutGrid size={12} className="text-purple-400" />
          <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Carousel Slides</span>
        </div>
        <div className="bg-card/50 border border-border rounded-lg p-3">
          <div className="flex items-center gap-2 mb-1.5">
            <div className="w-5 h-5 rounded bg-purple-500/20 flex items-center justify-center">
              <span className="text-[9px] font-bold text-purple-400">1</span>
            </div>
            <span className="text-[10px] text-muted-foreground/70">Single slide</span>
          </div>
          <textarea
            data-testid="input-carousel-slide-0"
            value={slides[0] || caption}
            onChange={(e) => handleSlideChange(0, e.target.value)}
            readOnly={!onCaptionChange}
            rows={3}
            className="w-full bg-muted/30 border border-border focus:border-purple-500 outline-none rounded-lg px-2 py-1.5 text-xs text-foreground/70 resize-none"
          />
          <div className="mt-2 h-16 rounded bg-muted/30 border border-dashed border-border flex items-center justify-center">
            <span className="text-[9px] text-muted-foreground/40">Image placeholder</span>
          </div>
        </div>
        <p className="text-[9px] text-muted-foreground/40">Separate slides with --- on its own line</p>
      </div>
    );
  }
  return (
    <div className="space-y-2" data-testid="carousel-slides-panel">
      <div className="flex items-center gap-2">
        <LayoutGrid size={12} className="text-purple-400" />
        <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Carousel Slides ({slides.length})</span>
      </div>
      <div className="space-y-2 max-h-48 overflow-y-auto custom-scrollbar">
        {slides.map((slide, i) => (
          <div key={i} className="bg-card/50 border border-border rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1.5">
              <div className="w-5 h-5 rounded bg-purple-500/20 flex items-center justify-center">
                <span className="text-[9px] font-bold text-purple-400">{i + 1}</span>
              </div>
              <span className="text-[10px] text-muted-foreground/70">Slide {i + 1}</span>
            </div>
            <textarea
              data-testid={`input-carousel-slide-${i}`}
              value={slide.trim()}
              onChange={(e) => handleSlideChange(i, e.target.value)}
              readOnly={!onCaptionChange}
              rows={2}
              className="w-full bg-muted/30 border border-border focus:border-purple-500 outline-none rounded-lg px-2 py-1.5 text-xs text-foreground/70 resize-none"
            />
            <div className="mt-2 h-12 rounded bg-muted/30 border border-dashed border-border flex items-center justify-center">
              <span className="text-[9px] text-muted-foreground/40">Image placeholder</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ContentEditModal({ item, onClose }: { item: ContentItem; onClose: () => void }) {
  const { activeWorkspaceId } = useWorkspaceStore();
  const updateContent = useUpdateContent();
  const approveContent = useApproveContent();
  const rejectContent = useRejectContent();
  const { toast } = useToast();
  const [isPublishing, setIsPublishing] = useState(false);
  const [isStamping, setIsStamping] = useState(false);

  useEffect(() => {
    function handleEscape(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  const [caption, setCaption] = useState(item.caption);
  const [platform, setPlatform] = useState(item.platform);
  const [contentType, setContentType] = useState(item.type);
  const [status, setStatus] = useState(item.status);
  const [mediaUrl, setMediaUrl] = useState(item.mediaUrl || "");
  const [previewFormat, setPreviewFormat] = useState<PreviewFormat>(mapPlatformToFormat(item.platform));

  const { data: workspace } = useQuery<any>({
    queryKey: ["/api/workspaces", activeWorkspaceId],
    enabled: !!activeWorkspaceId,
    queryFn: async () => {
      const res = await fetch(`/api/workspaces/${activeWorkspaceId}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch workspace");
      return res.json();
    },
  });

  const workspaceCity = workspace?.contactInfo?.address?.split(",")?.slice(-2, -1)?.[0]?.trim() || workspace?.location || "";

  const existingDate = item.scheduledAt ? new Date(item.scheduledAt) : null;
  const [schedDate, setSchedDate] = useState(existingDate ? existingDate.toISOString().split("T")[0] : "");
  const [schedTime, setSchedTime] = useState(existingDate ? existingDate.toTimeString().slice(0, 5) : "09:00");

  const today = new Date().toISOString().split("T")[0];

  const regenerateMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/ai/content", {
        platform,
        strategy: caption.slice(0, 200) || "Create engaging social media content",
        context: `Regenerate and improve this content: ${caption.slice(0, 500)}`,
        workspaceId: activeWorkspaceId,
      });
      return res.json();
    },
    onSuccess: (data: any) => {
      if (data.caption) setCaption(data.caption);
      toast({ title: "Regenerated", description: "Content regenerated with AI." });
    },
    onError: () => toast({ title: "Error", description: "Failed to regenerate content.", variant: "destructive" }),
  });

  const regenerateImageMutation = useMutation({
    mutationFn: async () => {
      const prompt = `Professional marketing image for ${platform}: ${caption.slice(0, 200)}`;
      const res = await apiRequest("POST", "/api/ai/image", { prompt, workspaceId: activeWorkspaceId });
      return res.json();
    },
    onSuccess: (data: any) => {
      if (data.imageUrl) {
        setMediaUrl(data.imageUrl);
        toast({ title: "Image Generated", description: "New image generated." });
      }
    },
    onError: () => toast({ title: "Error", description: "Failed to generate image.", variant: "destructive" }),
  });

  const handleSave = () => {
    const updates: any = { id: item.id, caption, platform, type: contentType, status };
    if (mediaUrl !== (item.mediaUrl || "")) updates.mediaUrl = mediaUrl || null;
    if (schedDate) {
      updates.scheduledAt = new Date(`${schedDate}T${schedTime}:00`).toISOString();
      if (status === "DRAFT") updates.status = "SCHEDULED";
    }
    updateContent.mutate(updates, {
      onSuccess: () => {
        if (updates.status === "PUBLISHED") {
          toast({ title: "Published", description: "Content has been published successfully." });
        } else if (updates.status === "SCHEDULED" || schedDate) {
          toast({ title: "Scheduled", description: `Content scheduled for ${schedDate} at ${schedTime}.` });
        } else {
          toast({ title: "Updated", description: "Content updated successfully." });
        }
        onClose();
      },
      onError: () => toast({ title: "Error", description: "Failed to update content.", variant: "destructive" }),
    });
  };

  const [publishTarget, setPublishTarget] = useState("");

  const handlePublishNow = async (targetPlatform?: string) => {
    setIsPublishing(true);
    const target = targetPlatform || platform.toLowerCase();
    setPublishTarget(target);
    try {
      const res = await fetch(`/api/workspaces/${item.workspaceId}/content/${item.id}/publish-now`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ platform: target }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.blocked) {
          // refused before anything was sent (test mode): the post stays approved, it did not fail
          toast({ title: "Not posted", description: data.message || data.error || "Test mode: not posted." });
        } else if (data.code === "NEEDS_IMAGE") {
          toast({ title: "Image Required", description: "Instagram requires an image. Generate one first using the Generate Image button.", variant: "destructive" });
        } else {
          toast({ title: "Publish Failed", description: data.error || data.message || "Failed to publish", variant: "destructive" });
        }
      } else {
        toast({ title: "Published!", description: `Content posted to ${target} successfully.` });
        queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "content"] });
        onClose();
      }
    } catch (e: any) {
      toast({ title: "Error", description: e.message || "Publish failed", variant: "destructive" });
    } finally {
      setIsPublishing(false);
      setPublishTarget("");
    }
  };

  const handleApprove = () => {
    approveContent.mutate(item.id, {
      onSuccess: () => {
        toast({ title: "Approved", description: "Content approved for publishing." });
        onClose();
      },
    });
  };

  const handleReject = () => {
    rejectContent.mutate(item.id, {
      onSuccess: () => {
        toast({ title: "Rejected", description: "Content has been rejected." });
        onClose();
      },
    });
  };

  const handleApplyStamp = async () => {
    if (!activeWorkspaceId || !item.mediaUrl) return;
    setIsStamping(true);
    try {
      const res = await fetch(`/api/workspaces/${activeWorkspaceId}/apply-brand-stamp`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ postId: item.id }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast({ title: "Stamp Failed", description: data.message || "Could not apply brand stamp.", variant: "destructive" });
      } else {
        setMediaUrl(data.stampedUrl);
        queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "content"] });
        toast({ title: "Brand Stamp Applied", description: "Logo and contact info have been baked into the image." });
      }
    } catch (e: any) {
      toast({ title: "Error", description: e.message || "Failed to apply stamp.", variant: "destructive" });
    } finally {
      setIsStamping(false);
    }
  };

  const formatTabs = getFormatTabsForPlatform(platform);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/70 backdrop-blur-sm" data-testid="content-edit-modal" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-[#0a0a14] border border-border rounded-2xl w-full max-w-4xl max-h-[90vh] mx-4 flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-border flex-shrink-0">
          <div className="flex items-center gap-3">
            <Edit3 size={16} className="text-blue-400" />
            <h3 className="text-sm font-display font-bold text-foreground">Edit Content</h3>
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md border ${approvalBadgeStyle(item.approvalStatus)}`}>
              {approvalLabel(item.approvalStatus)}
            </span>
          </div>
          <button onClick={onClose} className="text-muted-foreground/70 hover:text-foreground transition-colors" data-testid="button-close-modal">
            <X size={18} />
          </button>
        </div>

        {(item as any).qualityStatus === "failed" && (item as any).publishError && (
          <div className="mx-4 mt-3 flex items-start gap-2 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2.5" data-testid="claims-hold-banner">
            <AlertTriangle size={13} className="text-amber-400 shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold text-amber-400">Held: fix these before it can go out</p>
              <p className="text-[11px] text-muted-foreground/80 mt-0.5 break-words whitespace-pre-line">{(item as any).publishError}</p>
            </div>
          </div>
        )}

        {item.status === "PUBLISH_FAILED" && (item as any).publishError && (
          <div className="mx-4 mt-3 flex items-start gap-2 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2.5" data-testid="publish-error-banner">
            <AlertTriangle size={13} className="text-red-400 shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold text-red-400">Publish Failed</p>
              <p className="text-[11px] text-muted-foreground/80 mt-0.5 break-words">{(item as any).publishError}</p>
            </div>
            <button
              data-testid="button-retry-publish-modal"
              onClick={async () => {
                try {
                  const res = await fetch(`/api/workspaces/${item.workspaceId}/content/${item.id}/publish-now`, {
                    method: "POST",
                    credentials: "include",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ platform: item.platform.toLowerCase() }),
                  });
                  if (res.ok) {
                    queryClient.invalidateQueries({ queryKey: ["/api/workspaces", item.workspaceId, "content"] });
                    onClose();
                  } else {
                    const data = await res.json().catch(() => ({}));
                    toast({ title: "Retry Failed", description: data.error || data.message || "Failed to publish. Check that your account is connected in Settings.", variant: "destructive" });
                    queryClient.invalidateQueries({ queryKey: ["/api/workspaces", item.workspaceId, "content"] });
                  }
                } catch (err: any) {
                  toast({ title: "Retry Failed", description: err?.message || "An unexpected error occurred", variant: "destructive" });
                }
              }}
              className="text-xs font-bold px-3 py-1.5 rounded-lg bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30 transition-colors shrink-0"
            >
              <RefreshCw size={11} className="inline mr-1" />
              Retry
            </button>
          </div>
        )}

        <div className="flex flex-col lg:flex-row flex-1 min-h-0 overflow-hidden">
          <div className="lg:w-[55%] overflow-y-auto custom-scrollbar p-4 space-y-4 border-b lg:border-b-0 lg:border-r border-border">
            <div className="space-y-2">
              {(mediaUrl || item.mediaUrl) ? (
                <div className="rounded-xl overflow-hidden border border-border bg-muted/30">
                  <img src={mediaUrl || item.mediaUrl!} alt="Content" className="w-full max-h-48 object-contain" data-testid="modal-image-preview" />
                </div>
              ) : (
                <div className="rounded-xl border border-dashed border-border bg-muted/30 flex items-center justify-center h-24">
                  <span className="text-[10px] text-muted-foreground/50">No image attached</span>
                </div>
              )}
              <div className="flex gap-1.5 flex-wrap">
                <button
                  data-testid="button-regenerate-image"
                  onClick={() => regenerateImageMutation.mutate()}
                  disabled={regenerateImageMutation.isPending}
                  className="border border-border hover:border-blue-500/30 text-muted-foreground hover:text-blue-400 font-semibold rounded-lg px-3 py-1.5 text-xs flex items-center gap-1.5 transition-colors"
                >
                  {regenerateImageMutation.isPending ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                  {(mediaUrl || item.mediaUrl) ? "Regenerate Image" : "Generate Image"}
                </button>
                {(mediaUrl || item.mediaUrl) && (
                  <button
                    data-testid="button-apply-brand-stamp"
                    onClick={handleApplyStamp}
                    disabled={isStamping}
                    className="border border-border hover:border-amber-500/40 text-muted-foreground hover:text-amber-400 font-semibold rounded-lg px-3 py-1.5 text-xs flex items-center gap-1.5 transition-colors"
                    title="Bake logo and contact info onto this image"
                  >
                    {isStamping ? <Loader2 size={12} className="animate-spin" /> : <Stamp size={12} />}
                    Apply Brand Stamp
                  </button>
                )}
              </div>
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Caption</label>
                <button
                  data-testid="button-regenerate-caption"
                  onClick={() => regenerateMutation.mutate()}
                  disabled={regenerateMutation.isPending}
                  className="border border-border hover:border-blue-500/30 text-muted-foreground/70 hover:text-blue-400 font-semibold rounded-md px-2 py-0.5 text-[10px] flex items-center gap-1 transition-colors"
                >
                  {regenerateMutation.isPending ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />}
                  Regenerate
                </button>
              </div>
              <textarea
                data-testid="input-edit-caption"
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                rows={5}
                className="w-full bg-card/50 border border-border focus:border-blue-500 outline-none rounded-xl px-3 py-2.5 text-sm text-foreground/80 resize-none"
              />
              {workspaceCity && caption.match(/\[city\]/i) && (
                <p className="text-[9px] text-muted-foreground/50 mt-1">[city] will display as "{workspaceCity}" in previews</p>
              )}
            </div>

            {contentType === "Carousel" && (
              <CarouselSlidesPanel caption={caption} onCaptionChange={(newCaption) => setCaption(newCaption)} />
            )}

            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Platform</label>
                <select
                  data-testid="select-edit-platform"
                  value={platform}
                  onChange={(e) => {
                    setPlatform(e.target.value);
                    setPreviewFormat(mapPlatformToFormat(e.target.value));
                  }}
                  className="w-full bg-card/50 border border-border focus:border-blue-500 outline-none rounded-lg px-3 py-2 text-xs text-foreground/80"
                >
                  {PLATFORMS.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Type</label>
                <select
                  data-testid="select-edit-type"
                  value={contentType}
                  onChange={(e) => setContentType(e.target.value)}
                  className="w-full bg-card/50 border border-border focus:border-blue-500 outline-none rounded-lg px-3 py-2 text-xs text-foreground/80"
                >
                  {CONTENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Status</label>
                <select
                  data-testid="select-edit-status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="w-full bg-card/50 border border-border focus:border-blue-500 outline-none rounded-lg px-3 py-2 text-xs text-foreground/80"
                >
                  {["DRAFT", "SCHEDULED", "PUBLISHED"].map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Schedule</label>
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="date"
                  data-testid="input-edit-schedule-date"
                  value={schedDate}
                  min={today}
                  onChange={(e) => setSchedDate(e.target.value)}
                  className="w-full bg-card/50 border border-border focus:border-blue-500 outline-none rounded-lg px-3 py-2 text-xs text-foreground/80"
                />
                <input
                  type="time"
                  data-testid="input-edit-schedule-time"
                  value={schedTime}
                  onChange={(e) => setSchedTime(e.target.value)}
                  className="w-full bg-card/50 border border-border focus:border-blue-500 outline-none rounded-lg px-3 py-2 text-xs text-foreground/80"
                />
              </div>
              {schedDate && (
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-muted-foreground/70 flex items-center gap-1">
                    <Clock size={10} />
                    Will schedule for {schedDate} at {schedTime}
                  </span>
                  <button
                    data-testid="button-clear-schedule"
                    onClick={() => { setSchedDate(""); setSchedTime("09:00"); }}
                    className="text-[10px] text-muted-foreground/50 hover:text-muted-foreground transition-colors"
                  >
                    Clear
                  </button>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between pt-2 gap-2 flex-wrap border-t border-border">
              <div className="flex gap-1.5">
                {item.approvalStatus !== "approved" && (
                  <button
                    data-testid="button-approve-content"
                    onClick={handleApprove}
                    disabled={approveContent.isPending}
                    className="bg-green-600/20 hover:bg-green-600/30 text-green-400 font-semibold rounded-lg px-3 py-1.5 text-xs flex items-center gap-1.5 transition-colors border border-green-500/20"
                  >
                    {approveContent.isPending ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
                    Approve
                  </button>
                )}
                {item.approvalStatus !== "rejected" && (
                  <button
                    data-testid="button-reject-content"
                    onClick={handleReject}
                    disabled={rejectContent.isPending}
                    className="bg-red-600/20 hover:bg-red-600/30 text-red-400 font-semibold rounded-lg px-3 py-1.5 text-xs flex items-center gap-1.5 transition-colors border border-red-500/20"
                  >
                    {rejectContent.isPending ? <Loader2 size={12} className="animate-spin" /> : <XCircle size={12} />}
                    Reject
                  </button>
                )}
              </div>
              <div className="flex gap-1.5 flex-wrap">
                <button
                  data-testid="button-cancel-edit"
                  onClick={onClose}
                  className="border border-border text-muted-foreground hover:text-foreground font-semibold rounded-lg px-3 py-1.5 text-xs transition-colors"
                >
                  Cancel
                </button>
                <button
                  data-testid="button-save-edit"
                  onClick={handleSave}
                  disabled={updateContent.isPending}
                  className="bg-blue-600 hover:bg-blue-500 text-white font-semibold rounded-lg px-4 py-1.5 text-xs flex items-center gap-1.5 transition-colors"
                >
                  {updateContent.isPending ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                  Save
                </button>
                {status !== "PUBLISHED" && ["meta", "facebook", "instagram"].includes((platform || "").toLowerCase()) && (
                  <div className="flex gap-1.5">
                    <button
                      data-testid="button-publish-facebook"
                      onClick={() => handlePublishNow("facebook")}
                      disabled={isPublishing}
                      className="bg-[#1877F2] hover:bg-[#166fe5] text-foreground font-semibold rounded-lg px-3 py-1.5 text-xs flex items-center gap-1.5 transition-colors"
                    >
                      {isPublishing && publishTarget === "facebook" ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
                      FB
                    </button>
                    <button
                      data-testid="button-publish-instagram"
                      onClick={() => handlePublishNow("instagram")}
                      disabled={isPublishing || !(mediaUrl || item.mediaUrl)}
                      className="bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-500 hover:to-pink-400 text-white font-semibold rounded-lg px-3 py-1.5 text-xs flex items-center gap-1.5 transition-colors disabled:opacity-40"
                    >
                      {isPublishing && publishTarget === "instagram" ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
                      IG
                    </button>
                  </div>
                )}
                {status !== "PUBLISHED" && (platform || "").toLowerCase() === "linkedin" && (
                  <button
                    data-testid="button-publish-now"
                    onClick={() => handlePublishNow("linkedin")}
                    disabled={isPublishing}
                    className="bg-[#0A66C2] hover:bg-[#095daf] text-foreground font-semibold rounded-lg px-3 py-1.5 text-xs flex items-center gap-1.5 transition-colors"
                  >
                    {isPublishing ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
                    LinkedIn
                  </button>
                )}
                {status !== "PUBLISHED" && ["x", "twitter"].includes((platform || "").toLowerCase()) && (
                  <button
                    data-testid="button-publish-x"
                    onClick={() => handlePublishNow("x")}
                    disabled={isPublishing}
                    className="bg-background hover:bg-zinc-900 text-foreground font-semibold rounded-lg px-3 py-1.5 text-xs flex items-center gap-1.5 transition-colors border border-border"
                  >
                    {isPublishing && publishTarget === "x" ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
                    X
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="lg:w-[45%] overflow-y-auto custom-scrollbar p-4 space-y-3 bg-black/20">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Eye size={13} className="text-muted-foreground" />
                <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Live Preview</span>
              </div>
              {formatTabs.length > 1 && (
                <div className="flex gap-1">
                  {formatTabs.map(tab => (
                    <button
                      key={tab.key}
                      data-testid={`preview-tab-${tab.key}`}
                      onClick={() => setPreviewFormat(tab.key)}
                      className={`px-2 py-0.5 rounded text-[9px] font-semibold transition-colors ${
                        previewFormat === tab.key
                          ? "bg-purple-600/80 text-white"
                          : "bg-white/5 text-white/50 hover:bg-white/10"
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="w-full max-w-[260px] mx-auto" data-testid="modal-social-preview">
              {previewFormat === "google_ad" ? (
                <GoogleAdPreview
                  caption={substituteCityToken(caption, workspaceCity)}
                  url={workspace?.url}
                />
              ) : (
                <SocialPreviewMockup
                  imageUrl={mediaUrl || item.mediaUrl || undefined}
                  caption={substituteCityToken(caption, workspaceCity)}
                  platform={previewFormat}
                  username={workspace?.brandName || workspace?.name || "yourbrand"}
                  showPlatformToggle={false}
                />
              )}
            </div>

            <p className="text-[9px] text-muted-foreground/40 text-center">
              Preview approximation — actual appearance may vary
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function BlogEditModal({ item, onClose }: { item: ContentItem; onClose: () => void }) {
  const [title, setTitle] = useState(item.caption || "");
  const [body, setBody] = useState(item.documentContent || "");
  const [metaDesc, setMetaDesc] = useState(item.metaDescription || "");
  const [mediaUrl, setMediaUrl] = useState(item.mediaUrl || "");
  const [viewMode, setViewMode] = useState<"preview" | "edit">("preview");
  const [seoBreakdownOpen, setSeoBreakdownOpen] = useState(false);
  const [liveSeoScore, setLiveSeoScore] = useState<number | null>(item.seoScore ?? null);
  const [liveSeoDetails, setLiveSeoDetails] = useState<Record<string, { points: number; earned: number; note: string; active?: boolean }> | null>(item.seoDetails ?? null);
  const [liveVerification, setLiveVerification] = useState<{ status: 'pass' | 'fail'; checks: { name: string; passed: boolean; detail?: string }[]; checkedAt: string } | null>((item as any).postVerification ?? null);

  const updateContent = useUpdateContent();
  const approveContent = useApproveContent();
  const rejectContent = useRejectContent();
  const { activeWorkspaceId } = useWorkspaceStore();
  const { toast } = useToast();

  type SeoScoreResult = {
    score: number;
    details: Record<string, { points: number; earned: number; note: string; active?: boolean }>;
  };

  const rescoreSeoMutation = useMutation<SeoScoreResult>({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/content/seo-score", {
        title,
        content: body,
        seoDescription: metaDesc,
        workspaceId: activeWorkspaceId,
      });
      return res.json() as Promise<SeoScoreResult>;
    },
    onSuccess: (data) => {
      setLiveSeoScore(data.score);
      setLiveSeoDetails(data.details);
      setSeoBreakdownOpen(true);
      toast({ title: `SEO re-scored: ${data.score}/100` });
    },
    onError: () => toast({ title: "SEO scoring failed", variant: "destructive" }),
  });

  const reverifyMutation = useMutation<{ status: 'pass' | 'fail'; checks: { name: string; passed: boolean; detail?: string }[]; checkedAt: string }>({
    mutationFn: async () => {
      const res = await apiRequest('POST', `/api/workspaces/${activeWorkspaceId}/content/${item.id}/verify-post`, {});
      return res.json();
    },
    onSuccess: (data) => {
      setLiveVerification(data);
      toast({ title: data.status === 'pass' ? 'Post verified — all checks passed' : `Verification found ${data.checks.filter(c => !c.passed).length} issue(s)` });
    },
    onError: () => toast({ title: 'Re-verification failed', variant: 'destructive' }),
  });

  useEffect(() => {
    function handleEsc(e: KeyboardEvent) { if (e.key === "Escape") onClose(); }
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  const regenerateImageMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/ai/image", {
        prompt: `Professional blog hero image for article titled "${title}". Cinematic, clean composition, relevant visual. No text overlay.`,
        workspaceId: activeWorkspaceId,
      });
      return res.json();
    },
    onSuccess: (data: any) => {
      if (data.imageUrl) { setMediaUrl(data.imageUrl); toast({ title: "Hero image generated" }); }
    },
    onError: () => toast({ title: "Image generation failed", variant: "destructive" }),
  });

  const handleSave = () => {
    updateContent.mutate(
      { id: item.id, caption: title, documentContent: body, metaDescription: metaDesc, mediaUrl: mediaUrl || null } as any,
      { onSuccess: () => { toast({ title: "Blog saved" }); onClose(); } }
    );
  };

  const handleApprove = () => {
    approveContent.mutate(item.id, { onSuccess: () => { toast({ title: "Blog approved" }); onClose(); } });
  };

  const handleReject = () => {
    rejectContent.mutate(item.id, { onSuccess: () => { toast({ title: "Blog returned for revision" }); onClose(); } });
  };

  const isPending = item.approvalStatus === "pending_approval" || item.approvalStatus === "pending";
  const seoOk = (liveSeoScore ?? 0) >= 80;

  const cleanBody = useMemo(() => {
    if (!body) return "";
    return body.replace(/(\n{3,})/g, "\n\n").trimStart();
  }, [body]);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="blog-edit-modal">
      <div className="bg-card border border-border rounded-2xl w-full max-w-4xl max-h-[92vh] overflow-hidden flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
          <div className="flex items-center gap-3">
            <BookOpen size={18} className="text-violet-400" />
            <span className="text-sm font-semibold text-foreground">Blog Editor</span>
            {liveSeoScore != null && (
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded border ${seoOk ? "border-green-500/30 bg-green-500/10 text-green-400" : "border-amber-500/30 bg-amber-500/10 text-amber-400"}`} data-testid="blog-editor-seo-badge">
                SEO {liveSeoScore}/100
              </span>
            )}
            <button
              onClick={() => rescoreSeoMutation.mutate()}
              disabled={rescoreSeoMutation.isPending || !activeWorkspaceId}
              className="flex items-center gap-1 px-2 py-0.5 rounded border border-violet-500/30 bg-violet-500/10 text-violet-400 hover:bg-violet-500/20 transition-colors text-[10px] font-semibold disabled:opacity-50"
              data-testid="blog-editor-rescore-seo"
            >
              {rescoreSeoMutation.isPending ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />}
              Re-score SEO
            </button>
            {item.blogUrl && (
              <a href={item.blogUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[10px] text-violet-400 hover:text-violet-300 transition-colors" data-testid="blog-editor-wp-link">
                <Globe size={10} />View on WordPress<ExternalLink size={9} />
              </a>
            )}
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted/60 transition-colors" data-testid="blog-editor-close">
            <X size={16} className="text-muted-foreground" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* Hero image */}
          <div className="space-y-2">
            <label className="text-[10px] font-bold text-muted-foreground/60 uppercase tracking-wider">Hero Image</label>
            {mediaUrl ? (
              <div className="relative rounded-xl overflow-hidden bg-muted/30" style={{ aspectRatio: "16/7" }}>
                <img src={mediaUrl} alt="Blog hero" className="w-full h-full object-cover" data-testid="blog-editor-hero-img" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                <button
                  onClick={() => regenerateImageMutation.mutate()}
                  disabled={regenerateImageMutation.isPending}
                  className="absolute bottom-3 right-3 flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-black/70 text-white rounded-lg hover:bg-black/85 transition-colors disabled:opacity-60"
                  data-testid="blog-editor-regen-hero"
                >
                  {regenerateImageMutation.isPending ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                  Regenerate Image
                </button>
              </div>
            ) : (
              <button
                onClick={() => regenerateImageMutation.mutate()}
                disabled={regenerateImageMutation.isPending}
                className="w-full border-2 border-dashed border-border/60 rounded-xl flex flex-col items-center justify-center gap-2 py-10 hover:border-violet-500/40 hover:bg-violet-500/5 transition-all"
                data-testid="blog-editor-gen-hero"
              >
                {regenerateImageMutation.isPending
                  ? <Loader2 size={22} className="animate-spin text-violet-400" />
                  : <Image size={22} className="text-muted-foreground/40" />}
                <span className="text-xs text-muted-foreground/60">
                  {regenerateImageMutation.isPending ? "Generating hero image…" : "Generate Hero Image"}
                </span>
              </button>
            )}
          </div>

          {/* Title */}
          <div className="space-y-1.5">
            <label className="text-[10px] font-bold text-muted-foreground/60 uppercase tracking-wider">Title</label>
            <input
              value={title}
              onChange={e => setTitle(e.target.value)}
              className="w-full bg-background/60 border border-border rounded-lg px-3 py-2 text-sm font-semibold text-foreground focus:outline-none focus:ring-1 focus:ring-violet-500/50"
              placeholder="Blog title…"
              data-testid="blog-editor-title"
            />
          </div>

          {/* SEO explanation when below threshold */}
          {liveSeoScore != null && !seoOk && (
            <div className="flex items-start gap-2.5 px-3 py-2.5 bg-amber-500/8 border border-amber-500/20 rounded-lg">
              <AlertTriangle size={14} className="text-amber-400 shrink-0 mt-0.5" />
              <p className="text-[11px] text-amber-300/80 leading-relaxed">
                SEO score is {liveSeoScore}/100. Peggy ran up to 5 revision cycles trying to reach the target threshold — if the score was still too low, the post is saved here for your review instead of publishing automatically. Improve the title, body, and meta description below, then approve manually.
              </p>
            </div>
          )}

          {/* SEO Breakdown Panel */}
          {liveSeoScore != null && liveSeoDetails && (
            <div className="border border-border/60 rounded-xl overflow-hidden">
              <button
                onClick={() => setSeoBreakdownOpen(o => !o)}
                className="w-full flex items-center justify-between px-4 py-2.5 bg-muted/30 hover:bg-muted/50 transition-colors"
                data-testid="blog-editor-seo-breakdown-toggle"
              >
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-bold text-muted-foreground/70 uppercase tracking-wider">SEO Breakdown</span>
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${seoOk ? "bg-green-500/15 text-green-400" : "bg-amber-500/15 text-amber-400"}`}>
                    {liveSeoScore}/100
                  </span>
                </div>
                {seoBreakdownOpen ? <ChevronUp size={14} className="text-muted-foreground/60" /> : <ChevronDown size={14} className="text-muted-foreground/60" />}
              </button>
              {seoBreakdownOpen && (
                <div className="divide-y divide-border/30" data-testid="blog-editor-seo-breakdown-panel">
                  {Object.entries(liveSeoDetails).map(([key, val]) => {
                    const label = key.replace(/([A-Z])/g, " $1").replace(/^./, s => s.toUpperCase());
                    const pct = val.points > 0 ? Math.round((val.earned / val.points) * 100) : 0;
                    const passed = val.earned >= val.points;
                    return (
                      <div key={key} className="px-4 py-2.5 flex items-start gap-3" data-testid={`seo-criterion-${key}`}>
                        <div className={`mt-0.5 w-3.5 h-3.5 rounded-full shrink-0 flex items-center justify-center ${passed ? "bg-green-500/20" : val.earned > 0 ? "bg-amber-500/20" : "bg-red-500/20"}`}>
                          <div className={`w-1.5 h-1.5 rounded-full ${passed ? "bg-green-400" : val.earned > 0 ? "bg-amber-400" : "bg-red-400"}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <span className="text-[11px] font-semibold text-foreground/80 truncate">{label}</span>
                            <span className="text-[10px] text-muted-foreground/60 shrink-0">{val.earned}/{val.points} pts</span>
                          </div>
                          <div className="w-full h-1 bg-muted/50 rounded-full overflow-hidden mb-1">
                            <div
                              className={`h-full rounded-full transition-all ${passed ? "bg-green-500" : val.earned > 0 ? "bg-amber-400" : "bg-red-500/50"}`}
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                          {val.note && <p className="text-[10px] text-muted-foreground/60 leading-relaxed">{val.note}</p>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Article body */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-[10px] font-bold text-muted-foreground/60 uppercase tracking-wider">Article Body</label>
              <div className="flex items-center gap-0.5 border border-border/60 rounded-lg p-0.5">
                <button
                  onClick={() => setViewMode("preview")}
                  className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors ${viewMode === "preview" ? "bg-muted text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                  data-testid="blog-editor-preview-tab"
                >
                  <Eye size={10} /> Preview
                </button>
                <button
                  onClick={() => setViewMode("edit")}
                  className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors ${viewMode === "edit" ? "bg-muted text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                  data-testid="blog-editor-edit-tab"
                >
                  <Edit3 size={10} /> Edit
                </button>
              </div>
            </div>
            {viewMode === "preview" ? (
              <div
                className="min-h-[280px] max-h-[480px] overflow-y-auto bg-background/40 border border-border/60 rounded-xl px-6 py-5 prose prose-sm prose-invert max-w-none [&_h1]:text-xl [&_h1]:font-bold [&_h1]:mb-3 [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:mt-6 [&_h2]:mb-2 [&_h3]:text-base [&_h3]:font-semibold [&_h3]:mt-4 [&_h3]:mb-1 [&_p]:text-[13px] [&_p]:leading-7 [&_p]:text-foreground/85 [&_p]:mb-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-2 [&_li]:text-[13px] [&_li]:leading-6 [&_a]:text-violet-400 [&_a:hover]:text-violet-300 [&_strong]:text-foreground [&_blockquote]:border-l-2 [&_blockquote]:border-violet-400/40 [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-muted-foreground"
                data-testid="blog-editor-preview"
              >
                {cleanBody
                  ? <ReactMarkdown>{cleanBody}</ReactMarkdown>
                  : <p className="text-muted-foreground/50 italic text-sm">No content yet — switch to Edit to add the blog body.</p>
                }
              </div>
            ) : (
              <textarea
                value={body}
                onChange={e => setBody(e.target.value)}
                rows={18}
                className="w-full bg-background/60 border border-border rounded-xl px-4 py-3 text-xs text-foreground/85 font-mono leading-relaxed focus:outline-none focus:ring-1 focus:ring-violet-500/50 resize-none"
                placeholder="Full article content (Markdown or HTML)…"
                data-testid="blog-editor-body"
              />
            )}
          </div>

          {/* Meta description */}
          <div className="space-y-1.5">
            <label className="text-[10px] font-bold text-muted-foreground/60 uppercase tracking-wider">
              Meta Description <span className="text-muted-foreground/40 normal-case font-normal">(120–160 chars, for SEO)</span>
            </label>
            <textarea
              value={metaDesc}
              onChange={e => setMetaDesc(e.target.value)}
              rows={2}
              className="w-full bg-background/60 border border-border rounded-lg px-3 py-2 text-sm text-foreground/85 focus:outline-none focus:ring-1 focus:ring-violet-500/50 resize-none"
              placeholder="Concise summary shown in search engine results…"
              data-testid="blog-editor-meta"
            />
            <p className={`text-[10px] ${metaDesc.length > 160 ? "text-red-400" : "text-muted-foreground/40"}`}>
              {metaDesc.length} / 160 chars
            </p>
          </div>

          {/* Post Verification */}
          {(item as any).blogUrl && (
            <div className='space-y-2'>
              <div className='flex items-center gap-2'>
                <label className='text-[10px] font-bold text-muted-foreground/60 uppercase tracking-wider flex-1'>Post Verification</label>
                <button
                  onClick={() => reverifyMutation.mutate()}
                  disabled={reverifyMutation.isPending || !activeWorkspaceId}
                  className='flex items-center gap-1 px-2 py-0.5 rounded border border-violet-500/30 bg-violet-500/10 text-violet-400 hover:bg-violet-500/20 transition-colors text-[10px] font-semibold disabled:opacity-50'
                  data-testid='blog-editor-reverify'
                >
                  {reverifyMutation.isPending ? <Loader2 size={10} className='animate-spin' /> : <RefreshCw size={10} />}
                  Re-verify
                </button>
              </div>
              {liveVerification ? (
                <div className='rounded-xl bg-muted/20 border border-border/40 p-3 space-y-1.5' data-testid='blog-editor-verify-panel'>
                  {liveVerification.checks.map((check, ci) => (
                    <div key={ci} className={`flex items-start gap-2 text-[11px] ${check.passed ? 'text-emerald-400/90' : 'text-amber-400/90'}`}>
                      {check.passed ? <Check size={11} className='shrink-0 mt-0.5' /> : <AlertTriangle size={11} className='shrink-0 mt-0.5' />}
                      <div className='min-w-0'>
                        <span className='font-semibold'>{check.name.replace(/_/g, ' ')}</span>
                        {check.detail && <span className='text-muted-foreground/60 ml-1'>— {check.detail}</span>}
                      </div>
                    </div>
                  ))}
                  <p className='text-[9px] text-muted-foreground/30 pt-1 border-t border-border/20'>
                    Checked {new Date(liveVerification.checkedAt).toLocaleString()} · {liveVerification.checks.filter(c => c.passed).length}/{liveVerification.checks.length} checks passed
                  </p>
                </div>
              ) : (
                <p className='text-[11px] text-muted-foreground/50 italic' data-testid='blog-editor-verify-empty'>Not verified yet — click Re-verify to run checks on the live post.</p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-border shrink-0 gap-3">
          <div className="flex items-center gap-2">
            {isPending && (
              <>
                <button
                  onClick={handleApprove}
                  disabled={approveContent.isPending}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-emerald-400 border border-emerald-500/30 bg-emerald-500/10 hover:bg-emerald-500/20 transition-colors disabled:opacity-50"
                  data-testid="blog-editor-approve"
                >
                  {approveContent.isPending ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Approve
                </button>
                <button
                  onClick={handleReject}
                  disabled={rejectContent.isPending}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-rose-400 border border-rose-500/30 bg-rose-500/10 hover:bg-rose-500/20 transition-colors disabled:opacity-50"
                  data-testid="blog-editor-reject"
                >
                  {rejectContent.isPending ? <Loader2 size={12} className="animate-spin" /> : <XCircle size={12} />} Reject
                </button>
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-3 py-1.5 rounded-lg text-xs text-muted-foreground hover:text-foreground transition-colors">
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={updateContent.isPending}
              className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-semibold bg-violet-600 hover:bg-violet-500 text-white transition-colors disabled:opacity-50"
              data-testid="blog-editor-save"
            >
              {updateContent.isPending ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function statusBadgeStyle(status: string) {
  switch (status) {
    case "PUBLISHED": return "bg-green-500/15 text-green-400 border-green-500/20";
    case "SCHEDULED": return "bg-blue-500/15 text-blue-400 border-blue-500/20";
    case "PUBLISH_FAILED": return "bg-red-500/15 text-red-400 border-red-500/30";
    case "READY": return "bg-yellow-500/15 text-yellow-400 border-yellow-500/20";
    default: return "bg-muted text-muted-foreground border-border";
  }
}

const PLATFORM_ICONS: Record<string, { color: string; label: string }> = {
  LinkedIn: { color: "bg-blue-500", label: "in" },
  Instagram: { color: "bg-gradient-to-br from-purple-500 to-pink-500", label: "ig" },
  X: { color: "bg-foreground/60", label: "X" },
  Meta: { color: "bg-blue-400", label: "fb" },
  Google: { color: "bg-green-500", label: "G" },
  TikTok: { color: "bg-purple-500", label: "tt" },
};

function ContentPreviewCard({ item, onEdit, onApprove, onReject, onDelete, onRegenerate, isRegenerating, onSyncEngagement, syncingEngagement, stampEnabled, isPlatformConnected, workspaceId, onMakeReel }: {
  item: ContentItem;
  onEdit: () => void;
  onApprove: () => void;
  onReject: () => void;
  onDelete: () => void;
  onRegenerate?: () => void;
  isRegenerating?: boolean;
  onSyncEngagement?: () => void;
  syncingEngagement?: boolean;
  stampEnabled?: boolean;
  isPlatformConnected?: boolean;
  workspaceId?: number;
  onMakeReel?: () => void;
}) {
  const pi = PLATFORM_ICONS[item.platform] || { color: "bg-foreground/30", label: "?" };
  const previewFmt = mapPlatformToFormat(item.platform);
  const [cardPublishing, setCardPublishing] = useState(false);
  const [stampingCard, setStampingCard] = useState(false);
  const { toast } = useToast();

  return (
    <div
      className="group bg-muted/30 border border-border rounded-xl overflow-hidden hover:border-purple-500/30 transition-all cursor-pointer flex flex-col"
      data-testid={`content-card-${item.id}`}
      onClick={onEdit}
    >
      <div className="relative bg-black overflow-hidden" style={{ height: "160px" }}>
        <div
          className="absolute inset-0 flex items-start justify-center pointer-events-none overflow-hidden"
          style={{ transform: "scale(0.4)", transformOrigin: "top center", width: "250%", marginLeft: "-75%" }}
        >
          <div className="w-full">
            <SocialPreviewMockup
              imageUrl={item.mediaUrl || undefined}
              caption={item.caption}
              platform={previewFmt}
              username="yourbrand"
              showPlatformToggle={false}
            />
          </div>
        </div>
        <div className="absolute top-2 left-2 z-10">
          <div className={`w-6 h-6 rounded-md ${pi.color} flex items-center justify-center`}>
            <span className="text-[8px] font-black text-foreground uppercase leading-none">{pi.label}</span>
          </div>
        </div>
        <div className="absolute top-2 right-2 flex gap-1 z-10">
          <span
            className={`text-[9px] font-bold px-1.5 py-0.5 rounded border backdrop-blur-sm ${statusBadgeStyle(item.status)}`}
            title={item.status === "PUBLISH_FAILED" && (item as any).publishError ? `Error: ${(item as any).publishError}` : item.status}
            data-testid={`status-badge-${item.id}`}
          >
            {item.status === "PUBLISH_FAILED" ? "✗ FAILED" : item.status === "PUBLISHED" ? "✓ LIVE" : item.status}
          </span>
        </div>
        {item.mediaUrl && workspaceId && (
          <div className="absolute bottom-2 right-2 z-20">
            <button
              data-testid={`button-card-stamp-${item.id}`}
              onClick={async (e) => {
                e.stopPropagation();
                if (stampingCard) return;
                setStampingCard(true);
                try {
                  const res = await fetch(`/api/workspaces/${workspaceId}/apply-brand-stamp`, {
                    method: "POST",
                    credentials: "include",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ postId: item.id }),
                  });
                  const data = await res.json();
                  if (res.ok) {
                    queryClient.invalidateQueries({ queryKey: ["/api/workspaces", workspaceId, "content"] });
                    toast({ title: "Stamp applied", description: "Brand stamp baked into image." });
                  } else {
                    toast({ title: "Stamp failed", description: data.message, variant: "destructive" });
                  }
                } catch { toast({ title: "Stamp error", variant: "destructive" }); }
                finally { setStampingCard(false); }
              }}
              className="w-8 h-8 rounded-md bg-black/70 backdrop-blur-sm border border-white/20 flex items-center justify-center shadow-lg hover:bg-black/90 transition-colors"
              title="Apply Brand Stamp"
            >
              {stampingCard ? <Loader2 size={12} className="animate-spin text-white" /> : <Stamp size={12} className="text-amber-300" />}
            </button>
          </div>
        )}
      </div>
      <div className="p-2.5 space-y-1.5 flex-1 flex flex-col">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-[10px] font-bold text-muted-foreground uppercase">{item.type}</span>
          {(item as any).offerTier && (
            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border ${(item as any).offerTier === "aggressive" ? "text-red-400 border-red-500/30 bg-red-500/10" : (item as any).offerTier === "medium" ? "text-amber-400 border-amber-500/30 bg-amber-500/10" : "text-blue-400 border-blue-500/30 bg-blue-500/10"}`}>
              {(item as any).offerTier}
            </span>
          )}
          {(item as any).qualityStatus === "flagged" && (
            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded border text-amber-400 border-amber-500/30 bg-amber-500/10" title={`Quality Score: ${(item as any).qualityScore || "?"}/100`}>
              ⚠ Review
            </span>
          )}
          <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border ml-auto ${approvalBadgeStyle(item.approvalStatus)}`}>
            {approvalLabel(item.approvalStatus)}
          </span>
        </div>
        <p className="text-[11px] text-muted-foreground line-clamp-3 flex-1">{item.caption}</p>
        {isPlatformConnected === false && item.status === "DRAFT" && (
          <div className="flex items-center justify-between gap-1.5 px-2 py-1.5 rounded-lg bg-amber-500/8 border border-amber-500/20" data-testid={`connect-platform-cta-${item.id}`}>
            <span className="text-[10px] text-amber-400/80 leading-tight">Auto-publish requires {item.platform} connection.</span>
            <a href="/settings" onClick={(e) => e.stopPropagation()} className="text-[10px] font-bold text-blue-400 hover:text-blue-300 transition-colors shrink-0" data-testid={`link-connect-cta-${item.id}`}>Connect →</a>
          </div>
        )}
        {item.status === "PUBLISHED" && (
          <div className="space-y-1 pt-1" data-testid={`engagement-stats-${item.id}`}>
            <div className="grid grid-cols-5 gap-1">
              <div className="text-center bg-purple-500/10 border border-purple-500/20 rounded px-0.5 py-0.5">
                <p className="text-[9px] font-bold text-purple-300">{((item as any).impressions || 0).toLocaleString()}</p>
                <p className="text-[7px] text-muted-foreground">👁</p>
              </div>
              <div className="text-center bg-pink-500/10 border border-pink-500/20 rounded px-0.5 py-0.5">
                <p className="text-[9px] font-bold text-pink-300">{(item as any).likes || 0}</p>
                <p className="text-[7px] text-muted-foreground">❤️</p>
              </div>
              <div className="text-center bg-blue-500/10 border border-blue-500/20 rounded px-0.5 py-0.5">
                <p className="text-[9px] font-bold text-blue-300">{(item as any).comments || 0}</p>
                <p className="text-[7px] text-muted-foreground">💬</p>
              </div>
              <div className="text-center bg-sky-500/10 border border-sky-500/20 rounded px-0.5 py-0.5">
                <p className="text-[9px] font-bold text-sky-300">{(item as any).shares || 0}</p>
                <p className="text-[7px] text-muted-foreground">↗️</p>
              </div>
              <div className="text-center bg-green-500/10 border border-green-500/20 rounded px-0.5 py-0.5">
                <p className="text-[9px] font-bold text-green-300">
                  {(item as any).engagementRate != null ? `${Number((item as any).engagementRate).toFixed(1)}%` : "—"}
                </p>
                <p className="text-[7px] text-muted-foreground">📊</p>
              </div>
            </div>
            {(item as any).engagementSyncedAt && (
              <p className="text-[7px] text-muted-foreground/40" data-testid={`engagement-synced-at-${item.id}`}>
                Synced {new Date((item as any).engagementSyncedAt).toLocaleDateString()}
              </p>
            )}
          </div>
        )}
        {(item as any).publishedAt && item.status === "PUBLISHED" && (
          <div className="flex items-center gap-1 text-[10px] text-green-400/60" data-testid={`published-at-${item.id}`}>
            <CheckCircle2 size={10} />
            Published {new Date((item as any).publishedAt).toLocaleDateString()} {new Date((item as any).publishedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
          </div>
        )}
        {item.scheduledAt && item.status !== "PUBLISHED" && (
          <div className="flex items-center gap-1 text-[10px] text-muted-foreground/60">
            <Clock size={10} />
            {new Date(item.scheduledAt).toLocaleDateString()} {new Date(item.scheduledAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
          </div>
        )}
        <div className="flex items-center gap-1 pt-1.5 border-t border-border" onClick={(e) => e.stopPropagation()}>
          {item.approvalStatus === "pending_approval" && (
            <>
              <button
                data-testid={`button-quick-approve-${item.id}`}
                onClick={onApprove}
                className="text-green-400/50 hover:text-green-400 transition-colors p-1"
                title="Approve"
              >
                <CheckCircle2 size={13} />
              </button>
              <button
                data-testid={`button-quick-reject-${item.id}`}
                onClick={onReject}
                className="text-red-400/50 hover:text-red-400 transition-colors p-1"
                title="Reject"
              >
                <XCircle size={13} />
              </button>
            </>
          )}
          {(item as any).qualityStatus === "flagged" && onRegenerate && (
            <button
              data-testid={`button-regenerate-${item.id}`}
              onClick={() => { if (!isRegenerating) onRegenerate(); }}
              disabled={isRegenerating}
              className="text-amber-400/50 hover:text-amber-400 transition-colors p-1 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Regenerate Content"
            >
              {isRegenerating ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
            </button>
          )}
          {item.status !== "PUBLISHED" && item.status !== "PUBLISH_FAILED" && (
            <button
              data-testid={`button-card-publish-${item.id}`}
              disabled={cardPublishing}
              onClick={async (e) => {
                e.stopPropagation();
                if (cardPublishing) return;
                setCardPublishing(true);
                try {
                  const res = await fetch(`/api/workspaces/${item.workspaceId}/content/${item.id}/publish-now`, {
                    method: "POST",
                    credentials: "include",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ platform: item.platform.toLowerCase() }),
                  });
                  if (res.ok) {
                    queryClient.invalidateQueries({ queryKey: ["/api/workspaces", item.workspaceId, "content"] });
                  } else {
                    const data = await res.json().catch(() => ({}));
                    if (data.blocked) {
                      toast({ title: "Not posted", description: data.message || data.error || "Test mode: not posted." });
                    } else if (data.code === "NEEDS_IMAGE") {
                      toast({ title: "Image Required", description: "Instagram requires an image. Open the post to generate one first.", variant: "destructive" });
                    } else {
                      toast({ title: "Publish Failed", description: data.error || data.message || "Failed to publish", variant: "destructive" });
                    }
                  }
                } catch (e: any) {
                  toast({ title: "Publish Failed", description: e.message || "Connection error", variant: "destructive" });
                } finally {
                  setCardPublishing(false);
                }
              }}
              className="text-blue-400/50 hover:text-blue-400 transition-colors p-1 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Publish Now"
            >
              {cardPublishing ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
            </button>
          )}
          {item.status === "PUBLISH_FAILED" && (
            <button
              data-testid={`button-retry-publish-${item.id}`}
              onClick={async (e) => {
                e.stopPropagation();
                try {
                  const res = await fetch(`/api/workspaces/${item.workspaceId}/content/${item.id}/publish-now`, {
                    method: "POST",
                    credentials: "include",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ platform: item.platform.toLowerCase() }),
                  });
                  if (res.ok) {
                    queryClient.invalidateQueries({ queryKey: ["/api/workspaces", item.workspaceId, "content"] });
                  } else {
                    const data = await res.json().catch(() => ({}));
                    toast({ title: "Retry Failed", description: data.error || data.message || "Retry failed", variant: "destructive" });
                  }
                } catch (e: any) {
                  toast({ title: "Retry Failed", description: e.message || "Connection error", variant: "destructive" });
                }
              }}
              className="text-red-400/50 hover:text-red-400 transition-colors p-1"
              title="Retry Publish"
            >
              <RefreshCw size={13} />
            </button>
          )}
          {item.status === "PUBLISHED" && onSyncEngagement && ["facebook", "instagram", "meta"].includes((item.platform || "").toLowerCase()) && (
            <button
              data-testid={`button-sync-engagement-${item.id}`}
              onClick={(e) => { e.stopPropagation(); onSyncEngagement(); }}
              disabled={syncingEngagement}
              className="text-purple-400/50 hover:text-purple-400 transition-colors p-1"
              title="Refresh engagement stats"
            >
              {syncingEngagement ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
            </button>
          )}
          {onMakeReel && (
            <button
              data-testid={`button-make-reel-${item.id}`}
              onClick={(e) => { e.stopPropagation(); onMakeReel(); }}
              className="text-muted-foreground/50 hover:text-rose-400 transition-colors p-1"
              title="Make Reel"
            >
              <Film size={13} />
            </button>
          )}
          <button
            data-testid={`button-edit-card-${item.id}`}
            onClick={onEdit}
            className="text-muted-foreground/50 hover:text-foreground transition-colors p-1 ml-auto"
            title="Edit"
          >
            <Edit3 size={13} />
          </button>
          <button
            data-testid={`button-delete-card-${item.id}`}
            onClick={onDelete}
            className="text-muted-foreground/50 hover:text-red-400 transition-colors p-1"
            title="Delete"
          >
            <Trash2 size={13} />
          </button>
        </div>
      </div>
    </div>
  );
}

function LibraryTab({ onCreatePostFromPhoto }: { onCreatePostFromPhoto?: (assetUrl: string) => void } = {}) {
  const { activeWorkspaceId } = useWorkspaceStore();
  const { data: savedContent, isLoading: loadingContent } = useContent();
  const { copiedKey, copy } = useCopyFeedback();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [editItem, setEditItem] = useState<ContentItem | null>(null);
  // Dashboard links arrive as /content?tab=library&approvalStatus=pending_approval.
  const [filterApproval, setFilterApproval] = useState<string>(() => {
    try {
      const fromUrl = new URLSearchParams(window.location.search).get("approvalStatus");
      return fromUrl && ["pending_approval", "approved", "rejected"].includes(fromUrl) ? fromUrl : "all";
    } catch {
      return "all";
    }
  });
  const [filterTier, setFilterTier] = useState<string>("all");
  const [filterQuality, setFilterQuality] = useState<string>("all");
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [sortBy, setSortBy] = useState<"recent" | "top">("recent");
  const [syncingItemId, setSyncingItemId] = useState<number | null>(null);
  const approveContent = useApproveContent();
  const rejectContent = useRejectContent();

  const { data: workspaceListLib } = useQuery<any[]>({ queryKey: ["/api/workspaces"] });
  const activeWorkspaceLib = workspaceListLib?.find((w: any) => w.id === activeWorkspaceId);
  const wsIntegrationsLib = (activeWorkspaceLib?.integrations as any) || {};

  function isLibPlatformConnected(p: string): boolean {
    const key = p.toLowerCase();
    if (key === "instagram" || key === "meta" || key === "facebook") {
      return !!(wsIntegrationsLib?.meta?.connected || wsIntegrationsLib?.instagram?.connected || wsIntegrationsLib?.facebook?.connected || wsIntegrationsLib?.instagram_business?.connected);
    }
    if (key === "x" || key === "twitter") return !!(wsIntegrationsLib?.x?.connected);
    if (key === "linkedin") return !!(wsIntegrationsLib?.linkedin?.connected);
    if (key === "google") return !!(wsIntegrationsLib?.google?.connected);
    return false;
  }

  const { data: brandKit } = useQuery<any>({
    queryKey: ["/api/workspaces", activeWorkspaceId, "brand-kit"],
    enabled: !!activeWorkspaceId,
  });

  const applyBrandMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/workspaces/${activeWorkspaceId}/content/apply-brand`, {});
      return res.json();
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "content"] });
      toast({ title: "Brand kit applied", description: data.message || "Brand kit applied to all content." });
    },
    onError: (e: any) => {
      toast({ title: "Failed", description: e.message || "Could not apply brand kit.", variant: "destructive" });
    },
  });

  const { data: assets, isLoading: loadingAssets } = useQuery<WorkspaceAsset[]>({
    queryKey: ["/api/workspaces", activeWorkspaceId, "assets"],
    enabled: !!activeWorkspaceId,
  });

  const uploadAsset = useMutation({
    mutationFn: async (file: File) => {
      return new Promise<any>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = async () => {
          try {
            const res = await apiRequest("POST", `/api/workspaces/${activeWorkspaceId}/assets`, {
              name: file.name,
              url: reader.result as string,
              type: file.type.startsWith("image/") ? "image" : "file",
              size: file.size,
            });
            resolve(await res.json());
          } catch (e) { reject(e); }
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "assets"] });
      toast({ title: "Uploaded", description: "Asset uploaded to your library." });
    },
    onError: () => {
      toast({ title: "Upload Failed", description: "Failed to upload asset.", variant: "destructive" });
    },
  });

  const deleteAssetMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiRequest("DELETE", `/api/assets/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "assets"] });
      toast({ title: "Deleted", description: "Asset removed from library." });
    },
  });

  const deleteContentMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiRequest("DELETE", `/api/content/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "content"] });
      toast({ title: "Deleted", description: "Content removed from library." });
    },
  });

  const regenerateContentMutation = useMutation({
    mutationFn: async (item: ContentItem) => {
      const res = await apiRequest("POST", `/api/workspaces/${activeWorkspaceId}/regenerate-content`, {
        itemId: item.id,
        platform: item.platform,
        context: item.caption,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "content"] });
      toast({ title: "Regenerated", description: "Content has been regenerated with improvements." });
    },
    onError: () => {
      toast({ title: "Failed", description: "Could not regenerate content.", variant: "destructive" });
    },
  });

  function downloadText(text: string, filename: string) {
    const blob = new Blob([text], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  function downloadImage(dataUrl: string, filename: string) {
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = filename;
    a.click();
  }

  function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const files = e.target.files;
    if (!files) return;
    Array.from(files).forEach(file => uploadAsset.mutate(file));
    e.target.value = "";
  }

  const syncEngagement = async (item: ContentItem) => {
    if (!activeWorkspaceId) return;
    setSyncingItemId(item.id);
    try {
      const res = await fetch(`/api/workspaces/${activeWorkspaceId}/content/${item.id}/sync-engagement`, {
        method: "POST",
        credentials: "include",
      });
      const data = await res.json();
      if (res.ok) {
        queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "content"] });
        toast({ title: "Engagement synced", description: `${data.impressions?.toLocaleString() || 0} impressions · ${(data.engagementRate || 0).toFixed(1)}% rate` });
      } else {
        toast({ title: "Sync failed", description: data.message || "Could not sync engagement.", variant: "destructive" });
      }
    } catch {
      toast({ title: "Sync failed", description: "Network error.", variant: "destructive" });
    } finally {
      setSyncingItemId(null);
    }
  };

  const imageAssets = assets?.filter(a => a.type === "image") || [];

  const failedItems = savedContent?.filter((i: ContentItem) => i.status === "PUBLISH_FAILED") || [];
  const filteredContent = savedContent?.filter((item: ContentItem) => {
    const approvalMatch = filterApproval === "all" ? true : item.approvalStatus === filterApproval;
    const tierMatch = filterTier === "all" ? true : (filterTier === "none" ? !item.offerTier : item.offerTier === filterTier);
    const qualityMatch = filterQuality === "all" ? true : (item as any).qualityStatus === filterQuality;
    const statusMatch = filterStatus === "all" ? true : item.status === filterStatus;
    return approvalMatch && tierMatch && qualityMatch && statusMatch;
  })?.sort((a: ContentItem, b: ContentItem) => {
    if (sortBy === "top") {
      return ((b as any).engagementRate || 0) - ((a as any).engagementRate || 0);
    }
    return new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime();
  });

  const contentWithoutImages = savedContent?.filter((c: ContentItem) => !c.mediaUrl) || [];
  const totalContent = savedContent?.length || 0;
  const showImageBanner = totalContent > 0 && contentWithoutImages.length > totalContent * 0.8;

  return (
    <div className="space-y-6" data-testid="library-tab">
      {showImageBanner && (
        <div className="bg-gradient-to-r from-amber-500/[0.08] via-amber-600/[0.04] to-transparent border border-amber-500/[0.12] rounded-xl p-4 flex items-start gap-3" data-testid="banner-missing-images">
          <AlertTriangle size={16} className="text-amber-400 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-foreground/80 font-medium">Most content items don't have images yet</p>
            <p className="text-xs text-muted-foreground/70 mt-1">
              Images are generated when AI agents run. Make sure AI is enabled in your <a href="/settings" className="text-blue-400 hover:text-blue-300 underline underline-offset-2">workspace settings</a>, or upload your own brand assets below.
            </p>
          </div>
        </div>
      )}
      {failedItems.length > 0 && (
        <div className="bg-gradient-to-r from-red-500/10 via-red-600/5 to-transparent border border-red-500/20 rounded-xl p-4 flex items-start gap-3" data-testid="banner-publish-failed">
          <AlertTriangle size={16} className="text-red-400 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm text-foreground/80 font-medium">{failedItems.length} post{failedItems.length > 1 ? "s" : ""} failed to publish</p>
            <p className="text-xs text-muted-foreground/70 mt-1">Check your connected social account tokens in <a href="/settings" className="text-red-400 hover:text-red-300 underline underline-offset-2">Settings → Integrations</a>.</p>
          </div>
          <button
            onClick={() => setFilterStatus("PUBLISH_FAILED")}
            className="text-xs font-bold px-3 py-1.5 rounded-lg bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30 transition-colors shrink-0"
            data-testid="button-view-failed"
          >
            View Failed
          </button>
        </div>
      )}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <h2 className="text-sm font-display font-bold text-foreground">Library</h2>
        <div className="flex items-center gap-2">
          {brandKit && (
            <button
              data-testid="button-apply-brand-kit"
              onClick={() => applyBrandMutation.mutate()}
              disabled={applyBrandMutation.isPending}
              className="border border-purple-500/30 hover:border-purple-500/50 bg-purple-500/10 hover:bg-purple-500/20 text-purple-300 font-semibold rounded-lg px-3 py-1.5 text-xs flex items-center gap-1.5 transition-colors disabled:opacity-50"
              title="Tag all content with your brand kit for consistent branding"
            >
              {applyBrandMutation.isPending ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
              Apply Brand Kit
            </button>
          )}
          <button
            data-testid="button-upload-asset"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadAsset.isPending}
            className="bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-white font-bold rounded-lg px-3 py-1.5 text-xs flex items-center gap-1.5 transition-colors"
          >
            {uploadAsset.isPending ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
            Upload Asset
          </button>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleFileSelect}
          className="hidden"
        />
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-sm font-bold text-muted-foreground uppercase tracking-wider">My Assets</h3>
          <button
            data-testid="button-upload-brand-asset"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadAsset.isPending}
            className="border border-blue-500/30 hover:border-blue-500/50 text-blue-400 font-semibold rounded-lg px-3 py-1.5 text-[10px] flex items-center gap-1.5 transition-colors"
          >
            {uploadAsset.isPending ? <Loader2 size={10} className="animate-spin" /> : <Upload size={10} />}
            Upload Brand Asset
          </button>
        </div>
        <p className="text-[10px] text-muted-foreground/50">Upload your logos, product photos, and brand images here. Agents will use these in generated content.</p>
        {loadingAssets ? (
          <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-3">
            {Array(4).fill(0).map((_, i) => (
              <div key={i} className="aspect-square bg-muted/50 animate-pulse rounded-xl" />
            ))}
          </div>
        ) : imageAssets.length > 0 ? (
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
            {imageAssets.map((asset) => (
              <div key={asset.id} className="group flex flex-col gap-1" data-testid={`asset-item-${asset.id}`}>
                <div className="relative rounded-xl overflow-hidden border border-border aspect-square">
                  <img src={asset.url} alt={asset.name} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-background/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                    <button
                      data-testid={`button-download-asset-${asset.id}`}
                      onClick={() => downloadImage(asset.url, asset.name)}
                      className="p-1.5 rounded-lg bg-muted text-foreground/70 hover:text-foreground transition-colors"
                    >
                      <Download size={12} />
                    </button>
                    <button
                      data-testid={`button-delete-asset-${asset.id}`}
                      onClick={() => deleteAssetMutation.mutate(asset.id)}
                      className="p-1.5 rounded-lg bg-muted text-red-400/70 hover:text-red-400 transition-colors"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
                {onCreatePostFromPhoto && (
                  <button
                    data-testid={`button-create-post-from-asset-${asset.id}`}
                    onClick={() => onCreatePostFromPhoto(asset.url)}
                    className="w-full flex items-center justify-center gap-1 text-[10px] font-semibold text-purple-400 hover:text-purple-300 border border-purple-500/20 hover:border-purple-500/40 bg-purple-500/5 hover:bg-purple-500/15 rounded-lg py-1 transition-colors"
                    title="Create a post with AI caption from this photo"
                  >
                    <Sparkles size={9} /> Create Post
                  </button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground/50">No assets uploaded yet. Click "Upload Asset" to add images.</p>
        )}
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <h3 className="text-sm font-bold text-muted-foreground uppercase tracking-wider">Generated Content</h3>
            <div className="flex gap-1 bg-muted/40 rounded-lg p-0.5" data-testid="sort-controls">
              <button
                data-testid="sort-recent"
                onClick={() => setSortBy("recent")}
                className={`px-2.5 py-1 rounded-md text-[10px] font-semibold transition-colors ${sortBy === "recent" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
              >
                Recent
              </button>
              <button
                data-testid="sort-top-performing"
                onClick={() => setSortBy("top")}
                className={`px-2.5 py-1 rounded-md text-[10px] font-semibold transition-colors ${sortBy === "top" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
              >
                Top Performing
              </button>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex gap-1" data-testid="approval-filter">
              {[
                { key: "all", label: "All" },
                { key: "pending_approval", label: "Pending" },
                { key: "approved", label: "Approved" },
                { key: "rejected", label: "Rejected" },
              ].map((f) => (
                <button
                  key={f.key}
                  data-testid={`filter-${f.key}`}
                  onClick={() => setFilterApproval(f.key)}
                  className={`px-2 py-1 rounded-md text-[10px] font-semibold transition-colors ${
                    filterApproval === f.key
                      ? "bg-blue-600/20 text-blue-400"
                      : "text-muted-foreground/60 hover:text-muted-foreground"
                  }`}
                >
                  {f.label}
                  {f.key !== "all" && savedContent && (
                    <span className="ml-1 text-muted-foreground/50">
                      {savedContent.filter((i: ContentItem) => i.approvalStatus === f.key).length}
                    </span>
                  )}
                </button>
              ))}
            </div>
            <span className="text-muted-foreground/30">|</span>
            <div className="flex gap-1" data-testid="tier-filter">
              {[
                { key: "all", label: "All Tiers", color: "blue" },
                { key: "aggressive", label: "Aggressive", color: "red" },
                { key: "medium", label: "Medium", color: "amber" },
                { key: "general", label: "General", color: "sky" },
                { key: "none", label: "No Tier", color: "gray" },
              ].map((f) => (
                <button
                  key={f.key}
                  data-testid={`filter-tier-${f.key}`}
                  onClick={() => setFilterTier(f.key)}
                  className={`px-2 py-1 rounded-md text-[10px] font-semibold transition-colors ${
                    filterTier === f.key
                      ? f.color === "red" ? "bg-red-600/20 text-red-400"
                        : f.color === "amber" ? "bg-amber-600/20 text-amber-400"
                        : f.color === "sky" ? "bg-sky-600/20 text-sky-400"
                        : f.color === "gray" ? "bg-muted text-muted-foreground"
                        : "bg-blue-600/20 text-blue-400"
                      : "text-muted-foreground/60 hover:text-muted-foreground"
                  }`}
                >
                  {f.label}
                  {f.key !== "all" && savedContent && (
                    <span className="ml-1 text-muted-foreground/50">
                      {savedContent.filter((i: ContentItem) => f.key === "none" ? !i.offerTier : i.offerTier === f.key).length}
                    </span>
                  )}
                </button>
              ))}
            </div>
            <span className="text-muted-foreground/30">|</span>
            <div className="flex gap-1" data-testid="status-filter">
              {[
                { key: "all", label: "All Status" },
                { key: "PUBLISH_FAILED", label: "✗ Failed" },
              ].map((f) => (
                <button
                  key={f.key}
                  data-testid={`filter-status-${f.key}`}
                  onClick={() => setFilterStatus(f.key)}
                  className={`px-2 py-1 rounded-md text-[10px] font-semibold transition-colors ${
                    filterStatus === f.key
                      ? f.key === "PUBLISH_FAILED" ? "bg-red-600/20 text-red-400" : "bg-blue-600/20 text-blue-400"
                      : "text-muted-foreground/60 hover:text-muted-foreground"
                  }`}
                >
                  {f.label}
                  {f.key === "PUBLISH_FAILED" && failedItems.length > 0 && (
                    <span className="ml-1 text-red-400/70">{failedItems.length}</span>
                  )}
                </button>
              ))}
            </div>
            <span className="text-muted-foreground/30">|</span>
            <div className="flex gap-1" data-testid="quality-filter">
              {[
                { key: "all", label: "All" },
                { key: "flagged", label: "⚠ Flagged" },
              ].map((f) => (
                <button
                  key={f.key}
                  data-testid={`filter-quality-${f.key}`}
                  onClick={() => setFilterQuality(f.key)}
                  className={`px-2 py-1 rounded-md text-[10px] font-semibold transition-colors ${
                    filterQuality === f.key
                      ? "bg-amber-600/20 text-amber-400"
                      : "text-muted-foreground/60 hover:text-muted-foreground"
                  }`}
                >
                  {f.label}
                  {f.key === "flagged" && savedContent && (
                    <span className="ml-1 text-muted-foreground/50">
                      {savedContent.filter((i: ContentItem) => (i as any).qualityStatus === "flagged").length}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
        {loadingContent ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {Array(6).fill(0).map((_, i) => (
              <div key={i} className="h-48 bg-muted/50 animate-pulse rounded-xl" />
            ))}
          </div>
        ) : filteredContent && filteredContent.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {filteredContent.map((item: ContentItem) => (
              <ContentPreviewCard
                key={item.id}
                item={item}
                onEdit={() => setEditItem(item)}
                onApprove={() => approveContent.mutate(item.id, {
                  onSuccess: () => toast({ title: "Approved", description: "Content approved." }),
                })}
                onReject={() => rejectContent.mutate(item.id, {
                  onSuccess: () => toast({ title: "Rejected", description: "Content rejected." }),
                })}
                onDelete={() => deleteContentMutation.mutate(item.id)}
                onRegenerate={(item as any).qualityStatus === "flagged" ? () => regenerateContentMutation.mutate(item) : undefined}
                isRegenerating={regenerateContentMutation.isPending}
                onSyncEngagement={() => syncEngagement(item)}
                syncingEngagement={syncingItemId === item.id}
                stampEnabled={!!(brandKit?.stampLogoOnContent)}
                workspaceId={activeWorkspaceId || undefined}
                isPlatformConnected={isLibPlatformConnected(item.platform)}
                onMakeReel={() => {
                  window.location.replace(`/content?tab=videos&reelContentId=${item.id}`);
                }}
              />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="empty-library">
            <div className="p-4 rounded-2xl bg-card/50 border border-border mb-4">
              <Bookmark size={32} className="text-muted-foreground/40" />
            </div>
            <h3 className="text-base font-display font-bold text-muted-foreground mb-1">No Content Yet</h3>
            <p className="text-sm text-muted-foreground/70 mb-4">Generate content in the Create tab to see it here.</p>
          </div>
        )}
      </div>

      {editItem && <ContentEditModal item={editItem} onClose={() => setEditItem(null)} />}
    </div>
  );
}

function RepurposeBlogTab() {
  const { activeWorkspaceId: wsId } = useWorkspaceStore();
  const { toast } = useToast();
  const [mode, setMode] = useState<"url" | "text">("url");
  const [url, setUrl] = useState("");
  const [rawText, setRawText] = useState("");
  const [title, setTitle] = useState("");

  const repurposeMutation = useMutation({
    mutationFn: async () => {
      const body: Record<string, string> = {};
      if (mode === "url") {
        if (!url.trim()) throw new Error("Please enter a blog URL");
        body.url = url.trim();
      } else {
        if (!rawText.trim()) throw new Error("Please paste some blog content");
        body.rawText = rawText.trim();
      }
      if (title.trim()) body.title = title.trim();
      const res = await apiRequest("POST", `/api/workspaces/${wsId}/repurpose-blog`, body);
      return res.json();
    },
    onSuccess: (data) => {
      toast({
        title: "Posts scheduled!",
        description: `${data.savedCount} platform posts created and scheduled across the next 7–14 days.`,
      });
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", wsId, "content"] });
      setUrl("");
      setRawText("");
      setTitle("");
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err.message || "Failed to repurpose blog", variant: "destructive" });
    },
  });

  return (
    <div className="max-w-2xl space-y-4">
      <div className="bg-card/50 border border-border rounded-xl p-5 space-y-4">
        <div className="space-y-1">
          <h2 className="text-sm font-display font-bold text-foreground">Blog → Social Posts</h2>
          <p className="text-xs text-muted-foreground">
            Paste a blog URL or raw text. Peggy will turn it into 4 platform-optimized social posts and schedule them automatically over the next 7–14 days.
          </p>
        </div>

        <div className="flex gap-2">
          <button
            data-testid="repurpose-mode-url"
            onClick={() => setMode("url")}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
              mode === "url"
                ? "bg-blue-600 text-white"
                : "bg-muted/50 text-muted-foreground border border-border hover:text-foreground"
            }`}
          >
            Blog URL
          </button>
          <button
            data-testid="repurpose-mode-text"
            onClick={() => setMode("text")}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
              mode === "text"
                ? "bg-blue-600 text-white"
                : "bg-muted/50 text-muted-foreground border border-border hover:text-foreground"
            }`}
          >
            Paste Text
          </button>
        </div>

        {mode === "url" ? (
          <div className="space-y-1.5">
            <SectionLabel>Blog Post URL</SectionLabel>
            <input
              data-testid="input-repurpose-url"
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://yourblog.com/your-post"
              className="w-full bg-muted/50 border border-border focus:border-blue-500 outline-none rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/60"
            />
          </div>
        ) : (
          <div className="space-y-1.5">
            <SectionLabel>Blog Content</SectionLabel>
            <textarea
              data-testid="input-repurpose-text"
              value={rawText}
              onChange={(e) => setRawText(e.target.value)}
              placeholder="Paste your blog post text here..."
              rows={8}
              className="w-full bg-muted/50 border border-border focus:border-blue-500 outline-none rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/60 resize-none"
            />
          </div>
        )}

        <div className="space-y-1.5">
          <SectionLabel>Title (optional)</SectionLabel>
          <input
            data-testid="input-repurpose-title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Blog post title (auto-detected from URL if left blank)"
            className="w-full bg-muted/50 border border-border focus:border-blue-500 outline-none rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/60"
          />
        </div>

        <button
          data-testid="button-repurpose-generate"
          onClick={() => repurposeMutation.mutate()}
          disabled={repurposeMutation.isPending || (!url.trim() && !rawText.trim())}
          className="w-full bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-white font-bold rounded-lg px-4 py-2.5 text-sm flex items-center justify-center gap-2 transition-colors"
        >
          {repurposeMutation.isPending ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              Generating posts...
            </>
          ) : (
            <>
              <Repeat2 size={16} />
              Generate Social Posts
            </>
          )}
        </button>

        <div className="rounded-lg bg-muted/30 border border-border p-3 text-xs text-muted-foreground space-y-1">
          <p className="font-semibold text-foreground/70">What you get:</p>
          <ul className="space-y-0.5 list-disc list-inside">
            <li>LinkedIn post — professional insight angle</li>
            <li>Instagram caption — visual storytelling angle</li>
            <li>X/Twitter post — punchy take under 280 chars</li>
            <li>Facebook post — community-focused version</li>
          </ul>
          <p className="text-muted-foreground/60 pt-1">All posts scheduled 7–14 days out at optimal times for each platform.</p>
        </div>
      </div>
    </div>
  );
}

function SeoHubTab() {
  const { activeWorkspaceId: wsId } = useWorkspaceStore();
  const { toast } = useToast();

  const { data: keywords = [], isLoading: kwLoading } = useQuery<any[]>({
    queryKey: ["/api/workspaces", wsId, "seo-research"],
    enabled: !!wsId,
  });

  const { data: links = [], isLoading: blLoading } = useQuery<any[]>({
    queryKey: ["/api/workspaces", wsId, "backlinks"],
    enabled: !!wsId,
  });

  const researchMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/workspaces/${wsId}/seo-research/analyze`);
      return res.json();
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", wsId, "seo-research"] });
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", wsId, "backlinks"] });
      toast({ title: "SEO research complete", description: `Found ${data.keywords?.length || 0} keywords and ${data.backlinks?.length || 0} backlink opportunities` });
    },
    onError: () => toast({ title: "Research failed", variant: "destructive" }),
  });

  return (
    <div className="space-y-6" data-testid="seo-hub-tab">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-display font-bold text-foreground">SEO Research Hub</h3>
          <p className="text-sm text-muted-foreground mt-1">Keyword research, content gaps, and backlink analysis - Peggy (Kinsey)</p>
        </div>
        <button
          data-testid="button-run-seo-research"
          onClick={() => researchMutation.mutate()}
          disabled={researchMutation.isPending}
          className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold rounded-xl transition-colors disabled:opacity-50"
        >
          {researchMutation.isPending ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
          {researchMutation.isPending ? "Researching..." : "Run SEO Research"}
        </button>
      </div>

      {researchMutation.data && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {researchMutation.data.contentGaps?.length > 0 && (
            <div className="bg-card/50 border border-border rounded-2xl p-5">
              <h4 className="font-semibold text-foreground text-sm mb-3">Content Gaps</h4>
              <ul className="space-y-2">
                {researchMutation.data.contentGaps.map((gap: string, i: number) => (
                  <li key={i} className="text-sm text-muted-foreground flex gap-2">
                    <span className="text-blue-400 shrink-0">-</span> {gap}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {researchMutation.data.technicalTips?.length > 0 && (
            <div className="bg-card/50 border border-border rounded-2xl p-5">
              <h4 className="font-semibold text-foreground text-sm mb-3">Technical SEO Tips</h4>
              <ul className="space-y-2">
                {researchMutation.data.technicalTips.map((tip: string, i: number) => (
                  <li key={i} className="text-sm text-muted-foreground flex gap-2">
                    <span className="text-green-400 shrink-0">-</span> {tip}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <div className="bg-card/50 border border-border rounded-2xl p-5">
        <h4 className="font-semibold text-foreground text-sm mb-4">Keyword Research</h4>
        {kwLoading ? (
          <div className="flex items-center gap-2 text-muted-foreground text-sm"><Loader2 size={14} className="animate-spin" /> Loading...</div>
        ) : keywords.length === 0 ? (
          <p className="text-sm text-muted-foreground/70">No keywords yet. Run SEO research to discover keyword opportunities.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-muted-foreground/70 text-left border-b border-border">
                  <th className="pb-3 font-medium">Keyword</th>
                  <th className="pb-3 font-medium">Volume</th>
                  <th className="pb-3 font-medium">Difficulty</th>
                  <th className="pb-3 font-medium">Opportunity</th>
                </tr>
              </thead>
              <tbody>
                {keywords.map((kw: any) => (
                  <tr key={kw.id} className="border-b border-border" data-testid={`seo-keyword-${kw.id}`}>
                    <td className="py-3 text-foreground/80 font-medium">{kw.keyword}</td>
                    <td className="py-3 text-muted-foreground">{kw.searchVolume?.toLocaleString() || "-"}</td>
                    <td className="py-3">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${
                        (kw.difficulty || 0) > 60 ? "bg-red-500/20 text-red-400" :
                        (kw.difficulty || 0) > 30 ? "bg-yellow-500/20 text-yellow-400" :
                        "bg-green-500/20 text-green-400"
                      }`}>
                        {kw.difficulty || 0}
                      </span>
                    </td>
                    <td className="py-3 text-muted-foreground max-w-[200px] truncate">{kw.opportunity || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="bg-card/50 border border-border rounded-2xl p-5">
        <h4 className="font-semibold text-foreground text-sm mb-4">Backlink Opportunities</h4>
        {blLoading ? (
          <div className="flex items-center gap-2 text-muted-foreground text-sm"><Loader2 size={14} className="animate-spin" /> Loading...</div>
        ) : links.length === 0 ? (
          <p className="text-sm text-muted-foreground/70">No backlinks tracked yet. Run SEO research to find opportunities.</p>
        ) : (
          <div className="space-y-3">
            {links.map((bl: any) => (
              <div key={bl.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-xl border border-foreground/[0.05]" data-testid={`backlink-${bl.id}`}>
                <div className="flex-1">
                  <p className="text-sm text-foreground/80 font-medium">{bl.sourceDomain}</p>
                  <p className="text-xs text-muted-foreground/70 mt-0.5">{bl.anchorText}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                    bl.domainAuthority >= 60 ? "bg-green-500/20 text-green-400" :
                    bl.domainAuthority >= 30 ? "bg-yellow-500/20 text-yellow-400" :
                    "bg-muted text-muted-foreground"
                  }`}>DA {bl.domainAuthority || "?"}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    bl.status === "active" ? "bg-green-500/20 text-green-400" : "bg-blue-500/20 text-blue-400"
                  }`}>{bl.status}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/** The server's own message from an apiRequest error ("404: {\"message\":\"...\"}"), for the toast. */
function apiErrorText(err: unknown): string {
  const raw = String((err as any)?.message || "").replace(/^\d{3}:\s*/, "");
  try { const j = JSON.parse(raw); return j.message || j.error || "Please try again."; } catch { return raw || "Please try again."; }
}

function BlogsTab() {
  const { activeWorkspaceId } = useWorkspaceStore();
  const { data: savedContent, isLoading } = useContent();
  const [editItem, setEditItem] = useState<ContentItem | null>(null);
  const [search, setSearch] = useState("");
  const [actionPending, setActionPending] = useState<number | null>(null);
  const itemRefs = useRef<Record<number, HTMLDivElement | null>>({});

  // Keyword Coverage panel state
  const [showCoverage, setShowCoverage] = useState(false);
  const [expandedKeyword, setExpandedKeyword] = useState<string | null>(null);
  const { data: coverageData, isLoading: coverageLoading } = useQuery<{
    keywords: { keyword: string; posts: { id: number; title: string; status: string; approvalStatus: string | null; createdAt: string | null }[]; count: number }[];
    totalBlogs: number;
  }>({
    queryKey: ["/api/workspaces", activeWorkspaceId, "content", "blog-keyword-coverage"],
    queryFn: () => apiRequest("GET", `/api/workspaces/${activeWorkspaceId}/content/blog-keyword-coverage`).then(r => r.json()),
    enabled: !!activeWorkspaceId && showCoverage,
    staleTime: 60_000,
  });

  // AI Topic Suggestions panel state
  const [showSuggestions, setShowSuggestions] = useState(false);
  const { data: suggestionsData, isLoading: suggestionsLoading } = useQuery<{
    suggestions: { title: string; keyword: string; reason: string }[];
    coveredCount: number;
  }>({
    queryKey: ["/api/workspaces", activeWorkspaceId, "content", "blog-topic-suggestions"],
    queryFn: () => apiRequest("GET", `/api/workspaces/${activeWorkspaceId}/content/blog-topic-suggestions`).then(r => r.json()),
    enabled: !!activeWorkspaceId && showSuggestions,
    staleTime: 300_000,
  });

  // Generate Blog state
  const [showGenerateForm, setShowGenerateForm] = useState(false);
  const [generateTopic, setGenerateTopic] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [researchFirst, setResearchFirst] = useState(true);
  const [researchBrief, setResearchBrief] = useState<any>(null);
  const [isResearching, setIsResearching] = useState(false);
  const [newlyGeneratedId, setNewlyGeneratedId] = useState<number | null>(null);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pollAttemptsRef = useRef(0);

  // Read ?itemId= from URL for deep-link highlighting
  const urlParams = new URLSearchParams(window.location.search);
  const deepLinkItemId = urlParams.get("itemId") ? Number(urlParams.get("itemId")) : null;
  const [highlightedId, setHighlightedId] = useState<number | null>(deepLinkItemId);
  const [verifyPanelId, setVerifyPanelId] = useState<number | null>(null);

  // Fetch workspace to detect WordPress configuration
  const { data: workspaces, isLoading: isWorkspacesLoading } = useQuery<any[]>({ queryKey: ["/api/workspaces"] });
  const activeWorkspace = (workspaces || []).find((w: any) => w.id === activeWorkspaceId);
  // wordpressAppPassword is stripped server-side for security — use wordpressUrl + wordpressUsername as the indicator
  const wpConfigured = !!(activeWorkspace?.wordpressUrl && activeWorkspace?.wordpressUsername);
  const wpStatusKnown = !isWorkspacesLoading && !!workspaces;

  const blogItems: ContentItem[] = (savedContent || []).filter(
    (item: ContentItem) => item.contentType === "blog" || item.platform === "blog" || item.platform === "WordPress"
  );

  const filtered = blogItems.filter((item: ContentItem) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (item.caption || "").toLowerCase().includes(q) || (item.blogUrl || "").toLowerCase().includes(q);
  });

  // Scroll to and highlight the deep-linked item once content loads
  useEffect(() => {
    if (!deepLinkItemId || isLoading) return;
    const el = itemRefs.current[deepLinkItemId];
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      setHighlightedId(deepLinkItemId);
      const t = setTimeout(() => setHighlightedId(null), 3000);
      return () => clearTimeout(t);
    }
  }, [deepLinkItemId, isLoading, filtered.length]);

  // Cleanup poll on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, []);

  // Scroll to newly generated blog and highlight it
  useEffect(() => {
    if (!newlyGeneratedId) return;
    const el = itemRefs.current[newlyGeneratedId];
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      setHighlightedId(newlyGeneratedId);
      const t = setTimeout(() => {
        setHighlightedId(null);
        setNewlyGeneratedId(null);
      }, 3000);
      return () => clearTimeout(t);
    }
  }, [newlyGeneratedId, filtered.length]);

  const seoColor = (score: number | null | undefined) => {
    if (!score) return "text-muted-foreground/50";
    if (score >= 90) return "text-emerald-400";
    if (score >= 70) return "text-yellow-400";
    return "text-red-400";
  };

  const blogStatusLabel = (item: ContentItem) => {
    if (item.status === "PUBLISHED" && item.blogUrl) return "Published";
    if (item.status === "SCHEDULED") return "Scheduled";
    if (item.approvalStatus === "approved") return "Approved";
    if (item.approvalStatus === "pending_approval" || item.approvalStatus === "pending") return "Pending Review";
    if (item.approvalStatus === "rejected") return "Rejected";
    return "Draft";
  };

  const blogStatusBadgeStyle = (item: ContentItem) => {
    if (item.status === "PUBLISHED" && item.blogUrl) return "text-emerald-400 border-emerald-500/30 bg-emerald-500/10";
    if (item.status === "SCHEDULED") return "text-blue-400 border-blue-500/30 bg-blue-500/10";
    if (item.approvalStatus === "approved") return "text-teal-400 border-teal-500/30 bg-teal-500/10";
    if (item.approvalStatus === "pending_approval" || item.approvalStatus === "pending") return "text-yellow-400 border-yellow-500/30 bg-yellow-500/10";
    if (item.approvalStatus === "rejected") return "text-red-400 border-red-500/30 bg-red-500/10";
    return "text-muted-foreground border-border";
  };

  const { toast } = useToast();

  const handleResearchFirst = async () => {
    if (!generateTopic.trim()) {
      toast({ title: "Enter a topic first", description: "Type a blog topic to research before generating.", variant: "destructive" });
      return;
    }
    setIsResearching(true);
    try {
      const res = await apiRequest("POST", `/api/workspaces/${activeWorkspaceId}/research-pipeline`, { topic: generateTopic.trim() });
      if (!res.ok) throw new Error("Research failed");
      const brief = await res.json();
      setResearchBrief(brief);
    } catch (e: any) {
      toast({ title: "Research failed", description: e.message, variant: "destructive" });
    } finally {
      setIsResearching(false);
    }
  };

  const handleGenerateBlog = async () => {
    // Guard against double-submit: clear any existing poll before starting
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }

    // Snapshot current blog IDs before triggering
    const existingIds = new Set((blogItems || []).map((b: ContentItem) => b.id));

    setIsGenerating(true);
    setShowGenerateForm(false);
    setResearchBrief(null);

    try {
      // Pass structured research brief to backend so Peggy gets a rich, SEO-backed task.
      // Duplicate is a non-blocking warning — always pass the brief/topic so user's intent is preserved.
      // the blog is written for the workspace on screen (not the account's first workspace)
      const briefPayload = { workspaceId: activeWorkspaceId, ...(researchBrief ? { researchBrief } : {}) };
      const topicParam = generateTopic.trim() && !researchBrief ? `&topic=${encodeURIComponent(generateTopic.trim())}` : "";
      const res = await apiRequest("POST", `/api/agents/blog/trigger?force=true${topicParam}`, briefPayload);

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        if (res.status === 402) {
          toast({ title: "Budget exhausted", description: body.message || "Upgrade your plan to generate more blogs.", variant: "destructive" });
        } else if (res.status === 404) {
          toast({ title: "No active workspace", description: "Complete the strategy setup first.", variant: "destructive" });
        } else {
          toast({ title: "Could not start blog generation", description: body.message || "Please try again.", variant: "destructive" });
        }
        setIsGenerating(false);
        return;
      }

      toast({ title: "Peggy is writing your blog", description: "This takes 1–3 minutes. The list will update automatically." });
      setGenerateTopic("");

      // Poll every 15s for up to 3 minutes (12 rounds)
      pollAttemptsRef.current = 0;
      pollIntervalRef.current = setInterval(async () => {
        pollAttemptsRef.current += 1;

        // Force a guaranteed fresh fetch (not just a cache invalidation)
        let latestContent: ContentItem[] = [];
        try {
          latestContent = await queryClient.fetchQuery<ContentItem[]>({
            queryKey: ["/api/workspaces", activeWorkspaceId, "content"],
            staleTime: 0,
          });
        } catch {
          return; // network hiccup — try again next round
        }

        const latestBlogs = latestContent.filter(
          (i: ContentItem) => i.contentType === "blog" || i.platform === "blog" || i.platform === "WordPress"
        );
        const newBlog = latestBlogs.find((b: ContentItem) => !existingIds.has(b.id));

        if (newBlog) {
          clearInterval(pollIntervalRef.current!);
          pollIntervalRef.current = null;
          setIsGenerating(false);
          setNewlyGeneratedId(newBlog.id);
          toast({ title: "Blog ready!", description: `"${newBlog.caption?.split("\n")[0]?.slice(0, 60) || "New blog"}" is in your list.` });
          return;
        }

        if (pollAttemptsRef.current >= 12) {
          clearInterval(pollIntervalRef.current!);
          pollIntervalRef.current = null;
          setIsGenerating(false);
          toast({ title: "Still generating", description: "Peggy's still working — check back in a moment.", variant: "default" });
        }
      }, 15_000);

    } catch (err) {
      setIsGenerating(false);
      toast({ title: "Could not start blog generation", description: apiErrorText(err), variant: "destructive" });
    }
  };

  const handleApprove = async (e: React.MouseEvent, item: ContentItem) => {
    e.stopPropagation();
    setActionPending(item.id);
    try {
      // Single atomic call: backend sets approvalStatus=approved + status=SCHEDULED for blog items
      await apiRequest("POST", `/api/content/${item.id}/approve`, {});
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "content"] });
      toast({ title: "Blog approved", description: "Blog is now scheduled and ready to publish to WordPress." });
    } catch (err) {
      console.error("Approve failed", err);
      toast({ title: "Approve failed", description: "Could not approve this blog post.", variant: "destructive" });
    } finally {
      setActionPending(null);
    }
  };

  const handlePublishToWP = async (e: React.MouseEvent, item: ContentItem) => {
    e.stopPropagation();
    if (!activeWorkspaceId) return;
    setActionPending(item.id);
    try {
      const result = await apiRequest("POST", `/api/workspaces/${activeWorkspaceId}/content/${item.id}/publish-to-wordpress`, {});
      const data = await result.json();
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "content"] });
      if (data?.dryRun === true) {
        // only the workspace owner or an admin can publish; anyone else gets a preview and nothing is posted
        toast({ title: "Not published", description: "Only the workspace owner or an admin can publish to the website. Nothing was posted." });
      } else {
        toast({ title: "Published to WordPress", description: data.blogUrl ? `Live at: ${data.blogUrl}` : "Blog published successfully." });
      }
    } catch (err: any) {
      console.error("Publish to WordPress failed", err);
      // err.message from apiRequest is shaped "<status>: <raw response text>" — pull the
      // server's JSON { message } out of it so the toast shows a clean sentence, not raw JSON.
      let serverMessage = "Check WordPress credentials in Settings.";
      let blocked = false;
      const raw = String(err?.message || "");
      const jsonPart = raw.replace(/^\d+:\s*/, "");
      try {
        const parsed = JSON.parse(jsonPart);
        if (parsed?.message) serverMessage = parsed.message;
        blocked = parsed?.blocked === true;
      } catch {
        if (jsonPart) serverMessage = jsonPart;
      }
      if (blocked) {
        // refused before anything was sent (test mode): the post is still approved and unpublished, not failed
        toast({ title: "Not published", description: serverMessage });
        return;
      }
      toast({ title: "WordPress publish failed", description: serverMessage, variant: "destructive" });
      // Reflect the failure inline on the row so it doesn't look like the click did nothing.
      queryClient.setQueryData<ContentItem[]>(["/api/workspaces", activeWorkspaceId, "content"], (old) =>
        old?.map(i => (i.id === item.id ? { ...i, status: "PUBLISH_FAILED", publishError: serverMessage } as ContentItem : i)) ?? old,
      );
    } finally {
      setActionPending(null);
    }
  };

  const handleDelete = async (e: React.MouseEvent, item: ContentItem) => {
    e.stopPropagation();
    if (!confirm(`Delete "${item.caption?.slice(0, 60) || "this blog"}"?`)) return;
    setActionPending(item.id);
    try {
      await apiRequest("DELETE", `/api/content/${item.id}`);
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "content"] });
      toast({ title: "Blog deleted" });
    } catch (err) {
      console.error("Delete failed", err);
      toast({ title: "Delete failed", variant: "destructive" });
    } finally {
      setActionPending(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="blogs-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-lg font-display font-bold text-foreground">Blog Posts</h3>
          <p className="text-sm text-muted-foreground/70 mt-0.5">All blog content created by Peggy, with SEO scores and publish status.</p>
        </div>
        <div className="flex items-center gap-2">
          <input
            data-testid="input-blogs-search"
            type="text"
            placeholder="Search blogs..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="px-3 py-1.5 text-sm bg-muted/50 border border-border rounded-lg text-foreground placeholder:text-muted-foreground/40 focus:ring-1 focus:ring-[#21759B]/50 focus:border-[#21759B]/50 outline-none w-40"
          />
          {!isGenerating && (
            <button
              data-testid="button-generate-blog"
              onClick={() => setShowGenerateForm(v => !v)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-violet-600 hover:bg-violet-500 text-white transition-all shrink-0"
              title="Generate a new blog post"
            >
              <Sparkles size={13} />
              Generate Blog
            </button>
          )}
        </div>
      </div>

      {/* Inline topic form */}
      {showGenerateForm && !isGenerating && (
        <div className="flex flex-col gap-2 p-4 bg-violet-500/8 border border-violet-500/20 rounded-xl" data-testid="generate-blog-form">
          <p className="text-xs font-semibold text-violet-300">Generate a New Blog Post</p>
          <p className="text-[11px] text-muted-foreground/70">Leave blank and Draper will pick a live trending topic for your industry. Or give Peggy a specific topic to write about.</p>
          <input
            data-testid="input-generate-blog-topic"
            type="text"
            placeholder="e.g. Top roofing trends for 2025 (optional)"
            value={generateTopic}
            onChange={e => { setGenerateTopic(e.target.value); setResearchBrief(null); }}
            onKeyDown={e => { if (e.key === "Enter" && !researchFirst) handleGenerateBlog(); }}
            className="px-3 py-2 text-sm bg-muted/50 border border-border rounded-lg text-foreground placeholder:text-muted-foreground/40 focus:ring-1 focus:ring-violet-500/50 focus:border-violet-500/50 outline-none"
          />
          {/* Research First toggle */}
          <button
            data-testid="button-research-first-toggle"
            onClick={() => { setResearchFirst(v => !v); setResearchBrief(null); }}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-[11px] font-semibold border transition-all w-fit ${researchFirst ? "border-teal-500/40 bg-teal-500/10 text-teal-300" : "border-border text-muted-foreground hover:text-foreground"}`}
          >
            <FlaskConical size={11} />
            Research First {researchFirst ? "ON" : "OFF"}
          </button>
          {researchFirst && generateTopic.trim() && !researchBrief && (
            <button
              data-testid="button-run-research"
              onClick={handleResearchFirst}
              disabled={isResearching}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-teal-600 hover:bg-teal-500 text-white transition-all w-fit"
            >
              {isResearching ? <Loader2 size={12} className="animate-spin" /> : <Search size={12} />}
              {isResearching ? "Researching…" : "Run Research Pass"}
            </button>
          )}
          {/* Research brief preview */}
          {researchBrief && (
            <div className="p-3 bg-teal-500/8 border border-teal-500/20 rounded-lg space-y-1.5" data-testid="research-brief-preview">
              {researchBrief.duplicate && (
                <p className="text-[11px] text-amber-400 font-semibold">⚠️ Similar post exists: "{researchBrief.duplicateMatch}" — proceed anyway?</p>
              )}
              <p className="text-[11px] text-teal-300 font-semibold">Research Brief Ready</p>
              {researchBrief.suggestedTitle && <p className="text-[11px] text-foreground"><span className="text-muted-foreground/70">Title:</span> {researchBrief.suggestedTitle}</p>}
              {researchBrief.primaryKeyword && <p className="text-[11px] text-foreground"><span className="text-muted-foreground/70">Primary KW:</span> {researchBrief.primaryKeyword}</p>}
              {researchBrief.gscInsight && <p className="text-[11px] text-foreground"><span className="text-muted-foreground/70">GSC:</span> {researchBrief.gscInsight}</p>}
              {researchBrief.angle && <p className="text-[11px] text-foreground"><span className="text-muted-foreground/70">Angle:</span> {researchBrief.angle}</p>}
            </div>
          )}
          <div className="flex gap-2">
            <button
              data-testid="button-generate-blog-submit"
              onClick={handleGenerateBlog}
              disabled={researchFirst && generateTopic.trim() !== "" && !researchBrief && !isResearching}
              className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-semibold bg-violet-600 hover:bg-violet-500 text-white transition-all disabled:opacity-50"
            >
              <Sparkles size={12} />
              Generate
            </button>
            <button
              data-testid="button-generate-blog-cancel"
              onClick={() => { setShowGenerateForm(false); setGenerateTopic(""); setResearchFirst(false); setResearchBrief(null); }}
              className="px-3 py-1.5 rounded-lg text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Generating banner */}
      {isGenerating && (
        <div className="flex items-center gap-3 px-4 py-3 bg-violet-500/8 border border-violet-500/20 rounded-xl" data-testid="banner-blog-generating">
          <Loader2 size={15} className="text-violet-400 shrink-0 animate-spin" />
          <div className="flex-1">
            <p className="text-xs font-semibold text-violet-300">Peggy is writing your blog…</p>
            <p className="text-[11px] text-muted-foreground/60 mt-0.5">Researching trends, drafting, SEO scoring, and creating a hero image. Usually takes 1–3 minutes.</p>
          </div>
        </div>
      )}

      {/* Keyword Coverage panel */}
      <div className="border border-border rounded-xl overflow-hidden" data-testid="blog-coverage-panel">
        <button
          data-testid="button-toggle-coverage"
          onClick={() => setShowCoverage(v => !v)}
          className="w-full flex items-center justify-between px-4 py-3 bg-card/50 hover:bg-card/70 transition-colors text-left"
        >
          <div className="flex items-center gap-2">
            <Tag size={14} className="text-violet-400 shrink-0" />
            <span className="text-sm font-semibold text-foreground">Keyword Coverage</span>
            {coverageData && (
              <span className="text-xs text-muted-foreground/60">
                — {coverageData.keywords.length} unique {coverageData.keywords.length === 1 ? "topic" : "topics"} across {coverageData.totalBlogs} {coverageData.totalBlogs === 1 ? "post" : "posts"}
              </span>
            )}
          </div>
          <ChevronDown size={14} className={`text-muted-foreground/50 transition-transform ${showCoverage ? "rotate-180" : ""}`} />
        </button>
        {showCoverage && (
          <div className="border-t border-border bg-card/30 p-4">
            {coverageLoading ? (
              <div className="flex items-center gap-2 text-muted-foreground text-sm py-2">
                <Loader2 size={13} className="animate-spin" /> Loading coverage…
              </div>
            ) : !coverageData || coverageData.keywords.length === 0 ? (
              <p className="text-sm text-muted-foreground/60 py-2">No published blog posts yet — once a blog is published, its topics will appear here to help avoid keyword repetition.</p>
            ) : (
              <div className="space-y-1.5" data-testid="coverage-keyword-list">
                {coverageData.keywords.map(({ keyword, posts, count }) => (
                  <div key={keyword} className="rounded-lg border border-border/60 overflow-hidden" data-testid={`coverage-keyword-${keyword.replace(/\s+/g, "-")}`}>
                    <button
                      className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-muted/30 transition-colors"
                      onClick={() => setExpandedKeyword(expandedKeyword === keyword ? null : keyword)}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-xs font-semibold text-violet-300 capitalize truncate max-w-[280px]">{keyword}</span>
                      </div>
                      <div className="flex items-center gap-2 shrink-0 ml-2">
                        <span className="text-[10px] text-muted-foreground/60">{count} {count === 1 ? "post" : "posts"}</span>
                        <ChevronDown size={12} className={`text-muted-foreground/40 transition-transform ${expandedKeyword === keyword ? "rotate-180" : ""}`} />
                      </div>
                    </button>
                    {expandedKeyword === keyword && (
                      <div className="border-t border-border/40 bg-muted/10 px-3 py-2 space-y-1.5" data-testid={`coverage-posts-${keyword.replace(/\s+/g, "-")}`}>
                        {posts.map(post => (
                          <div key={post.id} className="flex items-center justify-between gap-3" data-testid={`coverage-post-${post.id}`}>
                            <p className="text-xs text-foreground/70 truncate flex-1">{post.title}</p>
                            {post.createdAt && (
                              <span className="text-[9px] text-muted-foreground/40 shrink-0">
                                {new Date(post.createdAt).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* AI Topic Suggestions panel */}
      <div className="border border-border rounded-xl overflow-hidden" data-testid="blog-suggestions-panel">
        <button
          data-testid="button-toggle-suggestions"
          onClick={() => setShowSuggestions(v => !v)}
          className="w-full flex items-center justify-between px-4 py-3 bg-card/50 hover:bg-card/70 transition-colors text-left"
        >
          <div className="flex items-center gap-2">
            <Lightbulb size={14} className="text-amber-400 shrink-0" />
            <span className="text-sm font-semibold text-foreground">AI Topic Suggestions</span>
            {suggestionsData && (
              <span className="text-xs text-muted-foreground/60">
                — {suggestionsData.suggestions.length} gap{suggestionsData.suggestions.length !== 1 ? "s" : ""} identified
              </span>
            )}
          </div>
          <ChevronDown size={14} className={`text-muted-foreground/50 transition-transform ${showSuggestions ? "rotate-180" : ""}`} />
        </button>
        {showSuggestions && (
          <div className="border-t border-border bg-card/30 p-4">
            {suggestionsLoading ? (
              <div className="flex items-center gap-2 text-muted-foreground text-sm py-2">
                <Loader2 size={13} className="animate-spin" /> Draper is analyzing coverage gaps…
              </div>
            ) : !suggestionsData || suggestionsData.suggestions.length === 0 ? (
              <p className="text-sm text-muted-foreground/60 py-2">No suggestions available — publish some blog posts first so Draper can identify gaps.</p>
            ) : (
              <div className="grid gap-3 sm:grid-cols-1" data-testid="suggestions-list">
                {suggestionsData.suggestions.map((s, i) => (
                  <div key={i} className="rounded-lg border border-border/60 bg-card/40 p-3 flex flex-col gap-2" data-testid={`suggestion-card-${i}`}>
                    <p className="text-sm font-semibold text-foreground leading-snug">{s.title}</p>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-300 font-medium">{s.keyword}</span>
                    </div>
                    <p className="text-xs text-muted-foreground/70 leading-relaxed">{s.reason}</p>
                    <button
                      data-testid={`button-generate-suggestion-${i}`}
                      onClick={() => { setGenerateTopic(s.title); setShowSuggestions(false); setShowGenerateForm(true); }}
                      className="self-start flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-violet-600 hover:bg-violet-500 text-white transition-all"
                    >
                      <Sparkles size={11} /> Generate Post
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* WordPress not connected banner — only show once workspace query has resolved */}
      {!wpConfigured && !isLoading && wpStatusKnown && (
        <div className="flex items-center gap-3 px-4 py-3 bg-yellow-500/8 border border-yellow-500/20 rounded-xl" data-testid="banner-wp-not-connected">
          <Globe size={15} className="text-yellow-400 shrink-0" />
          <p className="text-xs text-yellow-300/80 flex-1">
            WordPress is not connected — blogs will be saved as drafts.{" "}
            <a href="/settings?tab=publishing" className="underline hover:text-yellow-200 transition-colors" data-testid="link-connect-wordpress">
              Connect in Settings → Publishing
            </a>
          </p>
        </div>
      )}

      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 size={20} className="animate-spin text-muted-foreground/50" />
        </div>
      )}

      {!isLoading && filtered.length === 0 && !isGenerating && (
        <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
          <BookOpen size={32} className="text-muted-foreground/30" />
          <p className="text-sm font-medium text-muted-foreground/60">No blog posts yet</p>
          <p className="text-xs text-muted-foreground/40 max-w-xs">
            Click <span className="text-violet-400 font-medium">Generate Blog</span> above to have Peggy research a trending topic and write a full post — or ask Peggy in chat.
          </p>
        </div>
      )}

      {!isLoading && filtered.length > 0 && (
        <div className="space-y-2">
          {filtered.map((item: ContentItem) => {
            const title = item.caption?.split("\n")[0]?.replace(/^#+\s*/, "").slice(0, 120) || "Untitled Blog Post";
            const excerpt = item.documentContent
              ? item.documentContent.replace(/#+\s*/g, "").replace(/\*{1,2}([^*]+)\*{1,2}/g, "$1").replace(/\[([^\]]+)\]\([^)]+\)/g, "$1").replace(/\n+/g, " ").trim().slice(0, 180)
              : "";
            const date = item.scheduledAt || item.createdAt;
            const isPending = item.approvalStatus === "pending_approval" || item.approvalStatus === "pending";
            const isApproved = item.approvalStatus === "approved";
            const isPublished = item.status === "PUBLISHED" && !!item.blogUrl;
            const isHighlighted = highlightedId === item.id;
            const isBusy = actionPending === item.id;
            return (
              <div key={item.id} className="space-y-1">
              <div
                ref={el => { itemRefs.current[item.id] = el; }}
                data-testid={`blog-item-${item.id}`}
                className={`flex items-start gap-3 p-4 border rounded-xl transition-all cursor-pointer ${
                  isHighlighted
                    ? "border-yellow-400/60 bg-yellow-500/8 ring-1 ring-yellow-400/30"
                    : "bg-card/50 border-border hover:border-border/70 hover:bg-card/70"
                }`}
                onClick={() => setEditItem(item)}
              >
                {/* Hero image thumbnail */}
                {item.mediaUrl ? (
                  <div className="shrink-0 w-20 h-14 rounded-lg overflow-hidden bg-muted/40 border border-border/40">
                    <img src={item.mediaUrl} alt="" className="w-full h-full object-cover" data-testid={`blog-hero-${item.id}`} />
                  </div>
                ) : (
                  <div className="shrink-0 w-20 h-14 rounded-lg bg-muted/30 border border-border/30 flex items-center justify-center">
                    <BookOpen size={16} className="text-muted-foreground/30" />
                  </div>
                )}
                <div className="flex-1 min-w-0 space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      data-testid={`blog-status-${item.id}`}
                      className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${blogStatusBadgeStyle(item)}`}
                    >
                      {blogStatusLabel(item)}
                    </span>
                    {item.seoScore != null && (
                      <span className={`text-[10px] font-bold ${seoColor(item.seoScore)}`} data-testid={`blog-seo-${item.id}`}>
                        SEO {item.seoScore}/100
                      </span>
                    )}
                    {(item as any).qualityScore != null && (
                      <span
                        className={`text-[10px] font-bold ${seoColor((item as any).qualityScore)}`}
                        title="AIEO score — your v6 authority rubric (byline, key facts, question-H2s, FAQ+JSON-LD, ≥3 authoritative links, entity density). This is the standard that wins AI-assistant citations, not just generic SEO."
                        data-testid={`blog-aieo-${item.id}`}
                      >
                        AIEO {(item as any).qualityScore}/100
                      </span>
                    )}
                    {/* Post-publish verification badge */}
                    {item.postVerification && (() => {
                      const pv = item.postVerification;
                      const allPass = pv.status === "pass";
                      const passCount = pv.checks.filter(c => c.passed).length;
                      return (
                        <button
                          type="button"
                          data-testid={`blog-verify-${item.id}`}
                          onClick={e => { e.stopPropagation(); setVerifyPanelId(verifyPanelId === item.id ? null : item.id); }}
                          className={`inline-flex items-center gap-1 text-[9px] font-bold px-1.5 py-0.5 rounded border transition-all ${
                            allPass
                              ? "text-emerald-400 border-emerald-500/30 bg-emerald-500/10 hover:bg-emerald-500/20"
                              : "text-amber-400 border-amber-500/30 bg-amber-500/10 hover:bg-amber-500/20"
                          }`}
                        >
                          {allPass ? <Check size={8} /> : <AlertTriangle size={8} />}
                          {allPass ? "Verified" : `${passCount}/${pv.checks.length} checks`}
                        </button>
                      );
                    })()}
                  </div>
                  <p className="text-sm font-semibold text-foreground truncate" data-testid={`blog-title-${item.id}`}>{title}</p>
                  {excerpt && (
                    <p className="text-[11px] text-muted-foreground/55 leading-relaxed line-clamp-2" data-testid={`blog-excerpt-${item.id}`}>{excerpt}</p>
                  )}
                  {date && (
                    <p className="text-[10px] text-muted-foreground/50">
                      {new Date(date).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0 mt-0.5">
                  {/* Approve button — only for pending items */}
                  {isPending && (
                    <button
                      data-testid={`blog-approve-${item.id}`}
                      className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-bold text-emerald-400 border border-emerald-500/30 bg-emerald-500/10 hover:bg-emerald-500/20 transition-all disabled:opacity-50"
                      onClick={e => handleApprove(e, item)}
                      disabled={isBusy}
                      title="Approve"
                    >
                      {isBusy ? <Loader2 size={10} className="animate-spin" /> : <Check size={10} />}
                      Approve
                    </button>
                  )}
                  {/* Publish to WP button — for approved drafts when WP is connected and not yet published */}
                  {isApproved && !isPublished && wpConfigured && (
                    <button
                      data-testid={`blog-publish-wp-${item.id}`}
                      className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-bold text-[#21759B] border border-[#21759B]/30 bg-[#21759B]/10 hover:bg-[#21759B]/20 transition-all disabled:opacity-50"
                      onClick={e => handlePublishToWP(e, item)}
                      disabled={isBusy}
                      title="Publish to WordPress"
                    >
                      {isBusy ? <Loader2 size={10} className="animate-spin" /> : <Globe size={10} />}
                      Publish to WP
                    </button>
                  )}
                  {/* View on WP link — for published items */}
                  {item.blogUrl && (
                    <a
                      href={item.blogUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`blog-link-${item.id}`}
                      className="p-1.5 rounded-lg text-muted-foreground/60 hover:text-[#21759B] hover:bg-[#21759B]/10 transition-all"
                      onClick={e => e.stopPropagation()}
                      title="View on WordPress"
                    >
                      <Globe size={14} />
                    </a>
                  )}
                  {/* Edit button */}
                  <button
                    data-testid={`blog-edit-${item.id}`}
                    className="p-1.5 rounded-lg text-muted-foreground/60 hover:text-foreground hover:bg-muted/50 transition-all"
                    onClick={e => { e.stopPropagation(); setEditItem(item); }}
                    title="Edit"
                  >
                    <Edit3 size={14} />
                  </button>
                  {/* Delete button */}
                  <button
                    data-testid={`blog-delete-${item.id}`}
                    className="p-1.5 rounded-lg text-muted-foreground/60 hover:text-red-400 hover:bg-red-500/10 transition-all disabled:opacity-50"
                    onClick={e => handleDelete(e, item)}
                    disabled={isBusy}
                    title="Delete"
                  >
                    {isBusy ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                  </button>
                </div>
              </div>
              {/* Verification breakdown panel — shown when badge is clicked */}
              {verifyPanelId === item.id && item.postVerification && (
                <div
                  data-testid={`blog-verify-panel-${item.id}`}
                  className="p-3 rounded-xl bg-muted/20 border border-border/40 space-y-1.5"
                  onClick={e => e.stopPropagation()}
                >
                  <p className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground/50 mb-2">Post Verification Checks</p>
                  {item.postVerification.checks.map((check, ci) => (
                    <div key={ci} className={`flex items-start gap-2 text-[10px] ${check.passed ? "text-emerald-400/80" : "text-amber-400/80"}`}>
                      {check.passed ? <Check size={10} className="shrink-0 mt-0.5" /> : <AlertTriangle size={10} className="shrink-0 mt-0.5" />}
                      <div className="min-w-0">
                        <span className="font-bold">{check.name.replace(/_/g, " ")}</span>
                        {check.detail && <span className="text-muted-foreground/60 ml-1">— {check.detail}</span>}
                      </div>
                    </div>
                  ))}
                  {item.postVerification.checkedAt && (
                    <p className="text-[8px] text-muted-foreground/30 pt-1 border-t border-border/20">
                      Checked {new Date(item.postVerification.checkedAt).toLocaleString()}
                    </p>
                  )}
                </div>
              )}
              </div>
            );
          })}
        </div>
      )}

      {editItem && <BlogEditModal item={editItem} onClose={() => setEditItem(null)} />}
    </div>
  );
}

function PersonalBrandTab() {
  const { activeWorkspaceId } = useWorkspaceStore();
  const { toast } = useToast();
  const [generatePostType, setGeneratePostType] = useState("thought_leadership");
  const [generateAuthorityTopic, setGenerateAuthorityTopic] = useState("");
  const [commentUrl, setCommentUrl] = useState("");
  const [commentContent, setCommentContent] = useState("");
  const [commentDrafts, setCommentDrafts] = useState<{ type: string; label: string; comment: string }[]>([]);

  const { data: allContent, isLoading: loadingContent } = useQuery<ContentItem[]>({
    queryKey: ["/api/workspaces", activeWorkspaceId, "content"],
    enabled: !!activeWorkspaceId,
  });

  const { data: profile } = useQuery<any>({
    queryKey: ["/api/workspaces", activeWorkspaceId, "personal-brand/profile"],
    queryFn: () => apiRequest("GET", `/api/workspaces/${activeWorkspaceId}/personal-brand/profile`).then(r => r.json()),
    enabled: !!activeWorkspaceId,
    retry: 1,
  });

  const { data: research } = useQuery<any[]>({
    queryKey: ["/api/workspaces", activeWorkspaceId, "personal-brand/research"],
    queryFn: () => apiRequest("GET", `/api/workspaces/${activeWorkspaceId}/personal-brand/research`).then(r => r.json()),
    enabled: !!activeWorkspaceId && !!profile?.enabled,
  });

  const personalBrandPosts = (allContent ?? []).filter((item: ContentItem) => item.contentMode === "personal_brand" || (item as any).contentCategory === "personal_brand");

  const availableTopics: string[] = profile?.authorityTopics?.map((t: any) => t.label).filter(Boolean) ?? [];

  const generateMutation = useMutation({
    mutationFn: async () => {
      const topicToUse = generateAuthorityTopic || availableTopics[0] || "";
      if (!topicToUse) throw new Error("No authority topics configured — set them up in Settings > Personal Brand");
      const r = await apiRequest("POST", `/api/workspaces/${activeWorkspaceId}/personal-brand/generate`, {
        count: 1,
        postType: generatePostType,
        authorityTopic: topicToUse,
      });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.error ?? "Generate failed"); }
      return r.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "content"] });
      toast({ title: "Post generated and saved as draft" });
    },
    onError: (e: any) => toast({ title: e.message ?? "Generation failed", variant: "destructive" }),
  });

  const draftCommentMutation = useMutation({
    mutationFn: async () => {
      const r = await apiRequest("POST", `/api/workspaces/${activeWorkspaceId}/personal-brand/draft-comment`, {
        postUrl: commentUrl || undefined,
        postContent: commentContent || undefined,
      });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.error ?? "Draft failed"); }
      return r.json();
    },
    onSuccess: (data: any) => setCommentDrafts(data.options ?? []),
    onError: (e: any) => toast({ title: e.message ?? "Comment drafting failed", variant: "destructive" }),
  });

  const POST_TYPES = [
    { value: "thought_leadership", label: "Thought Leadership" },
    { value: "personal_story", label: "Personal Story" },
    { value: "industry_commentary", label: "Industry Commentary" },
    { value: "hot_take", label: "Hot Take" },
    { value: "emotional", label: "Emotional / Inspiration" },
  ];

  if (!profile?.enabled) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-4 text-center">
        <div className="w-14 h-14 rounded-2xl bg-purple-500/10 flex items-center justify-center">
          <UserCog size={24} className="text-purple-400" />
        </div>
        <h3 className="font-display font-bold text-foreground text-lg">Personal Brand not set up</h3>
        <p className="text-sm text-muted-foreground max-w-sm">Set up your authority topics, personal voice, and content mix to activate Draper's LinkedIn personal brand engine.</p>
        <a href="/settings?tab=personal-brand" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium transition-colors" data-testid="link-setup-personal-brand">
          <UserCog size={14} />Set up Personal Brand
        </a>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Quick Generate */}
      <div className="bg-card/50 border border-purple-500/20 rounded-2xl p-4 md:p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 rounded-lg bg-purple-500/10 flex items-center justify-center">
              <Sparkles size={14} className="text-purple-400" />
            </div>
            <h3 className="font-display font-semibold text-foreground">Generate LinkedIn Post</h3>
          </div>
          <a href="/settings?tab=personal-brand" className="text-xs text-purple-400 hover:text-purple-300 underline underline-offset-2" data-testid="link-pb-settings">Edit Profile</a>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <select data-testid="select-pb-post-type" value={generatePostType} onChange={e => setGeneratePostType(e.target.value)} className="flex-1 min-w-[140px] bg-muted/50 border border-border rounded-xl px-3 py-2 text-sm outline-none text-foreground focus:border-purple-500">
            {POST_TYPES.map(({ value, label }) => <option key={value} value={value}>{label}</option>)}
          </select>
          {availableTopics.length > 0 && (
            <select data-testid="select-pb-authority-topic" value={generateAuthorityTopic || availableTopics[0]} onChange={e => setGenerateAuthorityTopic(e.target.value)} className="flex-1 min-w-[120px] bg-muted/50 border border-border rounded-xl px-3 py-2 text-sm outline-none text-foreground focus:border-purple-500">
              {availableTopics.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          )}
          <button data-testid="btn-pb-generate" onClick={() => generateMutation.mutate()} disabled={generateMutation.isPending || availableTopics.length === 0} className="flex items-center gap-2 px-4 py-2 rounded-xl bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium transition-colors disabled:opacity-60">
            {generateMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />}Generate
          </button>
        </div>
        {availableTopics.length === 0 && <p className="text-xs text-muted-foreground mt-1">Add authority topics in Settings → Personal Brand to enable generation.</p>}
      </div>

      {/* Research Feed (compact) */}
      {research && research.length > 0 && (
        <div className="bg-card/50 border border-border rounded-2xl p-4 md:p-6 space-y-3">
          <h4 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Today's Research Feed</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {research.slice(0, 4).map((item: any) => (
              <div key={item.id} className="bg-muted/30 rounded-xl p-3 space-y-1">
                <p className="text-xs font-medium text-foreground leading-snug">{item.headline}</p>
                <p className="text-xs text-muted-foreground line-clamp-2">{item.summary}</p>
                <span className="text-[10px] text-purple-400">{item.topic}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Comment Drafter */}
      <div className="bg-card/50 border border-border rounded-2xl p-4 md:p-6 space-y-4">
        <div className="flex items-center gap-3">
          <h4 className="font-display font-semibold text-foreground">Comment Drafter</h4>
        </div>
        <div className="space-y-2">
          <input data-testid="input-pb-comment-url" value={commentUrl} onChange={e => setCommentUrl(e.target.value)} placeholder="LinkedIn post URL (optional)" className="w-full bg-muted/50 border border-border rounded-xl px-4 py-2.5 text-sm outline-none text-foreground focus:border-purple-500" />
          <textarea data-testid="textarea-pb-comment-content" value={commentContent} onChange={e => setCommentContent(e.target.value)} rows={2} placeholder="Or paste the post text / describe what it's about..." className="w-full bg-muted/50 border border-border rounded-xl px-4 py-2.5 text-sm outline-none text-foreground resize-none focus:border-purple-500" />
          <button data-testid="btn-pb-draft-comment" onClick={() => draftCommentMutation.mutate()} disabled={draftCommentMutation.isPending || (!commentUrl && !commentContent)} className="flex items-center gap-2 px-4 py-2 rounded-xl bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium transition-colors disabled:opacity-60">
            {draftCommentMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}Draft Comments
          </button>
        </div>
        {commentDrafts.length > 0 && (
          <div className="space-y-2 pt-2">
            {commentDrafts.map((draft, i) => (
              <div key={i} data-testid={`pb-comment-draft-${i}`} className="bg-purple-500/5 border border-purple-500/20 rounded-xl p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-purple-400 uppercase tracking-wide">{draft.label}</span>
                  <button data-testid={`btn-pb-copy-comment-${i}`} onClick={() => { navigator.clipboard.writeText(draft.comment); toast({ title: "Copied!" }); }} className="text-muted-foreground hover:text-purple-400 shrink-0"><Copy size={14} /></button>
                </div>
                <p className="text-sm text-foreground">{draft.comment}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Saved Personal Brand Posts */}
      <div className="bg-card/50 border border-border rounded-2xl p-4 md:p-6 space-y-4">
        <h4 className="font-display font-semibold text-foreground">Your Personal Brand Drafts</h4>
        {loadingContent ? (
          <div className="flex justify-center py-8"><Loader2 className="animate-spin text-purple-400" /></div>
        ) : personalBrandPosts.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground text-sm">No drafts yet — generate a post above.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {personalBrandPosts.map((item: any) => (
              <div key={item.id} data-testid={`pb-post-card-${item.id}`} className="bg-muted/30 border border-purple-500/10 rounded-xl p-4 space-y-2">
                <p className="text-sm text-foreground whitespace-pre-wrap line-clamp-4">{item.caption}</p>
                {item.hashtags && <p className="text-xs text-purple-400">{Array.isArray(item.hashtags) ? item.hashtags.join(" ") : item.hashtags}</p>}
                <div className="flex items-center justify-between pt-1">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${item.status === "APPROVED" ? "bg-green-500/10 text-green-400" : "bg-muted text-muted-foreground"}`}>{item.status}</span>
                  <button data-testid={`btn-pb-copy-post-${item.id}`} onClick={() => { navigator.clipboard.writeText(item.caption ?? ""); toast({ title: "Copied!" }); }} className="text-muted-foreground hover:text-purple-400"><Copy size={12} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Content Queue Tab ─────────────────────────────────────────────────────────

const CONTENT_TYPE_META: Record<string, { label: string; icon: any; color: string; desc: string }> = {
  article:    { label: "Article",    icon: FileText,   color: "text-blue-400 bg-blue-500/10 border-blue-500/20",   desc: "Long-form 1,200+ word post" },
  faq:        { label: "FAQ",        icon: HelpCircle, color: "text-purple-400 bg-purple-500/10 border-purple-500/20", desc: "FAQPage schema, Q&A pairs" },
  how_to:     { label: "How-To",     icon: Lightbulb,  color: "text-amber-400 bg-amber-500/10 border-amber-500/20",  desc: "HowTo schema, numbered steps" },
  listicle:   { label: "Listicle",   icon: ListOrdered, color: "text-cyan-400 bg-cyan-500/10 border-cyan-500/20",   desc: "Numbered list format" },
  case_study: { label: "Case Study", icon: BarChart2,  color: "text-green-400 bg-green-500/10 border-green-500/20", desc: "Problem / Solution / Results" },
  news:       { label: "News",       icon: Newspaper,  color: "text-rose-400 bg-rose-500/10 border-rose-500/20",    desc: "Timely commentary, 600+ words" },
};

const QUEUE_STATUS_META: Record<string, { label: string; color: string }> = {
  pending:     { label: "Pending",     color: "text-muted-foreground bg-muted/50" },
  in_progress: { label: "In Progress", color: "text-blue-400 bg-blue-500/10" },
  published:   { label: "Published",   color: "text-green-400 bg-green-500/10" },
  failed:      { label: "Failed",      color: "text-red-400 bg-red-500/10" },
  skipped:     { label: "Skipped",     color: "text-yellow-400 bg-yellow-500/10" },
};

type QueueItem = {
  id: number;
  workspaceId: number;
  title: string;
  topic: string | null;
  focusKeyword: string | null;
  secondaryKeywords: string | null;
  contentType: string | null;
  status: string | null;
  priority: number | null;
  targetPublishAt: string | null;
  publishedAt: string | null;
  wordpressUrl: string | null;
  notes: string | null;
  createdAt: string | null;
};

const BLANK_FORM = { title: "", topic: "", focusKeyword: "", secondaryKeywords: "", contentType: "article", notes: "", targetPublishAt: "" };

function QueueTab() {
  const { activeWorkspaceId } = useWorkspaceStore();
  const { toast } = useToast();
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState<QueueItem | null>(null);
  const [form, setForm] = useState({ ...BLANK_FORM });
  const [publishingId, setPublishingId] = useState<number | null>(null);
  const [bulkText, setBulkText] = useState("");
  const [showBulk, setShowBulk] = useState(false);
  const [savingBulk, setSavingBulk] = useState(false);

  const { data: queue = [], isLoading } = useQuery<QueueItem[]>({
    queryKey: ["/api/workspaces", activeWorkspaceId, "content-queue"],
    queryFn: () => apiRequest("GET", `/api/workspaces/${activeWorkspaceId}/content-queue`).then(r => r.json()),
    enabled: !!activeWorkspaceId,
    refetchInterval: 15_000,
  });

  const pendingCount = queue.filter(i => i.status === "pending").length;
  const publishedCount = queue.filter(i => i.status === "published").length;

  async function saveItem() {
    if (!form.title.trim()) return toast({ title: "Title required", variant: "destructive" });
    try {
      if (editItem) {
        await apiRequest("PATCH", `/api/workspaces/${activeWorkspaceId}/content-queue/${editItem.id}`, form);
        toast({ title: "Item updated" });
      } else {
        await apiRequest("POST", `/api/workspaces/${activeWorkspaceId}/content-queue`, form);
        toast({ title: "Added to queue" });
      }
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "content-queue"] });
      setShowForm(false);
      setEditItem(null);
      setForm({ ...BLANK_FORM });
    } catch { toast({ title: "Failed to save", variant: "destructive" }); }
  }

  async function deleteItem(id: number) {
    if (!confirm("Remove this item from the queue?")) return;
    await apiRequest("DELETE", `/api/workspaces/${activeWorkspaceId}/content-queue/${id}`);
    queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "content-queue"] });
  }

  async function skipItem(id: number) {
    await apiRequest("PATCH", `/api/workspaces/${activeWorkspaceId}/content-queue/${id}`, { status: "skipped" });
    queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "content-queue"] });
  }

  async function resetItem(id: number) {
    await apiRequest("PATCH", `/api/workspaces/${activeWorkspaceId}/content-queue/${id}`, { status: "pending" });
    queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "content-queue"] });
  }

  async function publishNow(id: number) {
    setPublishingId(id);
    try {
      const res = await apiRequest("POST", `/api/workspaces/${activeWorkspaceId}/content-queue/${id}/publish-now`, {});
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      toast({ title: "Publishing started", description: data.message });
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "content-queue"] });
    } catch (e: any) {
      toast({ title: "Failed to publish", description: e.message, variant: "destructive" });
    } finally {
      setPublishingId(null);
    }
  }

  async function saveBulk() {
    const lines = bulkText.split("\n").map(l => l.trim()).filter(Boolean);
    if (!lines.length) return;
    setSavingBulk(true);
    try {
      const items = lines.map((title, i) => ({ title, priority: i, contentType: "article" }));
      await apiRequest("POST", `/api/workspaces/${activeWorkspaceId}/content-queue`, { items });
      toast({ title: `Added ${items.length} items to queue` });
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "content-queue"] });
      setBulkText("");
      setShowBulk(false);
    } catch { toast({ title: "Failed to add items", variant: "destructive" }); }
    finally { setSavingBulk(false); }
  }

  function openEdit(item: QueueItem) {
    setEditItem(item);
    setForm({
      title: item.title || "",
      topic: item.topic || "",
      focusKeyword: item.focusKeyword || "",
      secondaryKeywords: item.secondaryKeywords || "",
      contentType: item.contentType || "article",
      notes: item.notes || "",
      targetPublishAt: item.targetPublishAt ? item.targetPublishAt.slice(0, 16) : "",
    });
    setShowForm(true);
  }

  return (
    <div className="space-y-5" data-testid="queue-tab">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-display font-bold text-foreground">Publishing Queue</h2>
          <p className="text-xs text-muted-foreground/70 mt-0.5">
            Peggy pulls from this queue when the blog publishing routine runs.
            {pendingCount > 0 && <span className="ml-1 text-blue-400 font-medium">{pendingCount} pending</span>}
            {publishedCount > 0 && <span className="ml-1 text-green-400 font-medium">· {publishedCount} published</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="button-queue-bulk-add"
            onClick={() => { setShowBulk(v => !v); setShowForm(false); }}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-border text-muted-foreground hover:text-foreground hover:border-border/80 transition-all"
          >
            <ListOrdered size={12} /> Bulk Add
          </button>
          <button
            data-testid="button-queue-add"
            onClick={() => { setShowForm(v => !v); setEditItem(null); setForm({ ...BLANK_FORM }); setShowBulk(false); }}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-blue-600 text-white hover:bg-blue-500 transition-all"
          >
            <Plus size={12} /> Add Article
          </button>
        </div>
      </div>

      {/* Bulk add panel */}
      {showBulk && (
        <div className="rounded-xl border border-border bg-muted/20 p-4 space-y-3">
          <p className="text-xs text-muted-foreground">Paste one article title per line. They'll be added as Article type — you can edit each one after.</p>
          <textarea
            data-testid="input-bulk-titles"
            value={bulkText}
            onChange={e => setBulkText(e.target.value)}
            placeholder={"How to File Your Taxes as a Self-Employed Canadian\nTop 5 CPA Services for Small Businesses\nWhat Is a T4 and Why Does It Matter?"}
            rows={6}
            className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/40 resize-none focus:outline-none focus:border-blue-500/50"
          />
          <div className="flex gap-2 justify-end">
            <button onClick={() => setShowBulk(false)} className="px-3 py-1.5 text-xs rounded-lg border border-border text-muted-foreground hover:text-foreground">Cancel</button>
            <button onClick={saveBulk} disabled={savingBulk || !bulkText.trim()} data-testid="button-bulk-save" className="px-3 py-1.5 text-xs rounded-lg bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-50 flex items-center gap-1.5">
              {savingBulk ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
              Add {bulkText.split("\n").filter(l => l.trim()).length} Items
            </button>
          </div>
        </div>
      )}

      {/* Add / Edit form */}
      {showForm && (
        <div className="rounded-xl border border-blue-500/20 bg-blue-500/5 p-4 space-y-3">
          <h3 className="text-sm font-semibold text-foreground">{editItem ? "Edit Queue Item" : "Add to Queue"}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <label className="text-xs text-muted-foreground mb-1 block">Article Title *</label>
              <input
                data-testid="input-queue-title"
                value={form.title}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder="e.g. How to Register a Business in Canada"
                className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/40 focus:outline-none focus:border-blue-500/50"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Content Type</label>
              <select
                data-testid="select-queue-content-type"
                value={form.contentType}
                onChange={e => setForm(f => ({ ...f, contentType: e.target.value }))}
                className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:border-blue-500/50"
              >
                {Object.entries(CONTENT_TYPE_META).map(([k, v]) => (
                  <option key={k} value={k}>{v.label} — {v.desc}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Focus Keyword</label>
              <input
                data-testid="input-queue-keyword"
                value={form.focusKeyword}
                onChange={e => setForm(f => ({ ...f, focusKeyword: e.target.value }))}
                placeholder="e.g. small business accounting Canada"
                className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/40 focus:outline-none focus:border-blue-500/50"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Secondary Keywords</label>
              <input
                data-testid="input-queue-secondary-keywords"
                value={form.secondaryKeywords}
                onChange={e => setForm(f => ({ ...f, secondaryKeywords: e.target.value }))}
                placeholder="bookkeeping, CRA, tax filing"
                className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/40 focus:outline-none focus:border-blue-500/50"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Target Publish Date (optional)</label>
              <input
                data-testid="input-queue-publish-date"
                type="datetime-local"
                value={form.targetPublishAt}
                onChange={e => setForm(f => ({ ...f, targetPublishAt: e.target.value }))}
                className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:border-blue-500/50"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Topic / Angle</label>
              <input
                data-testid="input-queue-topic"
                value={form.topic}
                onChange={e => setForm(f => ({ ...f, topic: e.target.value }))}
                placeholder="What specific angle should Peggy take?"
                className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/40 focus:outline-none focus:border-blue-500/50"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs text-muted-foreground mb-1 block">Notes for Peggy</label>
              <input
                data-testid="input-queue-notes"
                value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Any special instructions, sources to cite, competitors to mention, etc."
                className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/40 focus:outline-none focus:border-blue-500/50"
              />
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={() => { setShowForm(false); setEditItem(null); }} className="px-3 py-1.5 text-xs rounded-lg border border-border text-muted-foreground hover:text-foreground">Cancel</button>
            <button onClick={saveItem} data-testid="button-queue-save" className="px-3 py-1.5 text-xs rounded-lg bg-blue-600 text-white hover:bg-blue-500 flex items-center gap-1.5">
              <Check size={12} /> {editItem ? "Save Changes" : "Add to Queue"}
            </button>
          </div>
        </div>
      )}

      {/* Template guide */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        {Object.entries(CONTENT_TYPE_META).map(([k, v]) => {
          const Icon = v.icon;
          return (
            <div key={k} className={`rounded-lg border px-3 py-2 ${v.color}`}>
              <div className="flex items-center gap-1.5 mb-0.5">
                <Icon size={11} />
                <span className="text-xs font-semibold">{v.label}</span>
              </div>
              <p className="text-[10px] opacity-70 leading-tight">{v.desc}</p>
            </div>
          );
        })}
      </div>

      {/* Queue list */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 size={24} className="animate-spin text-muted-foreground/40" />
        </div>
      ) : queue.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <ListOrdered size={40} className="text-muted-foreground/20 mb-3" />
          <p className="text-sm text-muted-foreground font-medium">Your queue is empty</p>
          <p className="text-xs text-muted-foreground/60 mt-1 max-w-sm">
            Add articles above or tell Draper in chat: <span className="italic text-blue-400/80">"Add these 5 articles to my content queue"</span>
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {queue.map((item, idx) => {
            const typeMeta = CONTENT_TYPE_META[item.contentType || "article"] || CONTENT_TYPE_META.article;
            const statusMeta = QUEUE_STATUS_META[item.status || "pending"] || QUEUE_STATUS_META.pending;
            const TypeIcon = typeMeta.icon;
            const isPublishing = publishingId === item.id;
            return (
              <div
                key={item.id}
                data-testid={`queue-item-${item.id}`}
                className={`group rounded-xl border bg-card/50 hover:bg-card/80 transition-all px-4 py-3 flex items-start gap-3 ${item.status === "published" ? "opacity-60" : ""}`}
              >
                {/* Position badge */}
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-muted/50 flex items-center justify-center text-[10px] font-bold text-muted-foreground mt-0.5">
                  {idx + 1}
                </div>

                {/* Main content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start gap-2 flex-wrap">
                    <span className="text-sm font-medium text-foreground truncate flex-1">{item.title}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${statusMeta.color}`}>{statusMeta.label}</span>
                  </div>
                  <div className="flex items-center gap-3 mt-1 flex-wrap">
                    <span className={`flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border ${typeMeta.color}`}>
                      <TypeIcon size={9} />{typeMeta.label}
                    </span>
                    {item.focusKeyword && (
                      <span className="text-[10px] text-muted-foreground/60 flex items-center gap-1">
                        <Hash size={9} />{item.focusKeyword}
                      </span>
                    )}
                    {item.targetPublishAt && (
                      <span className="text-[10px] text-muted-foreground/60 flex items-center gap-1">
                        <Clock size={9} />{new Date(item.targetPublishAt).toLocaleDateString()}
                      </span>
                    )}
                    {item.wordpressUrl && (
                      <a href={item.wordpressUrl} target="_blank" rel="noopener noreferrer" className="text-[10px] text-green-400 flex items-center gap-1 hover:underline">
                        <ExternalLink size={9} />Live
                      </a>
                    )}
                  </div>
                  {item.notes && <p className="text-[10px] text-muted-foreground/50 mt-0.5 truncate">{item.notes}</p>}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                  {item.status === "pending" && (
                    <>
                      <button
                        data-testid={`button-publish-now-${item.id}`}
                        onClick={() => publishNow(item.id)}
                        disabled={isPublishing}
                        title="Publish Now"
                        className="p-1.5 rounded-lg text-green-400 hover:bg-green-500/10 transition-colors disabled:opacity-50"
                      >
                        {isPublishing ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />}
                      </button>
                      <button
                        data-testid={`button-skip-${item.id}`}
                        onClick={() => skipItem(item.id)}
                        title="Skip"
                        className="p-1.5 rounded-lg text-muted-foreground hover:text-yellow-400 hover:bg-yellow-500/10 transition-colors"
                      >
                        <SkipForward size={13} />
                      </button>
                    </>
                  )}
                  {(item.status === "failed" || item.status === "skipped") && (
                    <button
                      data-testid={`button-reset-${item.id}`}
                      onClick={() => resetItem(item.id)}
                      title="Reset to Pending"
                      className="p-1.5 rounded-lg text-muted-foreground hover:text-blue-400 hover:bg-blue-500/10 transition-colors"
                    >
                      <RefreshCw size={13} />
                    </button>
                  )}
                  <button
                    data-testid={`button-edit-queue-${item.id}`}
                    onClick={() => openEdit(item)}
                    title="Edit"
                    className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
                  >
                    <Edit3 size={13} />
                  </button>
                  <button
                    data-testid={`button-delete-queue-${item.id}`}
                    onClick={() => deleteItem(item.id)}
                    title="Remove"
                    className="p-1.5 rounded-lg text-muted-foreground hover:text-red-400 hover:bg-red-500/10 transition-colors"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Footer hint */}
      {queue.length > 0 && pendingCount > 0 && (
        <div className="rounded-xl border border-blue-500/10 bg-blue-500/5 px-4 py-3 flex items-start gap-3">
          <Zap size={14} className="text-blue-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-xs font-medium text-blue-300">{pendingCount} article{pendingCount !== 1 ? "s" : ""} pending in queue</p>
            <p className="text-[11px] text-muted-foreground/60 mt-0.5">
              Peggy will write and publish the next item each time your blog publishing routine runs.
              Go to <span className="text-blue-400">Settings → Routines</span> to schedule it, or hit <span className="text-green-400">▶ Publish Now</span> on any item for immediate publishing.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Content Intelligence Tab ──────────────────────────────────────────────

function IntelligenceTab() {
  const { activeWorkspaceId: wsId } = useWorkspaceStore();
  const { toast } = useToast();
  const [auditUrl, setAuditUrl] = useState("");
  const [isAuditing, setIsAuditing] = useState(false);
  const [auditResult, setAuditResult] = useState<any>(null);
  const [auditProgress, setAuditProgress] = useState<string[]>([]);
  const [serpKeyword, setSerpKeyword] = useState("");
  const [isSerpCrawling, setIsSerpCrawling] = useState(false);
  const [serpResult, setSerpResult] = useState<any>(null);
  const [activeSection, setActiveSection] = useState<"opportunities" | "coverage" | "digest" | "audit" | "serp">("opportunities");

  const { data: intelligenceData, isLoading: isLoadingIntel, refetch: refetchIntel } = useQuery<{ opportunities: any[] }>({
    queryKey: ["/api/workspaces", wsId, "content-intelligence"],
    queryFn: () => apiRequest("GET", `/api/workspaces/${wsId}/content-intelligence?limit=8`).then(r => r.json()),
    enabled: !!wsId,
    staleTime: 60_000,
  });

  const { data: digestData = [], isLoading: isLoadingDigest, refetch: refetchDigest } = useQuery<any[]>({
    queryKey: ["/api/workspaces", wsId, "research-digest"],
    queryFn: () => apiRequest("GET", `/api/workspaces/${wsId}/research-digest?limit=20`).then(r => r.json()),
    enabled: !!wsId,
    staleTime: 30_000,
  });

  const { data: gscStatus } = useQuery<{ connected: boolean; propertyUrl?: string }>({
    queryKey: ["/api/workspaces", wsId, "gsc", "status"],
    queryFn: () => apiRequest("GET", `/api/workspaces/${wsId}/gsc/status`).then(r => r.json()),
    enabled: !!wsId,
  });

  const { data: coverageData, isLoading: coverageLoading } = useQuery<{ keywords: { keyword: string; posts: any[]; count: number }[]; totalBlogs: number }>({
    queryKey: ["/api/workspaces", wsId, "content", "blog-keyword-coverage"],
    queryFn: () => apiRequest("GET", `/api/workspaces/${wsId}/content/blog-keyword-coverage`).then(r => r.json()),
    enabled: !!wsId && activeSection === "coverage",
    staleTime: 120_000,
  });

  const ensureRoutineMutation = useMutation({
    // The explicit button is the only call allowed to switch a paused routine back on (the page-load calls only create missing ones).
    mutationFn: () => apiRequest("POST", `/api/workspaces/${wsId}/ensure-research-routine`, { reactivate: true }),
    onSuccess: (res: any) => res.json().then((d: any) => {
      const parts = [d.keywordResearch, d.newsBrief];
      if (d.skipped) toast({ title: "Research is handled by the Claude team", description: "Daily research routines are not used on this workspace." });
      else if (d.created || parts.some((p) => p?.created)) toast({ title: "Daily research routine created", description: "Runs at 6:00 AM daily to fuel your content intelligence." });
      else if (parts.some((p) => p?.reactivated)) toast({ title: "Daily research turned back on", description: "Runs at 6:00 AM daily to fuel your content intelligence." });
      else if (parts.some((p) => p?.disabled)) toast({ title: "Daily research is still off", description: "Only an owner, admin or editor can turn it back on.", variant: "destructive" });
      else toast({ title: "Daily research is already on", description: "Nothing needed changing." });
    }),
    onError: () => toast({ title: "Could not create routine", variant: "destructive" }),
  });

  const gscSyncMutation = useMutation({
    mutationFn: () => apiRequest('POST', `/api/workspaces/${wsId}/gsc/sync`, {}),
    onSuccess: async (res: any) => {
      const d = await res.json();
      if (d.error === 'no_connection') {
        toast({ title: 'GSC not connected', description: 'Connect Google Search Console in Settings first.', variant: 'destructive' });
      } else {
        toast({ title: 'GSC synced', description: `Ingested ${d.synced} search queries.` });
      }
      queryClient.invalidateQueries({ queryKey: ['/api/workspaces', wsId, 'gsc', 'snapshots'] });
      refetchIntel();
    },
    onError: (e: any) => toast({ title: 'GSC sync failed', description: e.message, variant: 'destructive' }),
  });

  // Auto-seed the research routine for existing workspaces on first load.
  // Never on a Claude-team workspace (claude_worker or hybrid): merely viewing a screen must not create or re-enable routines there.
  const autoSeedRef = useRef(false);
  const { data: intelWorkspaces } = useQuery<any[]>({ queryKey: ["/api/workspaces"] });
  const intelEngine = getAiEngine(intelWorkspaces?.find((w: any) => w.id === wsId));
  useEffect(() => {
    if (wsId && !autoSeedRef.current && !usesClaudeTeam(intelEngine)) {
      autoSeedRef.current = true;
      apiRequest("POST", `/api/workspaces/${wsId}/ensure-research-routine`, {}).catch(() => {});
    }
  }, [wsId, intelEngine]);

  const handleRunAudit = async () => {
    if (!auditUrl.trim()) return;
    setIsAuditing(true);
    setAuditResult(null);
    setAuditProgress([]);
    try {
      const res = await fetch(`/api/workspaces/${wsId}/seo-audit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url: auditUrl }),
        credentials: "include",
      });
      const reader = res.body?.getReader();
      if (!reader) throw new Error("No stream");
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value);
        const events = buffer.split("\n\n");
        buffer = events.pop() || "";
        for (const event of events) {
          if (!event.startsWith("data:")) continue;
          try {
            const parsed = JSON.parse(event.slice(5).trim());
            if (parsed.type === "progress") setAuditProgress(prev => [...prev, parsed.message]);
            if (parsed.type === "done") { setAuditResult(parsed.result); refetchDigest(); }
            if (parsed.type === "error") toast({ title: "Audit error", description: parsed.error, variant: "destructive" });
          } catch {}
        }
      }
    } catch (e: any) {
      toast({ title: "Audit failed", description: e.message, variant: "destructive" });
    } finally {
      setIsAuditing(false);
    }
  };

  const handleSerpCrawl = async () => {
    if (!serpKeyword.trim()) return;
    setIsSerpCrawling(true);
    setSerpResult(null);
    try {
      const res = await apiRequest("POST", `/api/workspaces/${wsId}/serp-crawl`, { keyword: serpKeyword.trim() });
      const data = await res.json();
      setSerpResult(data);
      refetchDigest();
      toast({ title: "SERP crawl complete", description: `Found ${data.results?.length || 0} results for "${serpKeyword}"` });
    } catch (e: any) {
      toast({ title: "SERP crawl failed", description: e.message, variant: "destructive" });
    } finally {
      setIsSerpCrawling(false);
    }
  };

  const sourceLabel: Record<string, string> = {
    keyword_research: "Keyword Research",
    news_brief: "News Brief",
    competitor_watch: "Competitor Watch",
    serp_crawl: "SERP Crawl",
    technical_audit: "SEO Audit",
  };
  const sourceBadgeStyle: Record<string, string> = {
    keyword_research: "bg-blue-500/15 text-blue-400 border-blue-500/30",
    news_brief: "bg-orange-500/15 text-orange-400 border-orange-500/30",
    competitor_watch: "bg-red-500/15 text-red-400 border-red-500/30",
    serp_crawl: "bg-purple-500/15 text-purple-400 border-purple-500/30",
    technical_audit: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  };
  const opportunitySourceIcon: Record<string, any> = {
    gsc_page2: TrendingUp,
    keyword_gap: Target,
    competitor: Eye,
    digest: Lightbulb,
  };

  const { data: seoConfig } = useQuery<any>({
    queryKey: ["/api/workspaces", wsId, "seo-config"],
    queryFn: () => apiRequest("GET", `/api/workspaces/${wsId}/seo-config`).then(r => r.json()),
    enabled: !!wsId,
  });

  const [commissioningTopic, setCommissioningTopic] = useState<string | null>(null);

  const commissionPeggyMutation = useMutation({
    mutationFn: async (topic: string) => {
      setCommissioningTopic(topic);
      const res = await apiRequest("POST", `/api/workspaces/${wsId}/research-pipeline`, { topic });
      const brief = await res.json();
      if (brief.duplicate) {
        toast({ title: "Similar post exists", description: `"${brief.duplicateMatch}" already published. Consider a fresh angle — commissioning anyway with the research brief.`, variant: "default" });
      }
      // Always pass the structured research brief so Peggy writes against the SEO-backed context
      await apiRequest("POST", `/api/agents/blog/trigger?force=true`, { workspaceId: wsId, researchBrief: brief });
      toast({ title: "Peggy commissioned!", description: `Writing "${brief.suggestedTitle || topic}" using the research brief.` });
    },
    onError: (e: any) => toast({ title: "Commission failed", description: apiErrorText(e), variant: "destructive" }),
    onSettled: () => setCommissioningTopic(null),
  });

  const sections = [
    { id: "opportunities", label: "Opportunities", icon: Lightbulb },
    { id: "coverage", label: "Covered Topics", icon: Target },
    { id: "digest", label: "Research Digest", icon: Newspaper },
    { id: "audit", label: "Technical Audit", icon: ShieldAlert },
    { id: "serp", label: "SERP Crawl", icon: Search },
  ] as const;

  return (
    <div className="space-y-5" data-testid="intelligence-tab">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-lg font-display font-bold text-foreground flex items-center gap-2">
            <Brain size={18} className="text-teal-400" />
            Content Intelligence
          </h3>
          <p className="text-sm text-muted-foreground/70 mt-0.5">Ranked content gaps, research digest, technical SEO audits, and live SERP data.</p>
        </div>
        <div className="flex items-center gap-2">
          {gscStatus && (
            <button
              data-testid="button-gsc-sync"
              onClick={() => gscSyncMutation.mutate()}
              disabled={gscSyncMutation.isPending}
              className={`flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded border transition-all ${
                gscStatus.connected ? 'border-teal-500/30 bg-teal-500/10 text-teal-400 hover:bg-teal-500/20' : 'border-border text-muted-foreground/50 cursor-default'
              }`}
            >
              {gscSyncMutation.isPending ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />}
              GSC {gscStatus.connected ? `✓ ${gscStatus.propertyUrl || 'Connected'}` : 'Not Connected'}
            </button>
          )}
          <button
            data-testid="button-ensure-research-routine"
            onClick={() => ensureRoutineMutation.mutate()}
            disabled={ensureRoutineMutation.isPending}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border border-teal-500/30 text-teal-400 hover:bg-teal-500/10 transition-all"
          >
            {ensureRoutineMutation.isPending ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
            Enable Daily Research
          </button>
        </div>
      </div>

      {/* Section nav */}
      <div className="flex gap-1 border-b border-border pb-0">
        {sections.map(s => {
          const Icon = s.icon;
          return (
            <button
              key={s.id}
              data-testid={`intelligence-section-${s.id}`}
              onClick={() => setActiveSection(s.id)}
              className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 transition-all -mb-px ${activeSection === s.id ? "border-teal-500 text-teal-300" : "border-transparent text-muted-foreground hover:text-foreground"}`}
            >
              <Icon size={12} />
              {s.label}
            </button>
          );
        })}
      </div>

      {/* Opportunities */}
      {activeSection === "opportunities" && (
        <div className="space-y-3" data-testid="intelligence-opportunities">
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground/70">Topics ranked by impact score — click to commission Peggy to write about it.</p>
            <button onClick={() => refetchIntel()} className="text-[11px] text-muted-foreground/50 hover:text-muted-foreground flex items-center gap-1"><RefreshCw size={10} /> Refresh</button>
          </div>
          {isLoadingIntel ? (
            <div className="flex items-center gap-2 text-xs text-muted-foreground/50 py-4"><Loader2 size={14} className="animate-spin" /> Analyzing content gaps…</div>
          ) : !intelligenceData?.opportunities?.length ? (
            <div className="text-center py-8 text-muted-foreground/50 text-sm" data-testid="intelligence-empty">
              <Brain size={32} className="mx-auto mb-3 opacity-30" />
              <p className="font-semibold">No gaps detected yet</p>
              <p className="text-xs mt-1">Add seed keywords in Settings → Content Targeting, then enable the daily research routine above.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {(intelligenceData.opportunities || []).map((opp: any, i: number) => {
                const Icon = opportunitySourceIcon[opp.source] || Lightbulb;
                const isCommissioning = commissioningTopic === opp.topic;
                return (
                  <div key={i} className="flex items-start gap-3 p-3 bg-muted/20 border border-border rounded-xl hover:border-teal-500/30 transition-all group" data-testid={`opportunity-${i}`}>
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <Icon size={14} className="text-teal-400 shrink-0 mt-0.5" />
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-foreground truncate">{opp.topic}</p>
                        <p className="text-[11px] text-muted-foreground/70 mt-0.5 line-clamp-2">{opp.gapReason}</p>
                        {opp.serpContext && <p className="text-[10px] text-muted-foreground/50 mt-0.5 italic truncate">Competitors: {opp.serpContext}</p>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-[10px] font-bold text-teal-400">{Math.round(opp.score)}</span>
                      <button
                        data-testid={`button-commission-peggy-${i}`}
                        onClick={() => commissionPeggyMutation.mutate(opp.topic)}
                        disabled={commissionPeggyMutation.isPending}
                        title="Research & commission Peggy to write this"
                        className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-semibold bg-violet-600/20 text-violet-400 border border-violet-500/30 hover:bg-violet-600/40 transition-all opacity-0 group-hover:opacity-100"
                      >
                        {isCommissioning ? <Loader2 size={9} className="animate-spin" /> : <Sparkles size={9} />}
                        {isCommissioning ? "…" : "Brief Peggy"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Covered Topics — sourced from getBlogKeywordCoverage() clusters with real post counts */}
      {activeSection === "coverage" && (
        <div className="space-y-3" data-testid="intelligence-coverage">
          <p className="text-xs text-muted-foreground/70">Topics extracted from your published posts — post count per cluster. Green = well covered (3+), amber = thin (1–2), red = gap (0).</p>
          {coverageLoading ? (
            <div className="flex items-center gap-2 text-xs text-muted-foreground/50 py-4"><Loader2 size={14} className="animate-spin" /> Loading keyword clusters…</div>
          ) : !coverageData?.keywords?.length ? (
            <div className="text-center py-8 text-muted-foreground/50 text-sm">
              <Target size={32} className="mx-auto mb-3 opacity-30" />
              <p className="font-semibold">No coverage data yet</p>
              <p className="text-xs mt-1">Publish your first blog posts and coverage clusters will appear here automatically.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {(coverageData.keywords as { keyword: string; posts: any[]; count: number }[]).map(({ keyword, posts, count }) => {
                const tier = count === 0 ? "gap" : count <= 2 ? "thin" : "covered";
                const colors = {
                  gap: { bar: "bg-red-500", badge: "border-red-500/30 bg-red-500/10 text-red-400", label: "Gap — write about this" },
                  thin: { bar: "bg-amber-500", badge: "border-amber-500/30 bg-amber-500/10 text-amber-400", label: `${count} post${count !== 1 ? "s" : ""} — thin` },
                  covered: { bar: "bg-emerald-500", badge: "border-emerald-500/30 bg-emerald-500/10 text-emerald-400", label: `${count} posts — covered` },
                };
                const c = colors[tier];
                return (
                  <div key={keyword} className="p-3 bg-muted/20 border border-border rounded-xl space-y-2" data-testid={`coverage-kw-${keyword.replace(/\s+/g, "-")}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-semibold text-foreground truncate">{keyword}</span>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${c.badge}`}>{c.label}</span>
                    </div>
                    <div className="w-full bg-muted/40 rounded-full h-1.5">
                      <div className={`${c.bar} h-1.5 rounded-full transition-all`} style={{ width: `${Math.min(100, count * 25)}%` }} />
                    </div>
                    {tier !== "covered" && (
                      <button
                        data-testid={`button-commission-gap-${keyword.replace(/\s+/g, "-")}`}
                        onClick={() => commissionPeggyMutation.mutate(keyword)}
                        disabled={commissionPeggyMutation.isPending}
                        className="flex items-center gap-1 text-[10px] font-semibold text-violet-400 hover:text-violet-300 transition-colors"
                      >
                        {commissioningTopic === keyword ? <Loader2 size={9} className="animate-spin" /> : <Sparkles size={9} />}
                        Brief Peggy to {tier === "gap" ? "cover this gap" : "strengthen coverage"}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Research Digest */}
      {activeSection === "digest" && (
        <div className="space-y-3" data-testid="intelligence-digest">
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground/70">Recent research outputs from routines, audits, and SERP crawls.</p>
            <button onClick={() => refetchDigest()} className="text-[11px] text-muted-foreground/50 hover:text-muted-foreground flex items-center gap-1"><RefreshCw size={10} /> Refresh</button>
          </div>
          {isLoadingDigest ? (
            <div className="flex items-center gap-2 text-xs text-muted-foreground/50 py-4"><Loader2 size={14} className="animate-spin" /> Loading digest…</div>
          ) : !digestData.length ? (
            <div className="text-center py-8 text-muted-foreground/50 text-sm">
              <Newspaper size={32} className="mx-auto mb-3 opacity-30" />
              <p className="font-semibold">Research digest is empty</p>
              <p className="text-xs mt-1">Run a SERP crawl, daily research routine, or competitor watch to populate data here.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {digestData.map((entry: any) => (
                <div key={entry.id} className="p-3 bg-muted/20 border border-border rounded-xl space-y-1.5" data-testid={`digest-entry-${entry.id}`}>
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${sourceBadgeStyle[entry.source] || "bg-muted border-border text-muted-foreground"}`}>
                      {sourceLabel[entry.source] || entry.source}
                    </span>
                    <span className="text-[10px] text-muted-foreground/50">{new Date(entry.createdAt).toLocaleDateString()}</span>
                  </div>
                  <p className="text-xs text-foreground/80 line-clamp-4 whitespace-pre-line">{entry.summary}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Technical Audit */}
      {activeSection === "audit" && (
        <div className="space-y-4" data-testid="intelligence-audit">
          <p className="text-xs text-muted-foreground/70">Run a live technical SEO audit via Claw browser. Checks H1s, meta descriptions, image alt text, broken links, and title uniqueness.</p>
          <div className="flex gap-2">
            <input
              data-testid="input-audit-url"
              type="url"
              placeholder="https://yoursite.com"
              value={auditUrl}
              onChange={e => setAuditUrl(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") handleRunAudit(); }}
              className="flex-1 px-3 py-2 text-sm bg-muted/50 border border-border rounded-lg text-foreground placeholder:text-muted-foreground/40 focus:ring-1 focus:ring-teal-500/50 outline-none"
            />
            <button
              data-testid="button-run-audit"
              onClick={handleRunAudit}
              disabled={isAuditing || !auditUrl.trim()}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold bg-teal-600 hover:bg-teal-500 text-white transition-all disabled:opacity-50"
            >
              {isAuditing ? <Loader2 size={12} className="animate-spin" /> : <ShieldAlert size={12} />}
              {isAuditing ? "Auditing…" : "Run Audit"}
            </button>
          </div>
          {auditProgress.length > 0 && (
            <div className="p-3 bg-muted/20 border border-border rounded-lg space-y-0.5">
              {auditProgress.map((msg, i) => <p key={i} className="text-[11px] text-muted-foreground/70 font-mono">{msg}</p>)}
            </div>
          )}
          {auditResult && (
            <div className="space-y-3" data-testid="audit-result">
              <div className="flex items-center gap-3 p-3 bg-muted/20 border border-border rounded-xl">
                <div className={`text-2xl font-display font-black ${auditResult.score >= 80 ? "text-emerald-400" : auditResult.score >= 60 ? "text-yellow-400" : "text-red-400"}`}>{auditResult.score}</div>
                <div>
                  <p className="text-sm font-semibold text-foreground">{auditResult.url}</p>
                  <p className="text-[11px] text-muted-foreground/70">{auditResult.summary}</p>
                </div>
              </div>
              {auditResult.issues?.length > 0 && (
                <div className="space-y-1.5">
                  {auditResult.issues.slice(0, 8).map((issue: any, i: number) => (
                    <div key={i} className={`p-2.5 rounded-lg border text-[11px] ${issue.severity === "critical" ? "border-red-500/30 bg-red-500/8" : issue.severity === "warning" ? "border-amber-500/30 bg-amber-500/8" : "border-border bg-muted/20"}`} data-testid={`audit-issue-${i}`}>
                      <span className={`font-bold mr-2 ${issue.severity === "critical" ? "text-red-400" : issue.severity === "warning" ? "text-amber-400" : "text-muted-foreground"}`}>{issue.severity.toUpperCase()}</span>
                      <span className="text-foreground/80">{issue.description}</span>
                      {issue.recommendation && <p className="text-muted-foreground/60 mt-0.5">→ {issue.recommendation}</p>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* SERP Crawl */}
      {activeSection === "serp" && (
        <div className="space-y-4" data-testid="intelligence-serp">
          <p className="text-xs text-muted-foreground/70">Crawl Google search results for any keyword using Claw. See what competitors are ranking and identify content angles.</p>
          <div className="flex gap-2">
            <input
              data-testid="input-serp-keyword"
              type="text"
              placeholder="e.g. estate planning Toronto"
              value={serpKeyword}
              onChange={e => setSerpKeyword(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") handleSerpCrawl(); }}
              className="flex-1 px-3 py-2 text-sm bg-muted/50 border border-border rounded-lg text-foreground placeholder:text-muted-foreground/40 focus:ring-1 focus:ring-purple-500/50 outline-none"
            />
            <button
              data-testid="button-run-serp-crawl"
              onClick={handleSerpCrawl}
              disabled={isSerpCrawling || !serpKeyword.trim()}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold bg-purple-600 hover:bg-purple-500 text-white transition-all disabled:opacity-50"
            >
              {isSerpCrawling ? <Loader2 size={12} className="animate-spin" /> : <Search size={12} />}
              {isSerpCrawling ? "Crawling…" : "Crawl SERP"}
            </button>
          </div>
          {serpResult && (
            <div className="space-y-2" data-testid="serp-results">
              <p className="text-xs text-muted-foreground/70">{serpResult.results?.length || 0} results for "{serpResult.keyword}"</p>
              {(serpResult.results || []).slice(0, 10).map((r: any) => (
                <div key={r.position} className="flex items-start gap-3 p-2.5 bg-muted/20 border border-border rounded-lg" data-testid={`serp-result-${r.position}`}>
                  <span className="text-[11px] font-black text-purple-400 shrink-0 w-4 text-right">{r.position}</span>
                  <div className="min-w-0">
                    <a href={r.url} target="_blank" rel="noopener noreferrer" className="text-xs font-semibold text-foreground hover:text-blue-400 transition-colors line-clamp-1">{r.title}</a>
                    <p className="text-[10px] text-muted-foreground/50 truncate">{r.url}</p>
                    {r.description && <p className="text-[11px] text-muted-foreground/70 line-clamp-2 mt-0.5">{r.description}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function LegacyContentStudio({ engine }: { engine: AiEngine }) {
  const { activeWorkspaceId } = useWorkspaceStore();
  const { user } = useAuth();
  const { data: contentItems } = useContent();
  const params = new URLSearchParams(window.location.search);
  const tabParam = params.get("tab") as TabId | null;
  const validTabs: TabId[] = ["create", "calendar", "blogs", "queue", "repurpose", "playbook", "library", "videos", "seo", "intelligence", "pages", "personal-brand"];
  const initialTab: TabId = tabParam && validTabs.includes(tabParam) ? tabParam : "create";
  const [activeTab, setActiveTab] = useState<TabId>(initialTab);
  const [hasAutoSwitched, setHasAutoSwitched] = useState(false);
  const [createInitialMediaUrl, setCreateInitialMediaUrl] = useState<string | undefined>();
  const [createAutoCaption, setCreateAutoCaption] = useState(false);
  const [togglingStamp, setTogglingStamp] = useState(false);

  const { data: brandKitData } = useQuery<any>({
    queryKey: ["/api/workspaces", activeWorkspaceId, "brand-kit"],
    enabled: !!activeWorkspaceId,
  });

  const handleStampToggle = async () => {
    if (!activeWorkspaceId) return;
    setTogglingStamp(true);
    try {
      const newVal = !(brandKitData?.stampLogoOnContent);
      await apiRequest("PUT", `/api/workspaces/${activeWorkspaceId}/brand-kit`, {
        ...(brandKitData || {}),
        stampLogoOnContent: newVal,
      });
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "brand-kit"] });
    } catch {}
    finally { setTogglingStamp(false); }
  };

  function handleCreatePostFromPhoto(assetUrl: string) {
    setCreateInitialMediaUrl(assetUrl);
    setCreateAutoCaption(true);
    setActiveTab("create");
  }

  function handleCaptionConsumed() {
    setCreateAutoCaption(false);
    setCreateInitialMediaUrl(undefined);
  }

  useEffect(() => {
    if (!hasAutoSwitched && !tabParam && contentItems && contentItems.length > 0 && activeTab === "create") {
      setActiveTab("calendar");
      setHasAutoSwitched(true);
    }
  }, [contentItems, hasAutoSwitched, tabParam, activeTab]);

  // Retroactively ensure daily research routines exist for existing workspaces on first Content Studio access.
  // Skipped for Claude-team workspaces (claude_worker / hybrid): opening a screen must not create or re-enable routines there.
  useEffect(() => {
    if (!activeWorkspaceId || usesClaudeTeam(engine)) return;
    apiRequest("POST", `/api/workspaces/${activeWorkspaceId}/ensure-research-routine`, {}).catch(() => {});
  }, [activeWorkspaceId, engine]);

  const contentCount = contentItems?.length || 0;
  const scheduledCount = contentItems?.filter((i: ContentItem) => i.scheduledAt).length || 0;

  const { data: queueItems } = useQuery<{ status: string }[]>({
    queryKey: ["/api/workspaces", activeWorkspaceId, "content-queue"],
    queryFn: () => apiRequest("GET", `/api/workspaces/${activeWorkspaceId}/content-queue`).then(r => r.json()),
    enabled: !!activeWorkspaceId,
    staleTime: 30_000,
  });
  const queuePendingCount = (queueItems || []).filter(i => i.status === "pending").length;

  const tabCounts: Record<string, number | null> = {
    create: null,
    calendar: scheduledCount > 0 ? scheduledCount : null,
    queue: queuePendingCount > 0 ? queuePendingCount : null,
    library: contentCount > 0 ? contentCount : null,
    videos: null,
    seo: null,
    "personal-brand": null,
  };

  if (!activeWorkspaceId) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]" data-testid="no-workspace-state">
        <PenTool size={48} className="text-muted-foreground/50 mb-4" />
        <h2 className="text-xl font-display font-bold text-muted-foreground">No Workspace Selected</h2>
        <p className="text-muted-foreground/70 mt-2 text-sm">Select a workspace to create content.</p>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="content-studio-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-display font-bold text-foreground">Content Studio</h1>
          <p className="text-muted-foreground/70 mt-0.5 text-xs">Peggy generates and manages your marketing content.</p>
        </div>
        <div className="flex items-center gap-3">
          {(
            <button
              data-testid="button-stamp-toggle"
              onClick={handleStampToggle}
              disabled={togglingStamp}
              title={brandKitData?.stampLogoOnContent ? "Brand stamp is ON — click to disable" : "Brand stamp is OFF — click to enable"}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${
                brandKitData?.stampLogoOnContent
                  ? "border-amber-500/40 bg-amber-500/10 text-amber-400 hover:bg-amber-500/20"
                  : "border-border text-muted-foreground hover:text-foreground hover:border-border"
              }`}
            >
              {togglingStamp ? <Loader2 size={12} className="animate-spin" /> : <Stamp size={12} />}
              Brand Stamp {brandKitData?.stampLogoOnContent ? "ON" : "OFF"}
            </button>
          )}
          {user?.isAdmin && (
            <a
              href="/dev/peggy-audit"
              className="text-[10px] text-muted-foreground/40 hover:text-muted-foreground/70 underline underline-offset-2 transition-colors"
              data-testid="link-peggy-audit-report"
            >
              Audit Report
            </a>
          )}
        </div>
      </div>

      <div className="flex gap-1 bg-muted/30 border border-border rounded-xl p-1 overflow-x-auto custom-scrollbar" data-testid="tabs-container">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const count = tabCounts[tab.id];
          return (
            <button
              key={tab.id}
              data-testid={`tab-${tab.id}`}
              onClick={() => setActiveTab(tab.id as TabId)}
              className={`relative flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${
                activeTab === tab.id
                  ? "bg-blue-600 text-white shadow-lg shadow-blue-900/30"
                  : "text-muted-foreground hover:text-foreground/70 hover:bg-muted/50"
              }`}
            >
              <Icon size={14} />
              {tab.label}
              {count !== null && (
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                  activeTab === tab.id ? "bg-foreground/20 text-foreground" : "bg-blue-500/20 text-blue-400"
                }`} data-testid={`tab-count-${tab.id}`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div>
        {activeTab === "create" && <CreateTab initialMediaUrl={createInitialMediaUrl} autoGenerateCaption={createAutoCaption} onCaptionConsumed={handleCaptionConsumed} />}
        {activeTab === "calendar" && <ContentCalendarTab />}
        {activeTab === "blogs" && <BlogsTab />}
        {activeTab === "queue" && <QueueTab />}
        {activeTab === "repurpose" && <RepurposeBlogTab />}
        {activeTab === "playbook" && <PlaybookTab />}
        {activeTab === "library" && <LibraryTab onCreatePostFromPhoto={handleCreatePostFromPhoto} />}
        {activeTab === "videos" && <VideoStudio />}
        {activeTab === "pages" && <PageBuilderEmbed />}
        {activeTab === "seo" && <SeoHubTab />}
        {activeTab === "intelligence" && <IntelligenceTab />}
        {activeTab === "personal-brand" && <PersonalBrandTab />}
      </div>
    </div>
  );
}

/**
 * Content Studio entry point.
 *  - claude_worker and hybrid workspaces (the Claude team does the work) get the simple studio:
 *    review inbox, ready to publish, published, ideas & requests, brand.
 *  - gemini workspaces keep the original studio unchanged.
 *  - `?legacy=1` opens the original studio on any workspace ("Advanced (legacy tools)"). Old deep links to
 *    Pages / Video Studio land on "Ideas & requests" in the simple studio, which offers the advanced tool from there.
 */
export default function ContentStudio() {
  const { activeWorkspaceId } = useWorkspaceStore();
  const { data: workspaces, isLoading } = useQuery<any[]>({ queryKey: ["/api/workspaces"] });

  // The engine is unknown until the workspace list arrives. Wait, so the original studio's
  // mount-time side effects never run on a Claude-team (claude_worker / hybrid) workspace.
  if (activeWorkspaceId && isLoading && !workspaces) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center text-muted-foreground" data-testid="content-studio-loading">
        <Loader2 className="mr-2 animate-spin" size={18} /> Loading…
      </div>
    );
  }

  const engine = getAiEngine(workspaces?.find((w: any) => w.id === activeWorkspaceId));
  if (!usesClaudeTeam(engine)) return <LegacyContentStudio engine={engine} />;

  const params = new URLSearchParams(window.location.search);
  const showLegacy = params.get("legacy") === "1";
  if (!showLegacy) {
    return (
      <SimpleStudio
        renderEditor={(item, onClose) =>
          item.contentType === "blog" || ["blog", "wordpress"].includes((item.platform || "").toLowerCase()) ? (
            <BlogEditModal item={item} onClose={onClose} />
          ) : (
            <ContentEditModal item={item} onClose={onClose} />
          )
        }
      />
    );
  }

  return (
    <div className="space-y-4">
      <div
        className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-2.5 text-sm text-amber-100"
        data-testid="legacy-banner"
      >
        <span>Advanced view. These are the original tools; most of them use the built-in writer instead of Draper's team.</span>
        <a href="/content" className="font-medium underline underline-offset-2" data-testid="link-back-to-simple">
          Back to the simple Content Studio
        </a>
      </div>
      <LegacyContentStudio engine={engine} />
    </div>
  );
}
